// TemplateSections.tsx — section components for the master template editor.
// Each section receives { T, cfg, onNavigate?, ...sectionProps }.
// Inline editing is provided via E / EL from InlineEdit.tsx.
//
// NavSection  → sections/NavSection.tsx
// FooterSection → sections/FooterSection.tsx
// btnClass    → sections/sectionUtils.ts

import React from "react";
import { stripOrphanedCarouselResizeScripts } from "../EmailFlow/utils/carouselHtmlUtils";
import { listenForSbEmbedSize } from "../../../Utils/helpers/embedCodeHtml";
import { useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  ThemeTokens,
  SectionCfg,
  SectionOptions,
  CarouselData,
  TemplateNavigateFn,
  JournalPost,
  TemplateCarouselDeepLink,
  TemplatePlatformDeepLink,
  TemplateContentView,
  TemplateShopView,
  ShopBrowseSelection,
  BannerItem,
  HighlightItem,
} from "./types";
import {
  useCreatorData,
  useCreatorDataLoading,
  useCreatorJournalLoading,
  useCreatorSectionLoading,
  useJournalActivePostSlugOrId,
  findJournalPostBySlugOrId,
  useTemplateSectionInstance,
  useTemplateImageUpload,
} from "./templateData";
import { resolveLinksSectionHeading } from "./linksSectionHeading";
import {
  getJournalPostHeroImage,
  isPublishedBlogStatus,
  journalPostGallerySlots,
  normalizeBlogEmailLayout,
  publishBlogEmailHeroSettings,
} from "../../../Utils/SupabaseServices/BlogDatabase";
import { isLikelyVideoUrl } from "../../../Utils/media/feedMedia";
import { getBudgetedTemplateImageSrc } from "../../../Utils/templateImageDelivery";
import { CreatorFeedVideoPlayer } from "../../../components/FeedPost/PlatformPosts/CreatorFeedVideoPlayer";
import {
  BlogEditorEmptyState,
  JournalDraftBadge,
  JournalGridRowAside,
} from "./BlogApprovalControls";
import { E, EL, useEdit } from "./InlineEdit";
import CampaignWrapper from "../StayUpToDate/StayUpToDateComponents/TemplateWrapper/CampaignWrapper";
import {
  resolveSocialLinksForDisplay,
  subscribeLiveSocialCustom,
} from "../../../components/SocialLinks/socialCustomSync";
import { useLoadSocialLinks } from "../../../Utils/customHooks/useLoadSocialLinks";
import { stripUnsafeHtml } from "./websiteEditorVibeGenerate";
import {
  BlogProductBody,
  parseBlogProductBody,
} from "./blogBodyProducts";
import { resolvePostBlogLayout } from "./blogRelatedModules";
import {
  resolveWebsiteBanner,
  resolvedBannerCta,
  resolveBannerImageFrame,
  bannerImageFrameStyle,
  parseImageFrameEdit,
  imageFrameEditKey,
  readLinkCoverFrame,
  websiteBannerContentPaths,
} from "./templateBannerUtils";
import { normalizeBannerStyle } from "./websiteLibBannerOptions";
import { useLinkThumbCoverStyle } from "./linkThumbCoverFrame";
import { WebsiteTemplateMyLinksLayout } from "./WebsiteTemplateMyLinksLayout";
import { useWebsiteEditorInspectorOptional } from "./WebsiteEditorInspectorContext";
import { useWebsiteEditorMobileSelectionOptional } from "./websiteEditorMobileSelection";
import {
  TemplateStayUpToDateTabPanel,
  TemplateTabFeedPendingGrid,
} from "./TemplateStayUpToDateTabPanel";
import { TemplateCollectionCatalogPanel } from "./TemplateCollectionCatalogPanel";
import { useLoadRenderComponent } from "../../../components/StayUpToDateComponent/StayUpToDateRenderComponent/useLoadRenderComponent";
import {
  normalizeSoundValue,
  parseCollectionDescriptionFromDetails,
  visibleCarouselDescription,
} from "../../../Utils/helpers/carouselHelper";
import {
  clearIsEmptyStatus,
  openOutboundUrl,
  updateIsEmptyStatus,
} from "../../../Utils/StayUpToDate/RenderComponent";
import { TemplateNewsletterSubscribeForm } from "./TemplateNewsletterSubscribeForm";
import { newsletterSignupLayout } from "./NewsletterSignupInspector";
import { isTemplateTabFeedReady } from "./templateTabFeedReadyCache";
import {
  isCarouselSeriesTab,
  isCarouselLatestTab,
  isAutomatedRecentContentCarousel,
} from "../../../Utils/StayUpToDate/stayUpToDateComponentTabFilters";
import {
  findRecentContentPick,
  findCarouselBySlug,
  findCarouselByComponentId,
  isHandPickCarousel,
  listRecentContentCarousels,
  parsePlatformPickValue,
} from "./recentContentCarouselOptions";
import { useFeaturedCarouselStripItems } from "./useFeaturedCarouselStripItems";
import { useSectionNetworkCascade } from "./templateSectionNetworkCascade";
import { useSectionNearViewport } from "./useSectionNearViewport";
import {
  FeaturedCarouselStripSkeleton,
  featuredCarouselIsTrendingFeed,
  featuredCarouselStripIsProductish,
} from "./FeaturedCarouselStripSkeleton";
import { FEATURED_CAROUSEL_POST_LIMIT } from "./featuredCarouselPosts";
import {
  rememberLoadedStill,
  stillAlreadyLoaded,
} from "./FeaturedCarouselCardMedia";
import { peekPendingFeedStrip } from "./websiteEditorPendingFeedStrip";
import { FeaturedCarouselCard } from "./FeaturedCarouselCard";
import { useTemplateFilteredCarousels } from "./useTemplateFilteredCarousels";
import { useTemplateAnalyticsOptional } from "./TemplateAnalyticsContext";
import { useAboutMoreFromItems } from "./useAboutMoreFromItems";
import { useAboutMoreFromCardImages } from "./useAboutMoreFromCardImages";
import { AboutMoreFromPendingGrid } from "./AboutMoreFromPendingGrid";
import { useTemplateTabScrollIntoView } from "./useTemplateTabScrollIntoView";
import { TemplatePlatformHighlightFeed } from "./templatePages/TemplatePlatformHighlightFeed";
import {
  findTemplateBatchAnchor,
  scheduleScrollTemplateNewBatchIntoView,
} from "./scrollTemplateNewBatchIntoView";
import { JournalPostHeroSkeleton } from "./templatePages/JournalPostArticleLoader";
import { PostSourceGallery } from "./PostSourceGallery";
import { templatePlatformHighlightTabs } from "./templatePlatformPostsQuery";
import {
  BsInstagram,
  BsYoutube,
  BsFacebook,
  BsPinterest,
  BsSnapchat,
  BsTwitch,
  BsSpotify,
} from "react-icons/bs";
import {
  IoClose,
  IoLogoTiktok,
  IoMailOutline,
  IoMenu,
  IoSearchOutline,
} from "react-icons/io5";
import { FaLinkedin, FaAmazon } from "react-icons/fa";
import { MdOutlinePodcasts, MdOutlinePhotoCamera } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import "./template.css";
import { narrowTemplateLayout } from "./resolveTheme";

import { ShopProductCatalogPanel } from "./templatePages/ShopProductCatalogPanel";
import { ShopContentCatalogPanel } from "./templatePages/ShopContentCatalogPanel";
import { useShopCatalog } from "./useShopCatalog";
import {
  patchSearchParam,
  readContentBrowseFromSearch,
  readContentShoppableFromSearch,
} from "./websiteFilterSearchParams";
import {
  SHOP_BROWSE_OTHER_SLUG,
  browseSelectionIsActive,
  coerceShopBrowseSelection,
} from "./shopBreadcrumbs";
import { isPublicWebsiteTemplateRoute } from "../../../Utils/websiteTemplateGuards";
import { toCreatorBrowserPathname } from "../../../Utils/url/getCreatorSpaRelativePath";
import { ShopBrowseNav, ShopBrowseNavSkeleton, ShopBrowsePanel, ShopTextFilterRow, ShopSearchBar, ShopSearchSuggestions, ShopCatalogEmpty, parseSearchSuggestionTags, useOneTimeRise } from "./ShopBrowseNav";
import type { ShopBrowseNavCrumb, ShopBrowseNavOption } from "./ShopBrowseNav";
import { useShopInlineFeatured, ContentCatalogDeferProvider } from "./ShopInlineFeaturedContext";
import { WebsiteEditorAddComponentBar } from "./WebsiteEditorAddComponentBar";
import {
  TemplateMarqueeTrack,
  shouldMarqueeTicker,
} from "./TemplateMarqueeTrack";
import { useLoadSearchBarTags } from "../../../Utils/customHooks/useLoadSearchBarTags";
import { useTemplateStyles } from "./useTemplateStyles";
import { tplEditPlaceholderColor } from "./templateThemeVars";
import { supabase, supabasePublic } from "../../../config/supabaseClient";
import { TEMPLATE_ANALYTICS_EVENTS, TemplateEntityType } from "../../../Utils/SupabaseServices/templateAnalyticsEvents";
import { TEMPLATE_TRACKING_TYPES } from "../../../Utils/SupabaseServices/TrackTemplate";
import { btnClass } from "./sections/sectionUtils";
import {
  isContentBrowsePlatformVisible,
  isContentPlatformBrowseSlug,
  compareContentPlatformButtons,
} from "./websiteEditorInspectorTypes";
import WebsiteProductCollectionPanel from "../../ProductCollectionView/WebsiteProductCollectionPanel";
import { getContentFeedTimeframeStorageDetails } from "../StayUpToDate/AddComponentPicker/contentFeedTimeframe";

export { NavSection } from "./sections/NavSection";
export { FooterSection } from "./sections/FooterSection";
function sectionIdFromPath(sectionPath: string | null, fallback: string): string {
  if (!sectionPath) return fallback;
  const tail = sectionPath.split(".").pop();
  return tail || fallback;
}

/** Series + MT day/trending carousels keep their tab even when the feed is empty. */
function shouldHideEmptyContentTab(c: CarouselData | undefined): boolean {
  if (!c) return true;
  if (isCarouselSeriesTab(c)) return false;
  if (
    isAutomatedRecentContentCarousel({
      componentType: c.componentType,
      componentDisplayName: c.componentDisplayName,
      name: c.title,
      title: c.title,
      shoppable_content_filter: c.shoppableContentFilter,
    })
  ) {
    return false;
  }
  return true;
}

/** Hero chips must match the Social links editor (incl. custom names). */
function useBioSocialForDisplay(
  social: Array<{ platform: string; url: string }> | undefined,
  userId?: string,
  username?: string,
) {
  const uid = String(userId || "").trim();
  const { data: socialLinksQuery } = useLoadSocialLinks(uid);
  const [liveTick, setLiveTick] = React.useState(0);
  React.useEffect(() => {
    return subscribeLiveSocialCustom(() => setLiveTick((n) => n + 1));
  }, []);
  return React.useMemo(() => {
    const row = socialLinksQuery?.data;
    const hasOrderedSocial = Array.isArray(social) && social.length > 0;
    return resolveSocialLinksForDisplay({
      social,
      userId,
      username,
      customRaw: row?.social_custom,
      orderRaw: hasOrderedSocial
        ? undefined
        : row?.social_links_order != null
          ? String(row.social_links_order)
          : undefined,
    });
  }, [social, liveTick, userId, username, socialLinksQuery]);
}

function BioSocialLinks({
  social,
  showSocial,
  className,
  btnClassName,
  onSocialClick,
  limit,
}: {
  social: Array<{ platform: string; url: string }>;
  showSocial: boolean;
  className?: string;
  btnClassName?: string;
  onSocialClick?: (platform: string, url: string) => void;
  limit?: number;
}) {
  if (!showSocial) return null;
  const items = limit != null ? social.slice(0, limit) : social;
  return (
    <div className={className}>
      {items.map((s, i) => (
        <EL
          key={`${String(s.platform)}::${String(s.url)}`}
          path={`bio.social.${i}`}
          href={s.url}
          className={btnClassName || btnClass("ghost")}
          lockText
          navigateOnClick
          onClick={() => onSocialClick?.(s.platform, s.url)}
        >
          {s.platform}
        </EL>
      ))}
    </div>
  );
}

function BioHeroCtas({
  onNavigate,
  showShopAll = true,
  showBrowseAll = true,
}: {
  onNavigate?: TemplateNavigateFn;
  showShopAll?: boolean;
  showBrowseAll?: boolean;
}) {
  const analytics = useTemplateAnalyticsOptional();
  if (!showShopAll && !showBrowseAll) return null;
  return (
    <div className="mte-tpl-bio-hero-ctas">
      {showShopAll ? (
        <button
          type="button"
          className={btnClass("primary")}
          onClick={() => {
            analytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
              sectionId: "bio",
              sectionType: "Button",
              entityType: "section",
              entityId: "shop-all-products",
              entityLabel: "Shop All Products",
            });
            onNavigate?.("shop");
          }}
        >
          Shop All Products
        </button>
      ) : null}
      {showBrowseAll ? (
        <button
          type="button"
          className={btnClass("ghost")}
          onClick={() => {
            analytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
              sectionId: "bio",
              sectionType: "Button",
              entityType: "section",
              entityId: "browse-all-content",
              entityLabel: "Browse All Content",
            });
            onNavigate?.("content");
          }}
        >
          Browse All Content
        </button>
      ) : null}
    </div>
  );
}

function BioHeroShopFollow({
  social,
  showSocial,
  onSocialClick,
  variant = "onImage",
  socialClassName,
}: {
  social: Array<{ platform: string; url: string }>;
  showSocial: boolean;
  onSocialClick?: (platform: string, url: string) => void;
  variant?: "onImage" | "page";
  socialClassName?: string;
}) {
  if (!showSocial || social.length === 0) return null;
  const onImage = variant === "onImage";
  return (
    <div className={onImage ? "mte-tpl-bio-hero-follow" : "mte-tpl-bio-follow"}>
      <div
        className={
          onImage
            ? "mte-tpl-bio-hero-follow-kicker"
            : "mte-tpl-bio-follow-kicker"
        }
      >
        <E path="bio.shopFollow" placeholder="Shop & Follow">
          Shop & Follow
        </E>
      </div>
      <BioSocialLinks
        social={social}
        showSocial
        className={
          onImage
            ? "mte-tpl-bio-hero-chips"
            : socialClassName || "mte-tpl-bio-soc-ctr mte-tpl-bio-social-row"
        }
        btnClassName={onImage ? "mte-tpl-bio-hero-chip" : undefined}
        onSocialClick={onSocialClick}
      />
    </div>
  );
}

function TplVarsDiv({
  className,
  vars,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  vars?: React.CSSProperties;
}) {
  const ref = useTemplateStyles<HTMLDivElement>(vars);
  return (
    <div ref={ref} className={className} {...rest}>
      {children}
    </div>
  );
}

function toTemplateClassToken(value: string | undefined) {
  const token = String(value || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return token || "unknown";
}

function sectionPathClassParts(sectionPath: string | null | undefined) {
  const [rawPage, rawSection] = String(sectionPath || "").split("/");
  const page = toTemplateClassToken(rawPage);
  const section = toTemplateClassToken(rawSection);
  return {
    page,
    section,
    combo: `${page}-${section}`,
  };
}

function TplShopGrid({
  className,
  cols,
  aspect,
  children,
}: {
  className: string;
  cols: number;
  aspect: string;
  children: React.ReactNode;
}) {
  const vars = React.useMemo(
    () =>
      ({
        ["--tpl-grid-cols" as string]: String(cols),
        ["--tpl-card-aspect" as string]: aspect,
      }) as React.CSSProperties,
    [cols, aspect],
  );
  return (
    <TplVarsDiv className={className} vars={vars}>
      {children}
    </TplVarsDiv>
  );
}

function TplPostShell({
  vars,
  children,
  className,
}: {
  vars: React.CSSProperties;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <TplVarsDiv
      className={["mte-tpl-post-shell", className].filter(Boolean).join(" ")}
      vars={vars}
    >
      {children}
    </TplVarsDiv>
  );
}

function TplAvHero({
  image,
  children,
}: {
  image?: string;
  children: React.ReactNode;
}) {
  const vars = image
    ? ({
        ["--tpl-av-hero-img" as string]: `url(${JSON.stringify(image)})`,
      } as React.CSSProperties)
    : undefined;
  return (
    <TplVarsDiv className="mte-tpl-av-hero" vars={vars}>
      {children}
    </TplVarsDiv>
  );
}

export function tabSlugMatchesCarouselSlug(
  slug: string,
  c: { renderSlug?: string; id: string },
): boolean {
  const want = slug.trim().replace(/^\/+/, "");
  if (!want) return false;
  const rs = (c.renderSlug ?? "").trim().replace(/^\/+/, "");
  if (rs && rs === want) return true;
  return String(c.id).trim() === want;
}

function carouselTitleLc(c: CarouselData): string {
  return String(c.title || "").trim().toLowerCase();
}

function carouselDisplayLc(c: CarouselData): string {
  return String(c.componentDisplayName || "").trim().toLowerCase();
}

/** Prefer the automated This Week Content tab for the home content strip. */
function findThisWeekContentCarousel(
  carousels: CarouselData[] | null | undefined,
): CarouselData | null {
  const list = Array.isArray(carousels) ? carousels : [];
  return (
    list.find((c) => carouselDisplayLc(c) === "this week content") ||
    list.find((c) => {
      const t = carouselTitleLc(c);
      return t === "this week's content" || t === "this week content";
    }) ||
    null
  );
}

function carouselClickPlatform(platform?: string): string {
  const lc = String(platform || "").trim().toLowerCase();
  if (lc === "shopmy") return "ShopMy";
  if (lc === "amazon") return "Amazon";
  if (lc === "instagram" || lc === "reels") return "Instagram";
  if (lc === "ltk") return "LTK";
  if (!lc) return "";
  return lc.charAt(0).toUpperCase() + lc.slice(1);
}

const SHOP_THIS_PAST_WEEK_HEADING = "Shop This Past Week";

function shopPastWeekDisplayHeading(heading: string, enabled: boolean): string {
  const text = String(heading || "").trim();
  if (!enabled) return text;
  const lc = text.toLowerCase();
  if (
    !lc ||
    lc === "recently mentioned products" ||
    lc === "latest products" ||
    lc === "trending products" ||
    lc === "trending product" ||
    lc === "trending content" ||
    lc === "this week's content" ||
    lc === "this week content"
  ) {
    return SHOP_THIS_PAST_WEEK_HEADING;
  }
  return text;
}

const RECENT_CONTENT_PLATFORM_TYPES = new Set([
  "all",
  "reels",
  "instagram story",
  "tiktok",
  "youtube",
  "twitter",
  "linkedin",
  "podcast",
  "latest-podcast",
  "featured",
]);

function titleMatchesShopPastWeek(title?: string | null): boolean {
  const t = String(title || "").trim().toLowerCase();
  return (
    t.includes("shop this past week") ||
    t.includes("shop this week") ||
    t.includes("shop with past week") ||
    (t.includes("shop") &&
      (t.includes("past week") || t.includes("this week")))
  );
}

function isRecentContentShopPastWeekCarousel(c: CarouselData): boolean {
  if (!titleMatchesShopPastWeek(c.title)) return false;
  if (!RECENT_CONTENT_PLATFORM_TYPES.has(
    String(c.componentType || "").trim().toLowerCase(),
  )) {
    return false;
  }
  const filter = String(c.shoppableContentFilter || "").trim().toLowerCase();
  return filter !== "products" && c.type !== "shop";
}

/** Recent Content (platform All, this week) when Home has no saved row yet. */
const SHOP_THIS_PAST_WEEK_CAROUSEL: CarouselData = {
  id: "shop-this-past-week",
  title: SHOP_THIS_PAST_WEEK_HEADING,
  subtitle: "",
  items: [],
  type: "content",
  componentType: "all",
  componentDisplayName: "all",
  renderSlug: "shop-this-past-week",
  shoppableContentFilter: "both",
  contentOrder: "DESC",
  details: getContentFeedTimeframeStorageDetails("this-week"),
};

function findShopThisPastWeekCarousel(
  carousels: CarouselData[] | null | undefined,
): CarouselData | null {
  const list = Array.isArray(carousels) ? carousels : [];
  const matches = list.filter((c) => titleMatchesShopPastWeek(c.title));
  // Prefer Recent Content only — product fallback used wrong edit controls.
  return matches.find(isRecentContentShopPastWeekCarousel) || null;
}

/** Prefer Recently Mentioned / latest products for the home products marquee. */
function findRecentProductsCarousel(
  carousels: CarouselData[] | null | undefined,
): CarouselData | null {
  const list = Array.isArray(carousels) ? carousels : [];
  const byName =
    list.find((c) => {
      const t = carouselTitleLc(c);
      return (
        t === "recently mentioned products" ||
        t === "latest products" ||
        t.includes("recently mentioned")
      );
    }) || null;
  if (byName) return byName;
  return (
    list.find((c) => {
      const filter = String(c.shoppableContentFilter || "")
        .trim()
        .toLowerCase();
      if (filter !== "products") return false;
      const typeLc = String(c.componentType || "")
        .trim()
        .toLowerCase();
      return typeLc === "reels" || typeLc === "trending products" || !typeLc;
    }) || null
  );
}

function findContentTabIndexForPlatformKey(
  tabs: CarouselData[],
  highlights: Array<{ iconKey?: string; id: string; label: string }>,
  platformKey: string,
): number {
  const pk = platformKey.trim();
  if (!pk) return -1;
  const bySlug = tabs.findIndex((c) => tabSlugMatchesCarouselSlug(pk, c));
  if (bySlug >= 0) return bySlug;
  const hl = highlights.find((h) => (h.iconKey || h.id) === pk);
  const wantLabel = hl?.label?.trim().toLowerCase();
  if (wantLabel) {
    const byLabel = tabs.findIndex(
      (c) => String(c.title || "").trim().toLowerCase() === wantLabel,
    );
    if (byLabel >= 0) return byLabel;
  }
  return -1;
}

/** Deep-link from homepage / About cards → Shop (catalog) or Content (carousel tab). */
function navigateTemplateTabTarget(
  onNavigate: TemplateNavigateFn | undefined,
  page: "shop" | "content",
  slug: string,
  contentView: TemplateContentView = "latest",
) {
  if (!onNavigate) return;
  const s = slug.trim();
  if (page === "shop") {
    onNavigate("shop", {
      shopView: "all",
      shopBrowse: {},
      ...(s ? { carouselSlug: s } : {}),
    });
    return;
  }
  if (s) {
    onNavigate("content", { carouselSlug: s, contentView });
  }
}

/** Open affiliate / platform URLs — never invent an in-site Shop/Content route. */
function openExternalHttpUrl(raw: string | undefined | null): boolean {
  // Mobile-safe: synthetic <a> click (window.open is often blocked on touch).
  return openOutboundUrl(raw);
}

/**
 * Featured carousel card activation:
 * 1) shopLink / permalink (Spotify, affiliate, IG, …)
 * 2) optional tab deep-link for carousel-collection *member* tiles only
 * Never send a post click to Content/Shop when an external URL exists.
 */
function activateFeaturedCarouselItem(
  it: {
    slug?: string;
    kind?: "shop" | "content";
    shopLink?: string;
    permalink?: string;
  },
  opts: {
    onNavigate?: TemplateNavigateFn;
    contentView?: TemplateContentView;
    allowTabDeepLink?: boolean;
  },
): void {
  if (openExternalHttpUrl(it.shopLink)) return;
  if (openExternalHttpUrl(it.permalink)) return;
  if (!opts.allowTabDeepLink || !opts.onNavigate || !it.slug) return;
  navigateTemplateTabTarget(
    opts.onNavigate,
    it.kind === "shop" ? "shop" : "content",
    it.slug,
    opts.contentView,
  );
}

function featuredCarouselItemIsInteractive(
  it: {
    slug?: string;
    kind?: "shop" | "content";
    shopLink?: string;
    permalink?: string;
  },
  opts: { onNavigate?: TemplateNavigateFn; allowTabDeepLink?: boolean },
): boolean {
  if (String(it.shopLink || "").trim() || String(it.permalink || "").trim()) {
    return true;
  }
  return Boolean(opts.allowTabDeepLink && opts.onNavigate && it.slug);
}

const CAROUSEL_TAB_EDIT_MISSING = "\u0000";

/** Tab pill label — reflects inline `contentEdits` immediately (not only after live refetch). */
export function carouselTabDisplayTitle(
  get: (path: string, def: string) => string,
  carousel: { title: string; stayComponentRowId?: string },
): string {
  const rid = carousel.stayComponentRowId?.trim();
  if (!rid) return carousel.title;
  const prefix = `sutc.${rid}.`;
  const read = (field: string) => {
    const v = get(`${prefix}${field}`, CAROUSEL_TAB_EDIT_MISSING);
    return v === CAROUSEL_TAB_EDIT_MISSING ? null : v;
  };
  return (
    read("tabTitle") ??
    read("name") ??
    read("display_name") ??
    carousel.title
  );
}

/** Never flash unique_id / route slug as a collection label while the row loads. */
function titleUnlessSlug(title: unknown, slug: unknown): string {
  const t = String(title || "").trim();
  const s = String(slug || "").trim();
  if (!t) return "";
  if (s && t.toLowerCase() === s.toLowerCase()) return "";
  return t;
}

export function CarouselTabLabel({
  carousel,
}: {
  carousel: { title: string; stayComponentRowId?: string };
}) {
  const { get } = useEdit();
  return <>{carouselTabDisplayTitle(get, carousel)}</>;
}

// ── Platform icon map ─────────────────────────────────────────
// Maps the display platform name (from KNOWN_PLATFORMS) to a react-icons component.
const PLATFORM_ICONS: Record<string, React.ElementType> = {
  Instagram: BsInstagram,
  YouTube: BsYoutube,
  TikTok: IoLogoTiktok,
  LinkedIn: FaLinkedin,
  Facebook: BsFacebook,
  Pinterest: BsPinterest,
  Amazon: FaAmazon,
  Snapchat: BsSnapchat,
  Twitch: BsTwitch,
  Spotify: BsSpotify,
  Newsletter: IoMailOutline,
  Podcast: MdOutlinePodcasts,
  "Apple Podcasts": MdOutlinePodcasts,
};

/**
 * Maps the highlight platform-filter keys produced by `useCreatorDataFromDB`
 * (mirrors `FeedNew.tsx` platform-filter keys) to an icon component.
 */
const HIGHLIGHT_FILTER_ICONS: Record<string, React.ElementType> = {
  instagram_posts: BsInstagram,
  instagram_stories: BsInstagram,
  youtube: BsYoutube,
  tiktok: IoLogoTiktok,
  pinterest: BsPinterest,
  newsletter: IoMailOutline,
  podcast: MdOutlinePodcasts,
  applepodcast: MdOutlinePodcasts,
  linkedin: FaLinkedin,
  facebook: BsFacebook,
  snapchat: BsSnapchat,
  spotify: BsSpotify,
};

// ── Button helper ──────────────────────────────────────────────
// Defined in sections/sectionUtils.ts; imported for local use and re-exported for
// backwards compatibility with consumers that import { btnClass } from TemplateSections.
export { btnClass };

function stopParentNav(e: React.SyntheticEvent) {
  e.stopPropagation();
}

function stopParentLink(e: React.SyntheticEvent) {
  e.preventDefault();
  e.stopPropagation();
}

function EditableBioAvatar({
  src,
  className,
}: {
  src: string;
  className: string;
}) {
  const upload = useTemplateImageUpload();
  const inspector = useWebsiteEditorInspectorOptional();
  const CREATOR = useCreatorData();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const { get } = useEdit();
  const rawSrc = String(src || "").trim();
  const frame = parseImageFrameEdit(get(imageFrameEditKey("bio-avatar"), ""));
  const frameStyle = frame ? bannerImageFrameStyle(frame) : undefined;
  // When Hero still points at a LiB-baked `user.picture`, crop against the
  // uncropped original so CSS zoom/pan matches the inspector (same as LiB).
  const original = String(CREATOR.bio?.originalProfileUrl || "").trim();
  const picture = String(CREATOR.bio?.profilePicture || "").trim();
  const frameSrc =
    frame &&
    original &&
    !rawSrc.startsWith("blob:") &&
    !rawSrc.startsWith("data:") &&
    picture &&
    rawSrc === picture
      ? original
      : rawSrc;
  const hasSrc = Boolean(frameSrc);
  // Hero/avatar is often LCP on mobile — serve display-sized WebP, not full JPEG.
  // Budget shrinks further on 3G / Slow 4G via Network Information API.
  const deliverySrc = hasSrc
    ? getBudgetedTemplateImageSrc(frameSrc, "avatar")
    : "";
  const photo = hasSrc ? (
    <span className={`${className} mte-tpl-bio-ava-frame`}>
      <img
        src={deliverySrc}
        alt=""
        style={frameStyle}
        decoding="async"
        {...({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)}
      />
    </span>
  ) : null;
  // Dashboard website inspector: photo is edited in the right panel (zoom + focal point).
  if (!upload || inspector?.enabled) {
    return photo;
  }
  return (
    <div
      className="mte-tpl-editable-image-wrap"
      onClick={stopParentLink}
      onMouseDown={stopParentNav}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="mte-tpl-hidden-file"
        disabled={busy}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f) return;
          setBusy(true);
          try {
            await upload("bio-avatar", f);
          } finally {
            setBusy(false);
          }
        }}
      />
      <button
        type="button"
        className="mte-tpl-editable-avatar-btn mte-tpl-editable-image-btn"
        onClick={(e) => {
          stopParentLink(e);
          inputRef.current?.click();
        }}
        aria-label="Change profile image"
      >
        {hasSrc ? (
          photo
        ) : (
          <span className={`${className} mte-tpl-bio-ava-empty`} aria-hidden />
        )}
        <span className="mte-tpl-editable-image-hover" aria-hidden>
          <MdOutlinePhotoCamera />
        </span>
        {busy && (
          <span
            className="mte-tpl-editable-image-busy"
            role="status"
            aria-label="Uploading"
          >
            <AiOutlineLoading3Quarters className="mte-tpl-editable-image-spin" />
          </span>
        )}
      </button>
    </div>
  );
}

export function EditableTemplateAsset({
  slot,
  src,
  className,
  alt = "",
  /** Empty editor slot still shows a clickable placeholder (default on). */
  allowEmpty = true,
  imgStyle,
  /** Persisted My Links cover frame when contentEdits is empty. */
  imageCrop,
}: {
  slot: string;
  src: string;
  className: string;
  alt?: string;
  allowEmpty?: boolean;
  imgStyle?: React.CSSProperties;
  imageCrop?: unknown;
}) {
  const up = useTemplateImageUpload();
  const { get } = useEdit();
  const [busy, setBusy] = React.useState(false);
  const [imageReady, setImageReady] = React.useState(() =>
    stillAlreadyLoaded(src),
  );
  const [localPreview, setLocalPreview] = React.useState<string | null>(null);
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const localPreviewRef = React.useRef<string | null>(null);
  const rawDisplaySrc = localPreview || src;
  const hasSrc = Boolean(rawDisplaySrc && String(rawDisplaySrc).trim());
  // About headshot / hero covers: resize via Supabase render (was ~1–2MB JPEGs).
  // Network budget further caps width on 3G (~520) / Slow 4G (~640).
  const displaySrc =
    hasSrc && !String(rawDisplaySrc).startsWith("blob:")
      ? getBudgetedTemplateImageSrc(
          rawDisplaySrc,
          slot === "about-headshot"
            ? "lcp"
            : slot.startsWith("banner")
              ? "banner"
              : "lcp",
        )
      : rawDisplaySrc;
  const prioritizeLcp =
    slot === "about-headshot" ||
    slot === "bio-avatar" ||
    slot.startsWith("banner");
  const storedFrame = imgStyle
    ? undefined
    : parseImageFrameEdit(get(imageFrameEditKey(slot), "")) ||
      readLinkCoverFrame(imageCrop);
  const resolvedImgStyle =
    imgStyle || (storedFrame ? bannerImageFrameStyle(storedFrame) : undefined);

  // Cached images often skip `onLoad` after device remounts — sync readiness
  // in layout so hero covers don't stay at opacity:0 when toggling mobile/desktop.
  React.useLayoutEffect(() => {
    const img = imgRef.current;
    if (
      stillAlreadyLoaded(displaySrc) ||
      (img?.complete && img.naturalWidth > 0)
    ) {
      rememberLoadedStill(String(img?.currentSrc || displaySrc || ""));
      setImageReady(true);
      return;
    }
    setImageReady(false);
  }, [displaySrc]);

  const bindImgRef = React.useCallback((node: HTMLImageElement | null) => {
    imgRef.current = node;
    if (node?.complete && node.naturalWidth > 0) {
      setImageReady(true);
    }
  }, []);

  React.useEffect(() => {
    // Drop blob preview once the live `src` catches up after upload.
    if (!localPreview) return;
    if (src && src === localPreview) return;
    if (src && !src.startsWith("blob:") && src !== localPreview) {
      URL.revokeObjectURL(localPreview);
      localPreviewRef.current = null;
      setLocalPreview(null);
    }
  }, [src, localPreview]);

  React.useEffect(
    () => () => {
      if (localPreviewRef.current) {
        URL.revokeObjectURL(localPreviewRef.current);
        localPreviewRef.current = null;
      }
    },
    [],
  );

  // Mobile shell: tap selects the section (Edit bar). Inline file pickers steal
  // that tap on image-only banners — defer upload to the inspector sheet.
  const mobileSelection = useWebsiteEditorMobileSelectionOptional();
  const deferUploadToInspector = Boolean(mobileSelection);

  const runUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !up) return;
    if (localPreviewRef.current) {
      URL.revokeObjectURL(localPreviewRef.current);
    }
    const preview = URL.createObjectURL(f);
    localPreviewRef.current = preview;
    setLocalPreview(preview);
    setBusy(true);
    try {
      const url = await up(slot, f);
      if (!url) {
        URL.revokeObjectURL(preview);
        if (localPreviewRef.current === preview) localPreviewRef.current = null;
        setLocalPreview(null);
      }
    } catch {
      URL.revokeObjectURL(preview);
      if (localPreviewRef.current === preview) localPreviewRef.current = null;
      setLocalPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const markImageReady = (
    event?: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    const img = event?.currentTarget;
    rememberLoadedStill(
      String(img?.currentSrc || img?.src || displaySrc || ""),
    );
    setImageReady(true);
  };
  const markImageFailed = () => setImageReady(true);
  const pendingImage = hasSrc && !imageReady;

  if (!up) {
    if (!hasSrc) return null;
    return (
      <span className="mte-tpl-editable-image-btn">
        {pendingImage ? (
          <span className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" aria-hidden />
        ) : null}
        <img
          ref={bindImgRef}
          src={displaySrc}
          alt={alt}
          className={className}
          style={{
            ...resolvedImgStyle,
            opacity: pendingImage ? 0 : undefined,
          }}
          decoding="async"
          loading={prioritizeLcp ? "eager" : "lazy"}
          {...(prioritizeLcp
            ? ({
                fetchpriority: "high",
              } as React.ImgHTMLAttributes<HTMLImageElement>)
            : null)}
          onLoad={markImageReady}
          onError={markImageFailed}
        />
      </span>
    );
  }
  if (!hasSrc && !allowEmpty) return null;

  if (deferUploadToInspector) {
    return (
      <div
        className={`mte-tpl-editable-image-wrap mte-tpl-editable-image-wrap--passive${
          hasSrc ? "" : " mte-tpl-editable-image-wrap--empty mte-tpl-links-thumb-empty"
        }`.trim()}
      >
        <span className="mte-tpl-editable-avatar-btn mte-tpl-editable-image-btn">
          {hasSrc ? (
            <>
              {pendingImage ? (
                <span className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" aria-hidden />
              ) : null}
              <img
                ref={bindImgRef}
                src={displaySrc}
                alt={alt}
                className={className}
                style={{
                  ...resolvedImgStyle,
                  opacity: pendingImage ? 0 : undefined,
                }}
                onLoad={markImageReady}
                onError={markImageFailed}
              />
            </>
          ) : (
            <span className={className} aria-hidden />
          )}
          {!hasSrc ? (
            <span className="mte-tpl-editable-image-hover" aria-hidden>
              <MdOutlinePhotoCamera />
            </span>
          ) : null}
        </span>
      </div>
    );
  }

  return (
    <label
      className={`mte-tpl-editable-image-wrap${
        hasSrc ? "" : " mte-tpl-editable-image-wrap--empty mte-tpl-links-thumb-empty"
      }`.trim()}
      data-om-edit
      aria-label={hasSrc ? "Change image" : "Add image"}
      onClick={stopParentNav}
      onMouseDown={stopParentNav}
    >
      <input
        type="file"
        accept="image/*"
        className="mte-tpl-editable-image-file"
        disabled={busy}
        onChange={runUpload}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="mte-tpl-editable-avatar-btn mte-tpl-editable-image-btn">
        {hasSrc ? (
          <>
            {pendingImage ? (
              <span className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" aria-hidden />
            ) : null}
            <img
              ref={bindImgRef}
              src={displaySrc}
              alt={alt}
              className={className}
              style={{
                ...resolvedImgStyle,
                opacity: pendingImage ? 0 : undefined,
              }}
              onLoad={markImageReady}
              onError={markImageFailed}
            />
          </>
        ) : (
          <span className={className} aria-hidden />
        )}
        <span className="mte-tpl-editable-image-hover" aria-hidden>
          <MdOutlinePhotoCamera />
        </span>
        {busy && (
          <span
            className="mte-tpl-editable-image-busy"
            role="status"
            aria-label="Uploading"
          >
            <AiOutlineLoading3Quarters className="mte-tpl-editable-image-spin" />
          </span>
        )}
      </span>
    </label>
  );
}

function bannerContentPaths(which: string, b: BannerItem) {
  const slot =
    which === "promo" || which === "footer" || which === "hero" ? which : "hero";
  return websiteBannerContentPaths(slot, b);
}

// ── Section shell ─────────────────────────────────────────────
interface ShellProps {
  T: ThemeTokens;
  cfg?: SectionCfg;
  heading?: string;
  eyebrow?: string;
  children?: React.ReactNode;
  fullBleed?: boolean;
  /** Keep the section title visible above a full-bleed body. */
  headingOutsideBleed?: boolean;
  noTopPad?: boolean;
  independentShellClassName?: string;
  /**
   * Public only: soft-fade live body when it replaces a skeleton so the
   * swap doesn't hard-cut. No-op in the editor (`editable`).
   */
  arrive?: boolean;
  /** When set (and not editable), clicking the section title runs this. */
  onHeadingClick?: () => void;
}
export function Shell({
  T,
  cfg,
  heading,
  eyebrow,
  children,
  fullBleed,
  headingOutsideBleed,
  noTopPad,
  independentShellClassName,
  arrive,
  onHeadingClick,
}: ShellProps) {
  const sectionPath = useTemplateSectionInstance();
  const analytics = useTemplateAnalyticsOptional();
  const { get, editable } = useEdit();
  const shellClass = sectionPathClassParts(sectionPath);
  const bgTone = cfg?.bgTone;
  const bg =
    bgTone === "surface" ? T.surface : bgTone === "accent" ? T.accent : T.bg;
  const fg = bgTone === "accent" ? T.accentInk : T.ink;
  const muted = bgTone === "accent" ? T.accentInk : T.muted;
  // Only own the shell title when cfg.heading or the heading prop is set.
  // Schema blocks (Section Header, etc.) and banners strip heading so they can
  // render their own copy — still don't pull section.*.heading from contentEdits
  // or Headline edits leak a duplicate uppercase shell title.
  const shellOwnsHeading =
    cfg?.heading !== undefined || heading !== undefined;
  const headingFallback =
    cfg?.heading !== undefined ? cfg.heading : heading;
  // Prefer contentEdits so clearing on canvas / restoring from the drawer stay in sync.
  const liveHeading = !shellOwnsHeading
    ? ""
    : sectionPath
      ? String(
          get(
            `section.${sectionPath}.heading`,
            String(headingFallback ?? ""),
          ) ?? "",
        )
      : String(headingFallback ?? "");
  const liveEyebrow =
    sectionPath && eyebrow
      ? String(get(`section.${sectionPath}.eyebrow`, eyebrow) || "")
      : String(eyebrow || "");
  // Collapse the title band when heading (and eyebrow) are empty — same as
  // other sections. Restore the title from the Edit drawer, not a canvas
  // "Add section title" placeholder that leaves a blank gap (My Links).
  const showHeadingRow =
    Boolean(liveHeading.trim()) || Boolean(liveEyebrow.trim());
  /* Vertical rhythm lives on `.mte-tpl-sections-stack` + `--tpl-section-gap`; shells are horizontal gutter only (CSS). */
  const shellPad = fullBleed ? "0" : `0 ${T.gutter} 0`;
  const editPlaceholder = tplEditPlaceholderColor(bg);
  const shellStyle = React.useMemo(
    () =>
      ({
        ["--tpl-shell-bg" as string]: bg,
        ["--tpl-shell-fg" as string]: fg,
        ["--tpl-shell-muted" as string]: muted,
        ["--tpl-shell-pad" as string]: shellPad,
        /* Section bgTone (e.g. light accent on Midnight) can differ from page canvas. */
        ["--tpl-edit-placeholder" as string]: editPlaceholder,
      }) as React.CSSProperties,
    [bg, fg, muted, shellPad, editPlaceholder],
  );
  const shellRef = useTemplateStyles<HTMLElement>(shellStyle);
  // Expose the background tone so the stylesheet can give surface/accent shells
  // internal vertical padding (content air inside the tinted band). Inter-section
  // rhythm stays on `.mte-tpl-sections-stack` gap — don't pad the editor chrome too.
  // Full-bleed sections (banners, etc.) manage their own padding and opt out.
  const shellToneAttrs: Record<string, string> = {
    "data-template-shell-tone": bgTone || "bg",
    ...(fullBleed ? { "data-template-shell-fullbleed": "true" } : {}),
  };
  const headingDomId = sectionPath
    ? `mte-shell-h-${sectionPath.replace(/[^a-zA-Z0-9_-]/g, "-")}`
    : undefined;
  return (
    <section
      ref={shellRef as React.Ref<HTMLElement>}
      className={[
        independentShellClassName ? "mte-tpl-section-shell" : "mte-tpl-shell",
        independentShellClassName || "",
        independentShellClassName
          ? `${independentShellClassName}--page-${shellClass.page}`
          : `mte-tpl-shell--page-${shellClass.page}`,
        independentShellClassName
          ? `${independentShellClassName}--section-${shellClass.section}`
          : `mte-tpl-shell--section-${shellClass.section}`,
        independentShellClassName
          ? `${independentShellClassName}--${shellClass.combo}`
          : `mte-tpl-shell--${shellClass.combo}`,
      ]
        .filter(Boolean)
        .join(" ")}
      data-template-shell="true"
      data-template-shell-page={shellClass.page}
      data-template-shell-section={shellClass.section}
      aria-labelledby={
        showHeadingRow && liveHeading.trim() && headingDomId
          ? headingDomId
          : undefined
      }
      aria-label={
        showHeadingRow && liveHeading.trim() && !headingDomId
          ? liveHeading.trim()
          : undefined
      }
      {...shellToneAttrs}
    >
      {showHeadingRow && (!fullBleed || headingOutsideBleed) && (
        <div
          className={[
            "mte-tpl-shell-head",
            `mte-tpl-shell-head--${shellClass.combo}`,
          ].join(" ")}
          style={
            fullBleed && headingOutsideBleed
              ? { paddingInline: T.gutter, paddingBottom: 18 }
              : undefined
          }
        >
          <div>
            {liveEyebrow ? (
              <div
                className={[
                  "mte-tpl-shell-eyebrow",
                  `mte-tpl-shell-eyebrow--${shellClass.combo}`,
                ].join(" ")}
              >
                {sectionPath ? (
                  <E path={`section.${sectionPath}.eyebrow`}>{eyebrow}</E>
                ) : (
                  liveEyebrow
                )}
              </div>
            ) : null}
            {liveHeading.trim() ? (
              <h2
                id={headingDomId}
                className={[
                  "mte-tpl-shell-h2",
                  `mte-tpl-shell-h2--${shellClass.combo}`,
                  onHeadingClick ? "mte-tpl-shell-h2--nav" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {onHeadingClick ? (
                  <button
                    type="button"
                    className="mte-tpl-shell-h2-btn"
                    onClick={onHeadingClick}
                  >
                    {liveHeading}
                  </button>
                ) : sectionPath ? (
                  <E path={`section.${sectionPath}.heading`}>{liveHeading}</E>
                ) : (
                  liveHeading
                )}
              </h2>
            ) : null}
          </div>
          {cfg?.ctaLink && (
            <a
              href="#"
              className="mte-tpl-shell-cta"
              onClick={(e) => {
                if (editable) return;
                e.preventDefault();
                const label = String(cfg.ctaLink || "").trim();
                if (!label) return;
                const sectionId =
                  String(sectionPath || "").split("/").pop()?.split(".").pop() ||
                  "section";
                analytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
                  sectionId,
                  sectionType: "Carousel",
                  entityType: "section",
                  entityId: label,
                  entityLabel: label,
                });
              }}
            >
              {cfg.ctaLink}
            </a>
          )}
        </div>
      )}
      {arrive ? (
        <div className="mte-tpl-content-arrive sb-animate-content-arrive">
          {children}
        </div>
      ) : (
        children
      )}
    </section>
  );
}

// NavSection and FooterSection are now in sections/NavSection.tsx and sections/FooterSection.tsx.
// They are re-exported at the top of this file via `export { NavSection } from "./sections/NavSection"`.

// ── (placeholder to preserve line structure below) ────────────
// ── Bio (section components below this point) ─────────────────
// ── Bio ───────────────────────────────────────────────────────
export interface SectionProps {
  T: ThemeTokens;
  cfg?: SectionCfg;
  onNavigate?: TemplateNavigateFn;
  /**
   * Whether this section's page body layer is the visible route.
   * Pre-mounted pages stay in the tree while hidden; draft UI (forms) should
   * reset when this flips to false.
   */
  isActive?: boolean;
  /** Public site: real paths for blog post links (enables open-in-new-tab). */
  blogPostHrefForSlug?: (slug: string) => string;
  /** Public site: `/website/:user/platform/:key` for highlight → platform (new tab). */
  publicPlatformHrefForKey?: (platformKey: string) => string;
  carouselDeepLink?: TemplateCarouselDeepLink | null;
  onCarouselDeepLinkConsumed?: () => void;
  platformDeepLink?: TemplatePlatformDeepLink | null;
  onPlatformDeepLinkConsumed?: () => void;
  /** Parent-owned shop browse (URL + preview chrome); keeps grid in sync when layers stay mounted. */
  shopBrowseSelection?: ShopBrowseSelection;
  shopViewSelection?: TemplateShopView;
}

function BioSectionSkeleton({ style = "center" }: { style?: string }) {
  if (style === "magazine") {
    return (
      <div className="mte-tpl-bio-mag-grid" aria-hidden="true">
        <div className="mte-tpl-bio-mag-img mte-tpl-skel-fill" />
        <div className="mte-tpl-bio-mag-col">
          <div className="mte-tpl-skel-line mte-tpl-skel-line--display" />
          <div className="mte-tpl-skel-line mte-tpl-skel-line--lg" />
          <div className="mte-tpl-skel-line mte-tpl-skel-line--md" />
        </div>
      </div>
    );
  }
  return (
    <div className="mte-tpl-bio-center-wrap" aria-hidden="true">
      <div className="mte-tpl-bio-ava-ctr">
        <div className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
      </div>
      <div className="mte-tpl-skel-line mte-tpl-skel-line--display" style={{ animationDelay: "60ms" }} />
      <div className="mte-tpl-skel-line mte-tpl-skel-line--md" style={{ animationDelay: "120ms" }} />
      <div className="mte-tpl-skel-line mte-tpl-skel-line--sm" style={{ animationDelay: "180ms" }} />
    </div>
  );
}

export function BioSection({ T, cfg, onNavigate }: SectionProps) {
  const CREATOR = useCreatorData();
  const { get, editable } = useEdit();
  const hasBioContent = Boolean(
    String(CREATOR.bio?.name || "").trim() ||
      String(CREATOR.bio?.displayName || "").trim() ||
      String(CREATOR.bio?.avatar || "").trim(),
  );
  const isLoading = useCreatorSectionLoading(hasBioContent);
  const sectionPath = useTemplateSectionInstance();
  const analytics = useTemplateAnalyticsOptional();
  const sectionId = sectionIdFromPath(sectionPath, "bio");
  const displaySocial = useBioSocialForDisplay(
    CREATOR.bio?.social,
    CREATOR.bio?.userId,
    CREATOR.bio?.username,
  );
  if (isLoading) {
    return (
      <Shell T={T} cfg={cfg}>
        <BioSectionSkeleton style={(cfg?.style as string) || "center"} />
      </Shell>
    );
  }
  const trackBioSocial = (platform: string, url: string) => {
    analytics?.trackSocialLink({
      platform,
      url,
      sectionId,
      sectionType: "Bio",
    });
  };
  const bioClass = sectionPathClassParts(sectionPath);
  const b = CREATOR.bio;
  const style = cfg?.style || "center";
  const showAvatar = cfg?.showAvatar !== false;
  // Location toggle removed — tagline already sits under the name.
  const showLocation = false;
  const showTagline = cfg?.showTagline !== false;
  const showSocial = cfg?.showSocial !== false;
  const showShopAllProducts = cfg?.showShopAllProducts !== false;
  const showBrowseAllContent = cfg?.showBrowseAllContent !== false;
  const heroCtas = (
    <BioHeroCtas
      onNavigate={onNavigate}
      showShopAll={showShopAllProducts}
      showBrowseAll={showBrowseAllContent}
    />
  );

  const bioCardEdge = {
    ["--tpl-bio-card-edge" as string]: `${T.ink}10`,
  } as React.CSSProperties;
  // Missing content edit keeps the saved name. A blank edit stays blank.
  const bioName = String(b.displayName || "").trim();
  // <E path="bio.tagline"> children = live profile tagline (LiB / user.description),
  // not the resolved contentEdit — so no-op blur only clears when text matches live.
  // Blank-line collapse for display/save lives in <E> (sanitizeBioDescription).
  const liveTagline = String(get("bio.tagline", b.tagline || "") || "");
  const hasTagline = showTagline && Boolean(liveTagline.trim());
  const nameEdit = get("bio.name", "\u0000");
  const liveName =
    nameEdit === "\u0000" ? bioName : String(nameEdit || "").trim();
  const hasName = Boolean(liveName);

  if (style === "magazine")
    return (
      <Shell T={T} cfg={cfg} arrive>
        <div
          className={`mte-tpl-bio-mag-grid mte-tpl-bio-section mte-tpl-bio-section--${bioClass.combo} mte-tpl-bio-section--style-magazine`}
        >
          {showAvatar && (
            <EditableBioAvatar
              src={b.avatar}
              className="mte-tpl-img-filter mte-tpl-bio-mag-img"
            />
          )}
          <div className="mte-tpl-bio-mag-col">
            {showLocation && (
              <div className="mte-tpl-bio-issue">
                ISSUE NO. 04 · <E path="bio.location">{b.location}</E>
              </div>
            )}
            {(hasName || editable) ? (
            <h1 className="mte-tpl-bio-mag-h1">
              <E path="bio.name" placeholder="Add title">{liveName}</E>
            </h1>
            ) : null}
            {showLocation && (
              <div className="mte-tpl-bio-mag-handle">
                <E path="bio.handle">{b.handle}</E>
              </div>
            )}
            {hasTagline && (
              <div className="mte-tpl-bio-mag-tag">
                <E path="bio.tagline" multiline>
                  {b.tagline || ""}
                </E>
              </div>
            )}
            <BioSocialLinks
              social={displaySocial}
              showSocial={showSocial}
              className="mte-tpl-bio-social-row"
              onSocialClick={trackBioSocial}
            />
          </div>
        </div>
      </Shell>
    );
  if (style === "hero")
    return (
      <Shell T={T} cfg={cfg} fullBleed arrive>
        <div
          className={`mte-tpl-bio-hero-wrap mte-tpl-bio-section mte-tpl-bio-section--${bioClass.combo} mte-tpl-bio-section--style-hero`}
        >
          {showAvatar && (
            <EditableBioAvatar
              src={b.avatar}
              className="mte-tpl-img-filter mte-tpl-bio-hero-img"
            />
          )}
          <div className="mte-tpl-bio-hero-grad" />
          <div className="mte-tpl-bio-hero-inner">
            {(hasName || editable) ? (
            <h1 className="mte-tpl-bio-hero-h1">
              <E path="bio.name" placeholder="Add title">{liveName}</E>
            </h1>
            ) : null}
            {T.device === "mobile" ? (
              <div className="mte-tpl-bio-hero-stack">
                {showLocation && (
                  <div className="mte-tpl-bio-hero-meta">
                    <E path="bio.handle">{b.handle}</E>
                    {b.location ? (
                      <>
                        {" · "}
                        <E path="bio.location">{b.location}</E>
                      </>
                    ) : null}
                  </div>
                )}
                {hasTagline && (
                  <div className="mte-tpl-bio-tag-sm">
                    <E path="bio.tagline" multiline>
                      {b.tagline || ""}
                    </E>
                  </div>
                )}
                {heroCtas}
                <BioHeroShopFollow
                  social={displaySocial}
                  showSocial={showSocial}
                  onSocialClick={trackBioSocial}
                />
              </div>
            ) : (
              <>
                <div className="mte-tpl-bio-hero-row">
                  <div>
                    {showLocation && (
                      <div className="mte-tpl-bio-hero-meta-lg">
                        <E path="bio.handle">{b.handle}</E>
                        {b.location ? (
                          <>
                            {" · "}
                            <E path="bio.location">{b.location}</E>
                          </>
                        ) : null}
                      </div>
                    )}
                    {hasTagline && (
                      <div className="mte-tpl-bio-hero-tag-lg">
                        <E path="bio.tagline" multiline>
                          {b.tagline || ""}
                        </E>
                      </div>
                    )}
                  </div>
                  {heroCtas}
                </div>
                <BioHeroShopFollow
                  social={displaySocial}
                  showSocial={showSocial}
                  onSocialClick={trackBioSocial}
                />
              </>
            )}
          </div>
        </div>
      </Shell>
    );
  if (style === "stacked-large")
    return (
      <Shell T={T} cfg={cfg} arrive>
        <div
          className={`mte-tpl-bio-stack-lg mte-tpl-bio-section mte-tpl-bio-section--${bioClass.combo} mte-tpl-bio-section--style-stacked-large`}
        >
          {(hasName || editable) ? (
          <h1 className="mte-tpl-bio-stack-lg-h1">
            <E path="bio.name" placeholder="Add title">{liveName}</E>
          </h1>
          ) : null}
          {showAvatar && (
            <EditableBioAvatar
              src={b.avatar}
              className="mte-tpl-img-filter mte-tpl-bio-ava-lg mte-tpl-bio-ava-lg--a"
            />
          )}
          {showLocation && (
            <div className="mte-tpl-bio-loc-wide">
              <E path="bio.handle">{b.handle}</E>
              {b.location ? (
                <>
                  {" · "}
                  <E path="bio.location">{b.location}</E>
                </>
              ) : null}
            </div>
          )}
          {hasTagline && (
            <div className="mte-tpl-bio-tag-stack">
              <E path="bio.tagline" multiline>
                {b.tagline || ""}
              </E>
            </div>
          )}
          <BioSocialLinks
            social={displaySocial}
            showSocial={showSocial}
            className="mte-tpl-bio-social-row mte-tpl-bio-social-row--center mte-tpl-bio-social-row--full"
            onSocialClick={trackBioSocial}
          />
        </div>
      </Shell>
    );
  if (style === "card")
    return (
      <Shell T={T} cfg={cfg} arrive>
        <div
          className={`mte-tpl-bio-card mte-tpl-bio-section mte-tpl-bio-section--${bioClass.combo} mte-tpl-bio-section--style-card`}
        >
          {showAvatar && (
            <EditableBioAvatar
              src={b.avatar}
              className="mte-tpl-img-filter mte-tpl-bio-ava-card"
            />
          )}
          {(hasName || editable) ? (
          <h1 className="mte-tpl-bio-card-h1">
            <E path="bio.name" placeholder="Add title">{liveName}</E>
          </h1>
          ) : null}
          {showLocation && (
            <div className="mte-tpl-bio-loc-card">
              <E path="bio.handle">{b.handle}</E>
              {b.location ? (
                <>
                  {" · "}
                  <E path="bio.location">{b.location}</E>
                </>
              ) : null}
            </div>
          )}
          {hasTagline && (
            <div className="mte-tpl-bio-tag-card">
              <E path="bio.tagline" multiline>
                {b.tagline || ""}
              </E>
            </div>
          )}
          {showSocial && (
            <BioSocialLinks
              social={displaySocial}
              showSocial={showSocial}
              className="mte-tpl-bio-social-row mte-tpl-bio-social-row--center mte-tpl-bio-social-row--full"
              btnClassName={`${btnClass("ghost")} mte-tpl-btn--grow-center`}
              onSocialClick={trackBioSocial}
            />
          )}
        </div>
      </Shell>
    );
  if (style === "split")
    return (
      <Shell T={T} cfg={cfg} arrive>
        <div
          className={`mte-tpl-bio-split-grid mte-tpl-bio-section mte-tpl-bio-section--${bioClass.combo} mte-tpl-bio-section--style-split`}
        >
          {showAvatar && (
            <EditableBioAvatar
              src={b.avatar}
              className="mte-tpl-img-filter mte-tpl-bio-ava-split"
            />
          )}
          <div>
            {showLocation && !!String(b.location || "").trim() && (
              <div className="mte-tpl-bio-loc-split">
                <E path="bio.location">{b.location}</E>
              </div>
            )}
            {(hasName || editable) ? (
            <h1 className="mte-tpl-bio-split-h1">
              <E path="bio.name" placeholder="Add title">{liveName}</E>
            </h1>
            ) : null}
            {showLocation && (
              <div className="mte-tpl-bio-handle-split">
                <E path="bio.handle">{b.handle}</E>
              </div>
            )}
            {hasTagline && (
              <div className="mte-tpl-bio-tag-split">
                <E path="bio.tagline" multiline>
                  {b.tagline || ""}
                </E>
              </div>
            )}
            {heroCtas}
            <BioHeroShopFollow
              social={displaySocial}
              showSocial={showSocial}
              onSocialClick={trackBioSocial}
              variant="page"
              socialClassName="mte-tpl-bio-split-soc"
            />
          </div>
        </div>
      </Shell>
    );
  if (style === "minimal")
    return (
      <Shell T={T} cfg={cfg} arrive>
        <div
          className={`mte-tpl-bio-min-wrap mte-tpl-bio-section mte-tpl-bio-section--${bioClass.combo} mte-tpl-bio-section--style-minimal`}
        >
          {(hasName || editable) ? (
          <h1 className="mte-tpl-bio-min-h1">
            <E path="bio.name" placeholder="Add title">{liveName}</E>
          </h1>
          ) : null}
          {showLocation && (
            <div className="mte-tpl-bio-min-loc">
              <E path="bio.handle">{b.handle}</E>
              {b.location ? (
                <>
                  {" · "}
                  <E path="bio.location">{b.location}</E>
                </>
              ) : null}
            </div>
          )}
        </div>
      </Shell>
    );
  // center (default)
  return (
    <Shell T={T} cfg={cfg} arrive>
      <TplVarsDiv
        className={`mte-tpl-bio-center-wrap mte-tpl-bio-section mte-tpl-bio-section--${bioClass.combo} mte-tpl-bio-section--style-center`}
      >
        {showAvatar && (
          <EditableBioAvatar
            src={b.avatar}
            className="mte-tpl-img-filter mte-tpl-bio-ava-ctr"
          />
        )}
        {(hasName || editable) ? (
          <h1 className="mte-tpl-bio-ctr-h1">
            <E path="bio.name" placeholder="Add title">{liveName}</E>
          </h1>
        ) : null}
        {showLocation && (
          <div className="mte-tpl-bio-loc-ctr">
            <E path="bio.handle">{b.handle}</E>
            {b.location ? (
              <>
                {" · "}
                <E path="bio.location">{b.location}</E>
              </>
            ) : null}
          </div>
        )}
        {hasTagline && (
          <div className="mte-tpl-bio-tag-ctr">
            <E path="bio.tagline" multiline>
              {b.tagline || ""}
            </E>
          </div>
        )}
        {heroCtas}
        <BioHeroShopFollow
          social={displaySocial}
          showSocial={showSocial}
          onSocialClick={trackBioSocial}
          variant="page"
          socialClassName="mte-tpl-bio-soc-ctr mte-tpl-bio-social-row"
        />
      </TplVarsDiv>
    </Shell>
  );
}
BioSection.options = {
  style: {
    kind: "radio",
    label: "Layout",
    options: [
      "center",
      "split",
      "minimal",
      "magazine",
      "hero",
      "stacked-large",
      "card",
    ],
    default: "center",
  },
  showAvatar: { kind: "toggle", label: "Show avatar", default: true },
  showLocation: { kind: "hidden", label: "Show location", default: false },
  showTagline: { kind: "toggle", label: "Show tagline", default: true },
  showSocial: { kind: "toggle", label: "Show social links", default: true },
  showShopAllProducts: {
    kind: "toggle",
    label: "Show Shop All Products",
    default: true,
  },
  showBrowseAllContent: {
    kind: "toggle",
    label: "Show Browse All Content",
    default: true,
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
  heading: { kind: "hidden", label: "" },
} as SectionOptions;

// ── Highlights ────────────────────────────────────────────────
export function HighlightsSection({
  T,
  cfg,
  onNavigate,
  publicPlatformHrefForKey,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const hasHighlights = templatePlatformHighlightTabs(CREATOR.highlights).length > 0;
  const isLoading = useCreatorSectionLoading(hasHighlights);
  const { editable } = useEdit();
  const analytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();
  const sectionId = sectionIdFromPath(sectionPath, "highlights");
  if (isLoading && !editable) {
    const shape = (cfg?.style as string) || "circle";
    const frameShape =
      shape === "square" || shape === "rounded" || shape === "pill"
        ? shape
        : "circle";
    const showLabels = cfg?.showLabels !== false;
    const n = Math.min(
      12,
      Math.max(1, (cfg?.count as number) || 8),
    );
    return (
      <Shell T={T} cfg={cfg}>
        <div className="mte-tpl-hl-strip" aria-hidden="true">
          {Array.from({ length: n }).map((_, i) => (
            <div
              key={i}
              className={`mte-tpl-hl-item mte-tpl-hl-item--${frameShape}`}
              style={{ animationDelay: `${i * 70}ms` }}
            >
              {/* Match live DOM: frame-link wraps frame; shape lives only on --frame modifiers */}
              <div className="mte-tpl-hl-frame-link">
                <div className={`mte-tpl-hl-frame mte-tpl-hl-frame--${frameShape}`}>
                  <div className="mte-tpl-hl-ph mte-tpl-skel-fill" />
                </div>
              </div>
              {showLabels ? (
                <div className="mte-tpl-hl-label">
                  <span className="mte-tpl-skel-line mte-tpl-skel-line--label" />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </Shell>
    );
  }
  // Live mode: only platforms with real feed posts (same filter as platform page).
  // Editor preview: fall back to connected socials when there are no highlights yet.
  // Seed / mock preview (no live bio identity): show cover-based MOCK highlights so
  // read-only onboarding isn't stuck with Bio + banner only.
  const highlightTabs = templatePlatformHighlightTabs(CREATOR.highlights);
  const isLiveSite = Boolean(
    CREATOR.bio?.userId?.trim() || CREATOR.bio?.username?.trim(),
  );
  const coverHighlights = (CREATOR.highlights ?? []).filter(
    (h) => String(h.cover || "").trim() || String(h.label || "").trim(),
  );
  const rawItems =
    highlightTabs.length > 0
      ? highlightTabs
      : editable
        ? CREATOR.bio.social.map((s) => ({
            id: `social-${s.platform}`,
            label: s.platform,
            cover: "",
            iconKey: undefined as string | undefined,
          }))
        : !isLiveSite
          ? coverHighlights
          : [];
  const count = (cfg?.count as number) || rawItems.length;
  const items = rawItems.slice(0, count);
  const shape = (cfg?.style as string) || "circle";
  const frameShape =
    shape === "square" || shape === "rounded" || shape === "pill"
      ? shape
      : "circle";
  const frameClass = `mte-tpl-hl-frame mte-tpl-hl-frame--${frameShape}`;
  const itemClass = `mte-tpl-hl-item mte-tpl-hl-item--${frameShape}`;
  if (!items.length) {
    if (!editable) return null;
    return (
      <Shell T={T} cfg={cfg} heading={cfg?.heading || ""}>
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Connect platforms or add highlights to preview this strip.
        </p>
      </Shell>
    );
  }
  return (
    <Shell T={T} cfg={cfg} heading={cfg?.heading || ""}>
      <div className="mte-tpl-hl-strip">
        {items.map((h) => {
          const iconKey = (h as { iconKey?: string }).iconKey;
          const IconComp = iconKey
            ? HIGHLIGHT_FILTER_ICONS[iconKey]
            : undefined;
          const FallbackIcon = !IconComp ? PLATFORM_ICONS[h.label] : undefined;
          const isNavigableHighlight = Boolean(onNavigate && iconKey);
          const href =
            isNavigableHighlight && publicPlatformHrefForKey && iconKey
              ? publicPlatformHrefForKey(iconKey)
              : undefined;
          const handleHighlightClick = (e: React.MouseEvent) => {
            if (!onNavigate || !iconKey) return;
            if (e.ctrlKey || e.metaKey || e.button === 1) return;
            e.preventDefault();
            analytics?.trackHighlightClick({
              label: h.label,
              platformKey: iconKey,
              sectionId,
            });
            onNavigate("content", {
              contentView: "latest",
              platformKey: iconKey,
              carouselSlug: iconKey,
            });
          };
          const frameHitClass = `mte-tpl-hl-frame-link${
            isNavigableHighlight ? " mte-tpl-hl-item--button" : ""
          }`;
          const frameEl = (
            <div className={frameClass}>
              {h.cover ? (
                <img
                  src={h.cover}
                  alt=""
                  className="mte-tpl-img-filter mte-tpl-hl-img"
                />
              ) : IconComp ? (
                <div className="mte-tpl-hl-ph mte-tpl-hl-ph--icon">
                  <IconComp />
                </div>
              ) : FallbackIcon ? (
                <div className="mte-tpl-hl-ph mte-tpl-hl-ph--icon">
                  <FallbackIcon />
                </div>
              ) : (
                <div className="mte-tpl-hl-ph">
                  {String(h.label || "").slice(0, 2)}
                </div>
              )}
            </div>
          );
          const labelEl =
            cfg?.showLabels !== false ? (
              <div className="mte-tpl-hl-label">
                <E path={`highlights.${h.id}.label`}>{h.label}</E>
              </div>
            ) : null;
          const hit = href ? (
            <a
              href={href}
              className={frameHitClass}
              onClick={handleHighlightClick}
            >
              {frameEl}
            </a>
          ) : (
            <button
              type="button"
              className={frameHitClass}
              onClick={() => {
                if (onNavigate && iconKey) {
                  analytics?.trackHighlightClick({
                    label: h.label,
                    platformKey: iconKey,
                    sectionId,
                  });
                  onNavigate("content", {
              contentView: "latest",
              platformKey: iconKey,
              carouselSlug: iconKey,
            });
                }
              }}
              disabled={!isNavigableHighlight}
            >
              {frameEl}
            </button>
          );
          return (
            <div key={h.id} className={itemClass}>
              {hit}
              {labelEl}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
HighlightsSection.options = {
  style: {
    kind: "radio",
    label: "Shape",
    options: ["circle", "square", "rounded", "pill"],
    default: "circle",
  },
  count: {
    kind: "number",
    label: "Visible items",
    min: 1,
    max: 12,
    default: 8,
  },
  showLabels: { kind: "toggle", label: "Show labels", default: true },
  heading: { kind: "text", label: "Heading", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Banner ────────────────────────────────────────────────────
interface BannerProps extends SectionProps {
  which?: "hero" | "promo" | "footer";
}
export function BannerSection({ T, cfg, which = "hero" }: BannerProps) {
  const CREATOR = useCreatorData();
  const sectionPath = useTemplateSectionInstance();
  const sectionIdFromInstance = sectionPath?.includes("/")
    ? sectionPath.split("/")[1]
    : undefined;
  const boundComponentId = String(cfg?.componentId || "").trim();
  const resolved = resolveWebsiteBanner(
    CREATOR.banners,
    cfg,
    sectionIdFromInstance,
  );
  const slot = resolved.banner ? resolved.slot : which;
  // When a section is bound to a specific row, never paint another slot's banner
  // (that was showing stale hero copy after Add). Prefer seeded contentEdits.
  const banner =
    resolved.banner ||
    (boundComponentId
      ? ({
          id: boundComponentId,
          title: "",
          subtitle: String(cfg?.subtitle || ""),
          cta: String(cfg?.cta || "Learn more"),
          url: "#",
          image: "",
          stayComponentRowId: boundComponentId,
        } as BannerItem)
      : CREATOR.banners?.[which]);
  const hasBannerContent = Boolean(
    banner?.image ||
      banner?.title ||
      boundComponentId ||
      String(cfg?.subtitle || "").trim() ||
      String(cfg?.cta || "").trim(),
  );
  const isLoading = useCreatorSectionLoading(hasBannerContent);
  const { editable, get } = useEdit();
  const templateAnalytics = useTemplateAnalyticsOptional();
  // Section title: contentEdits win when set; otherwise use cfg from Add/place.
  // Previously a missing edit key forced "" and ignored cfg — so Add updated
  // pages.cfg.heading while the canvas kept a stale section.*.heading edit.
  const cfgHeading = String(cfg?.heading ?? "").trim();
  const explicitSectionHeading = sectionPath
    ? get(`section.${sectionPath}.heading`, "\u0000")
    : "\u0000";
  const shellCfg: SectionCfg = {
    ...(cfg || {}),
    heading:
      explicitSectionHeading === "\u0000"
        ? cfgHeading
        : String(explicitSectionHeading),
  };
  if (isLoading && !editable) {
    const style = normalizeBannerStyle(
      (cfg?.style as string) || (which === "hero" ? "overlay-left" : "contained"),
    );
    if (style === "contained") {
      return (
        <Shell T={T} cfg={shellCfg}>
          <div className="mte-tpl-ban-split" aria-hidden="true">
            <div className="mte-tpl-ban-split-img mte-tpl-skel-fill" />
            <div className="mte-tpl-ban-split-copy">
              <div className="mte-tpl-skel-line mte-tpl-skel-line--display" />
              <div className="mte-tpl-skel-line mte-tpl-skel-line--md" />
              <div className="mte-tpl-skel-line mte-tpl-skel-line--btn" />
            </div>
          </div>
        </Shell>
      );
    }
    const frameClass =
      style === "overlay" ? "mte-tpl-ban-ov" : "mte-tpl-ban-fb";
    return (
      <Shell T={T} cfg={shellCfg}>
        <div className={frameClass} aria-hidden="true">
          <div className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
        </div>
      </Shell>
    );
  }
  const b = banner;
  if (!b) {
    if (!editable) return null;
    const emptyPaths = bannerContentPaths(which, {
      id: which,
      title: "",
      subtitle: "",
      cta: "Learn more",
      url: "#",
      image: "",
    });
    return (
      <Shell T={T} cfg={shellCfg} arrive>
        <EditableTemplateAsset
          slot={emptyPaths.imgSlot}
          src=""
          className="mte-tpl-img-filter mte-tpl-ban-split-img"
          allowEmpty
        />
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Click the image slot or use the inspector to upload a banner.
        </p>
      </Shell>
    );
  }
  if (!b.image && !editable) return null;
  const paths = bannerContentPaths(slot, b);
  const style = normalizeBannerStyle(
    (cfg?.style as string) || (which === "hero" ? "overlay-left" : "contained"),
  );
  const title = String(get(paths.title, b.title || "") || "").trim();
  const showSubtitle = cfg?.showSubtitle !== false;
  const subtitlePath = sectionPath
    ? `section.${sectionPath}.subtitle`
    : paths.subtitle;
  const subtitle = showSubtitle
    ? String(
        cfg?.subtitle ||
          get(subtitlePath, "") ||
          get(paths.subtitle, "") ||
          b.subtitle ||
          "",
      ).trim()
    : "";
  const paintSubtitle = showSubtitle && Boolean(subtitle);
  const showCta = cfg?.showCta !== false;
  const cta = resolvedBannerCta(
    cfg?.cta,
    b.cta,
    get(`${paths.ctaLink}.text`, ""),
  );
  const ctaHref =
    String(get(`${paths.ctaLink}.href`, "") || "").trim() || b.url || "#";
  const onBannerCtaClick = () => {
    templateAnalytics?.trackBannerCta({
      which,
      label: String(cta),
      url: ctaHref,
    });
  };
  const eyebrow =
    cfg?.showEyebrow === true
      ? String((cfg?.eyebrow as string) || "").trim()
      : "";
  const eyebrowText = sectionPath ? (
    <E path={`section.${sectionPath}.eyebrow`}>{eyebrow}</E>
  ) : (
    eyebrow
  );
  const mob = T.device === "mobile";
  const frameStyle = bannerImageFrameStyle(resolveBannerImageFrame(cfg, b), {
    objectFit: "contain",
  });
  const banImg = (cls: string) => {
    const node = paths.imgSlot ? (
      <EditableTemplateAsset
        slot={paths.imgSlot}
        src={b.image}
        className={cls}
        imgStyle={frameStyle}
        allowEmpty={editable && !b.image}
      />
    ) : (
      <img src={b.image} alt="" className={cls} style={frameStyle} />
    );
    return cls.includes("mte-tpl-ban-split-img") ? (
      <div className="mte-tpl-ban-split-frame">{node}</div>
    ) : (
      node
    );
  };

  if (style === "contained")
    return (
      <Shell T={T} cfg={shellCfg} arrive>
        <div className="mte-tpl-ban-split">
          {banImg("mte-tpl-img-filter mte-tpl-ban-split-img")}
          <div className="mte-tpl-ban-split-copy">
            {eyebrow ? (
              <div className="mte-tpl-ban-eyebrow">{eyebrowText}</div>
            ) : null}
            <h2 className="mte-tpl-ban-split-h2">
              <E path={paths.title} placeholder="Add title">
                {title}
              </E>
            </h2>
            {paintSubtitle ? (
              <p className="mte-tpl-ban-split-sub">
                <E path={subtitlePath} multiline placeholder="Optional supporting line">
                  {subtitle}
                </E>
              </p>
            ) : null}
            {showCta && (
              <EL
                path={paths.ctaLink}
                href={ctaHref}
                className={`${btnClass("primary")}${mob ? " mte-tpl-btn--full-width" : ""}`}
              >
                {cta}
              </EL>
            )}
          </div>
        </div>
      </Shell>
    );
  if (style === "overlay-center")
    return (
      <Shell T={T} cfg={shellCfg} fullBleed headingOutsideBleed arrive>
        <div className="mte-tpl-ban-ov">
          {banImg("mte-tpl-img-filter mte-tpl-ban-ov-img")}
          <div className="mte-tpl-ban-ov-scrim" />
          <div className="mte-tpl-ban-ov-inner">
            {eyebrow ? (
              <div className="mte-tpl-ban-ov-eyebrow">{eyebrowText}</div>
            ) : null}
            <h2 className="mte-tpl-ban-ov-h2">
              <E path={paths.title} placeholder="Add title">
                {title}
              </E>
            </h2>
            {paintSubtitle ? (
              <p className="mte-tpl-ban-ov-sub">
                <E path={subtitlePath} multiline placeholder="Optional supporting line">
                  {subtitle}
                </E>
              </p>
            ) : null}
            {showCta && (
              <EL
                path={paths.ctaLink}
                href={ctaHref}
                className={`${btnClass("onImage")} mte-tpl-btn--mt-8`}
              >
                {cta} →
              </EL>
            )}
          </div>
        </div>
      </Shell>
    );
  if (style === "ribbon")
    return (
      <Shell
        T={T}
        cfg={{
          ...shellCfg,
          bgTone: (cfg?.bgTone as "bg" | "surface" | "accent") || "accent",
        }}
        fullBleed
        headingOutsideBleed
        arrive
      >
        <div className="mte-tpl-ban-ribbon">
          {title || paintSubtitle || editable ? (
            <TemplateMarqueeTrack
              mode="loop"
              key={`${title}|${paintSubtitle ? subtitle : ""}`}
              outerClassName="mte-tpl-ban-ribbon-marquee"
              trackClassName="mte-tpl-ban-ribbon-track mte-tpl-ban-ribbon-track--js"
            >
              <span className="mte-tpl-ban-ribbon-txt">
                {title || editable ? (
                  <E path={paths.title} placeholder="Add title">
                    {title}
                  </E>
                ) : null}
                {title && paintSubtitle ? " · " : null}
                {paintSubtitle ? (
                  <E path={subtitlePath} multiline>
                    {subtitle}
                  </E>
                ) : null}
              </span>
              <span className="mte-tpl-ban-ribbon-txt" aria-hidden>
                {[title, paintSubtitle ? subtitle : ""]
                  .map((s) => String(s || "").trim())
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </TemplateMarqueeTrack>
          ) : null}
          {showCta && (
            <EL
              path={paths.ctaLink}
              href={ctaHref}
              className={`${btnClass("invertOnAccent")} mte-tpl-ban-ribbon-cta`}
            >
              {cta} →
            </EL>
          )}
        </div>
      </Shell>
    );
  return (
    <Shell T={T} cfg={shellCfg} fullBleed headingOutsideBleed arrive>
      <div className="mte-tpl-ban-fb">
        {banImg("mte-tpl-img-filter mte-tpl-ban-fb-img")}
        <div className="mte-tpl-ban-fb-grad" />
        <div className="mte-tpl-ban-fb-bar">
          <div>
            {eyebrow ? (
              <div className="mte-tpl-ban-fb-eyebrow">{eyebrowText}</div>
            ) : null}
            <h2 className="mte-tpl-ban-fb-h2">
              <E path={paths.title} placeholder="Add title">
                {title}
              </E>
            </h2>
            {paintSubtitle ? (
              <p className="mte-tpl-ban-fb-sub">
                <E path={subtitlePath} multiline placeholder="Optional supporting line">
                  {subtitle}
                </E>
              </p>
            ) : null}
          </div>
          {showCta && (
            <EL
              path={paths.ctaLink}
              href={ctaHref}
              onClick={onBannerCtaClick}
              className={`${btnClass("onImage")} mte-tpl-btn--nowrap`}
            >
              {cta}
            </EL>
          )}
        </div>
      </div>
    </Shell>
  );
}
BannerSection.options = {
  style: {
    kind: "radio",
    label: "Style",
    options: ["overlay-left", "contained", "ribbon", "overlay-center"],
    default: "overlay-left",
  },
  showEyebrow: { kind: "toggle", label: "Show eyebrow", default: false },
  eyebrow: { kind: "text", label: "Eyebrow label", default: "", placeholder: "e.g. New arrivals" },
  showSubtitle: { kind: "toggle", label: "Show subtitle", default: true },
  showCta: { kind: "toggle", label: "Show button", default: true },
  heading: { kind: "text", label: "Section title", default: "", placeholder: "Optional heading above the banner" },
  cta: { kind: "text", label: "Button text", default: "", placeholder: "Uses banner CTA" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Highlight cards (stayuptodate type === 'highlight') ──────
// Renders every visible `highlight` stayuptodate component as a clickable
// image card with a title + CTA. Falls back to MOCK_CREATOR.highlightCards
// in editor/preview mode. Cards link to the `ctaUrl` stored in
// `stayuptodate_components.details = "imageUrl,ctaUrl,view"`.
export function HighlightCardsSection({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const { editable, get } = useEdit();
  const isEditor = Boolean(useTemplateImageUpload());
  const templateAnalytics = useTemplateAnalyticsOptional();
  /* One section owns one highlight row. Unbound legacy sections still show every card. */
  const boundHighlightId = String(cfg?.componentId || "").trim();
  const allHighlights = CREATOR.highlightCards ?? [];
  const matched = boundHighlightId
    ? allHighlights.filter(
        (card) =>
          String(card.stayComponentRowId || card.id || "").trim() ===
          boundHighlightId,
      )
    : allHighlights;
  const cfgImage = String(cfg?.highlightImage || "").trim();
  const items =
    matched.length > 0
      ? matched
      : editable && cfgImage
        ? [
            {
              id: boundHighlightId || "pending-highlight",
              title: String(cfg?.highlightTitle || "").trim(),
              subtitle: "",
              cta: String(cfg?.cta || "Check it out!").trim() || "Check it out!",
              url: String(cfg?.highlightLink || "#").trim() || "#",
              image: cfgImage,
              stayComponentRowId: boundHighlightId || undefined,
            },
          ]
        : matched;
  const isLoading = useCreatorSectionLoading(items.length > 0);
  if (isLoading && !editable) {
    return (
      <Shell T={T} cfg={cfg}>
        <div className="mte-tpl-hlc-grid" aria-busy="true" aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="mte-tpl-hlc-card mte-tpl-hlc-card--static"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div className="mte-tpl-hlc-media">
                <div className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
              </div>
              <div className="mte-tpl-hlc-body">
                <div className="mte-tpl-skel-line mte-tpl-skel-line--md" />
                <div className="mte-tpl-skel-line mte-tpl-skel-line--sm" />
              </div>
            </div>
          ))}
        </div>
      </Shell>
    );
  }
  if (!items.length) {
    if (!editable) return null;
    return (
      <Shell T={T} cfg={cfg}>
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Add a Highlight from + Add component to preview this section.
        </p>
      </Shell>
    );
  }
  const showCta = cfg?.showCta !== false;
  return (
    <Shell T={T} cfg={cfg}>
      <div className="mte-tpl-hlc-grid mte-tpl-grid-enter">
        {items.map((h) => {
          const rid = h.stayComponentRowId;
          const titlePath = rid
            ? `sutc.${rid}.name`
            : `highlightCards.${h.id}.title`;
          const ctaPath = rid
            ? `sutc.${rid}.cta_placeholder`
            : `highlightCards.${h.id}.cta`;
          const urlPath = rid ? `sutc.${rid}.url` : `highlightCards.${h.id}.url`;
          const imgSlot = rid ? `sutc-${rid}-detail-image` : "";
          const liveTitle = String(get(titlePath, h.title || "") || "").trim();
          const inlineCta = rid ? get(`${ctaPath}.text`, "\u0000") : "\u0000";
          const liveCta = String(
            inlineCta !== "\u0000" ? inlineCta : get(ctaPath, h.cta || "") || "",
          ).trim();
          const liveUrl = String(get(urlPath, h.url || "") || "").trim();
          const href = liveUrl || "#";
          const cardInner = (
            <>
              {(h.image || (editable && imgSlot)) && (
                <div className="mte-tpl-hlc-media">
                  {imgSlot ? (
                    <EditableTemplateAsset
                      slot={imgSlot}
                      src={h.image}
                      allowEmpty={editable && !h.image}
                      className="mte-tpl-img-filter mte-tpl-hlc-img"
                    />
                  ) : (
                    <img
                      src={h.image}
                      alt=""
                      className="mte-tpl-img-filter mte-tpl-hlc-img"
                    />
                  )}
                </div>
              )}
              <div className="mte-tpl-hlc-body">
                {editable || liveTitle ? (
                  <div className="mte-tpl-hlc-title">
                    <E path={titlePath} placeholder="Add title">
                      {h.title || ""}
                    </E>
                  </div>
                ) : null}
                {showCta && liveCta ? (
                  <EL
                    as="span"
                    path={ctaPath}
                    href={href}
                    className={`${btnClass("primary")} mte-tpl-hlc-cta`}
                  >
                    {String(get(ctaPath, h.cta || "") || "")}
                  </EL>
                ) : null}
              </div>
            </>
          );
          return href !== "#" && !isEditor ? (
            <a
              key={h.id}
              href={href}
              className="mte-tpl-hlc-card"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.HIGHLIGHT_CARD_CLICK, {
                  sectionId: "highlightCards",
                  sectionType: "HighlightCards",
                  entityType: "highlight_card",
                  entityId: String(h.id),
                  entityLabel: liveTitle || liveCta || "",
                  details: { url: href, cta: liveCta, position: items.indexOf(h) },
                });
              }}
            >
              {cardInner}
            </a>
          ) : (
            <div key={h.id} className="mte-tpl-hlc-card mte-tpl-hlc-card--static">
              {cardInner}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
HighlightCardsSection.options = {
  heading: {
    kind: "text",
    label: "Section title",
    default: "",
    placeholder: "Optional section heading",
  },
  showCta: { kind: "toggle", label: "Show button", default: true },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Links ─────────────────────────────────────────────────────

/** Square thumb with live contentEdits + persisted `image_crop.coverFrame`. */
function LinksSectionThumb({
  id,
  thumb,
  className,
  imageCrop,
  editable,
}: {
  id: string;
  thumb: string;
  className: string;
  imageCrop?: unknown;
  editable: boolean;
}) {
  const coverStyle = useLinkThumbCoverStyle(id, imageCrop);
  const has = Boolean(thumb && String(thumb).trim());
  if (has && !editable) {
    return (
      <span className="mte-tpl-editable-image-btn">
        <img src={thumb} alt="" className={className} style={coverStyle} />
      </span>
    );
  }
  if (has || editable) {
    return (
      <EditableTemplateAsset
        slot={`user-link-${id}-thumb`}
        src={has ? thumb : ""}
        allowEmpty
        className={className}
        imgStyle={coverStyle}
        imageCrop={imageCrop}
      />
    );
  }
  return null;
}

export function LinksSection({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const sectionPath = useTemplateSectionInstance();
  const { editable, get } = useEdit();
  const userId = String(CREATOR.bio?.userId || "").trim();
  const topCollection = String(CREATOR.linksCollectionName || "").trim();
  const componentId = String(cfg?.componentId || "").trim();
  const editedHeading = sectionPath
    ? get(`section.${sectionPath}.heading`, "\u0000")
    : "\u0000";
  const displayHeading =
    editedHeading === "\u0000"
      ? String(cfg?.heading || "")
      : String(editedHeading);
  const creatorLoading = useCreatorDataLoading();
  const configuredCollection = String(cfg?.collectionName || "").trim();
  const { data: boundCollection, isFetched: boundNameFetched } = useQuery(
    ["website-link-section-name", userId, componentId],
    async () => {
      const { data } = await supabasePublic
        .from("template_components")
        .select("name")
        .eq("user_id", userId)
        .eq("id", componentId)
        .maybeSingle();
      return String((data as { name?: string } | null)?.name || "").trim();
    },
    {
      enabled: Boolean(userId && componentId),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
  // One cache entry per creator. Filtering by heading / collection used to be
  // part of the query key, so each time those resolved the list dropped to
  // empty and painted again.
  const { data: allLinkRows, isFetched: linksFetched } = useQuery(
    ["websiteEditorLinkSection", userId, "live"],
    async () => {
      const { data } = await supabasePublic
        .from("user_links")
        .select(
          "id, link_name, link_url, image_url, image_crop, index, collection_name, type, parent_id",
        )
        .eq("user_id", userId)
        .eq("status", "active")
        .in("type", ["website-template", "landing-page"])
        .order("index", { ascending: true });
      return (data ?? []) as {
        id: string | number;
        link_name?: string | null;
        link_url?: string | null;
        image_url?: string | null;
        image_crop?: unknown;
        collection_name?: string | null;
        type?: string | null;
        parent_id?: string | null;
      }[];
    },
    {
      enabled: Boolean(userId),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
  const sectionLinks = React.useMemo(() => {
    if (!userId) {
      if (creatorLoading) return null;
      return CREATOR.links || [];
    }
    if (!linksFetched) return null;
    if (!allLinkRows) return [];
    const mapRows = (
      rows: NonNullable<typeof allLinkRows>,
    ) =>
      rows.map((row) => ({
        id: String(row.id),
        title: String(row.link_name || ""),
        subtitle: "",
        url: String(row.link_url || "#"),
        thumb: String(row.image_url || "").trim(),
        imageCrop: row.image_crop,
      }));
    const byParent = componentId
      ? allLinkRows.filter(
          (row) => String(row.parent_id || "").trim() === componentId,
        )
      : [];
    if (componentId) return mapRows(byParent);
    // Wait for the stored collection name before guessing from the heading,
    // otherwise the top collection flashes in and is then replaced.
    if (componentId && !boundNameFetched && !configuredCollection) return null;
    const boundName = boundNameFetched
      ? String(boundCollection || "").trim()
      : "";
    const collectionKeys = Array.from(
      new Set(
        [
          boundName,
          configuredCollection,
          cfg?.heading,
          displayHeading,
          !displayHeading && !configuredCollection ? topCollection : "",
        ]
          .map((value) => String(value || "").trim())
          .filter(Boolean),
      ),
    );
    if (!collectionKeys.length) return [];
    const normalizeLinkKey = (value: string) =>
      value
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();
    const exact = new Set(collectionKeys.map((key) => key.toLowerCase()));
    const fuzzy = new Set(collectionKeys.map(normalizeLinkKey).filter(Boolean));
    const matched = allLinkRows.filter((row) => {
      const name = String(row.collection_name || "").trim();
      if (!name) return false;
      if (exact.has(name.toLowerCase())) return true;
      const folded = normalizeLinkKey(name);
      return Boolean(folded && fuzzy.has(folded));
    });
    const website = matched.filter((row) => row.type === "website-template");
    return mapRows(website.length ? website : matched);
  }, [
    userId,
    creatorLoading,
    CREATOR.links,
    linksFetched,
    allLinkRows,
    componentId,
    boundNameFetched,
    boundCollection,
    configuredCollection,
    cfg?.heading,
    displayHeading,
    topCollection,
  ]);
  const linkItems = sectionLinks ?? [];
  const isLoading = sectionLinks == null;
  const templateAnalytics = useTemplateAnalyticsOptional();
  const linkThumbUpload = useTemplateImageUpload();
  if (isLoading && !editable) {
    const style = (cfg?.style as string) || "list";
    const n = 3;
    if (style === "filled" || style === "outline") {
      return (
        <Shell T={T} cfg={cfg}>
          <div className="mte-tpl-links-pills" aria-hidden="true">
            {Array.from({ length: n }).map((_, i) => (
              <span
                key={i}
                className={`${btnClass(
                  style === "outline" ? "ghost" : "primary",
                )} mte-tpl-btn--full-width`}
                style={{ animationDelay: `${i * 60}ms`, minWidth: 96, opacity: 0.35 }}
              >
                &nbsp;
              </span>
            ))}
          </div>
        </Shell>
      );
    }
    if (style === "grid" || style === "list") {
      return (
        <Shell T={T} cfg={cfg}>
          <WebsiteTemplateMyLinksLayout
            T={T}
            layout={style === "grid" ? "grid" : "list"}
            items={[]}
            isLoading
            isMobileView={T.device === "mobile"}
            username={String(CREATOR.bio?.username || "")}
            userId={userId}
          />
        </Shell>
      );
    }
    if (style === "cards") {
      return (
        <Shell T={T} cfg={cfg}>
          <div className="mte-tpl-links-cards-grid" aria-hidden="true">
            {Array.from({ length: Math.min(n, 4) }).map((_, i) => (
              <div
                key={i}
                className="mte-tpl-links-media"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="mte-tpl-links-media-frame">
                  <div className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
                </div>
                <div className="mte-tpl-links-media-body">
                  <div className="mte-tpl-skel-line mte-tpl-skel-line--md" />
                </div>
              </div>
            ))}
          </div>
        </Shell>
      );
    }
    return (
      <Shell T={T} cfg={cfg}>
        <WebsiteTemplateMyLinksLayout
          T={T}
          layout="list"
          items={[]}
          isLoading
          isMobileView={T.device === "mobile"}
          username={String(CREATOR.bio?.username || "")}
          userId={userId}
        />
      </Shell>
    );
  }
  // No item-count cap — show every link in this section.
  const items = linkItems;
  // Respect an explicit empty Title — don't fall back to CREATOR.linksTitle
  // (that left a blank My Links heading band after clearing the drawer field).
  const heading =
    displayHeading ||
    (typeof cfg?.heading === "string" ? cfg.heading : "") ||
    String(CREATOR.linksTitle || "");
  const linksStillResolving = sectionLinks == null;
  if (!items.length) {
    if (!editable) return null;
    if (linksStillResolving) {
      const pendingStyle = (cfg?.style as string) || "list";
      return (
        <Shell T={T} cfg={cfg} heading={heading}>
          <WebsiteTemplateMyLinksLayout
            T={T}
            layout={pendingStyle === "grid" ? "grid" : "list"}
            items={[]}
            isLoading
            isMobileView={T.device === "mobile"}
            username={String(CREATOR.bio?.username || "")}
            userId={userId}
          />
        </Shell>
      );
    }
    return (
      <Shell T={T} cfg={cfg} heading={heading}>
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Add links in the editor on the right.
        </p>
      </Shell>
    );
  }
  const style = (cfg?.style as string) || "list";
  const showSub = cfg?.showSubtitle !== false;
  const showThumb = cfg?.showThumb !== false;
  // In editor mode omit href so the browser never schedules native <a> navigation.
  // EL keeps its own href prop and still shows the edit-text/visit-link dropdown.
  const isEditor = Boolean(linkThumbUpload);
  const onLinkClick =
    (l: (typeof items)[number]) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      templateAnalytics?.trackQuickLink({
        linkId: String(l.id),
        title: l.title,
        url: l.url,
      });
    };

  const renderCardsThumb = (l: (typeof items)[number]) => {
    const has = Boolean(l.thumb && String(l.thumb).trim());
    if (has || linkThumbUpload) {
      return (
        <LinksSectionThumb
          id={String(l.id)}
          thumb={has ? l.thumb : ""}
          className="mte-tpl-img-filter mte-tpl-links-media-img"
          imageCrop={(l as { imageCrop?: unknown }).imageCrop}
          editable={Boolean(linkThumbUpload)}
        />
      );
    }
    // Reserve 16∶9 media space when Show thumbnail is on (Loom).
    return (
      <span
        aria-hidden
        className="mte-tpl-links-media-img mte-tpl-links-media-img--empty"
      />
    );
  };
  if (style === "filled" || style === "outline")
    return (
      <Shell T={T} cfg={cfg} heading={heading}>
        <div className="mte-tpl-links-pills">
          {items.map((l) => (
            <EL
              key={l.id}
              path={`links.${l.id}`}
              href={l.url}
              className={`${btnClass(
                style === "outline" ? "ghost" : "primary",
              )} mte-tpl-btn--full-width`}
              navigateOnClick={!isEditor}
              onClick={!isEditor ? onLinkClick(l) : undefined}
            >
              {l.title}
            </EL>
          ))}
        </div>
      </Shell>
    );
  // List + grid share LiB My Links templates so website pages match
  // Website Link-in-Bio Link Section CSS (LineTemplate / GridTemplate).
  if (style === "grid" || style === "list") {
    return (
      <Shell T={T} cfg={cfg} heading={heading}>
        <WebsiteTemplateMyLinksLayout
          T={T}
          layout={style === "grid" ? "grid" : "list"}
          items={items.map((l) => ({
            id: String(l.id),
            title: l.title,
            url: l.url,
            thumb: l.thumb,
            imageCrop: (l as { imageCrop?: unknown }).imageCrop,
          }))}
          showThumb={showThumb}
          username={String(CREATOR.bio?.username || "")}
          userId={userId}
          componentName={heading || "Links"}
          isMobileView={T.device === "mobile"}
          disableNavigate={isEditor}
        />
      </Shell>
    );
  }
  if (style === "cards")
    return (
      <Shell T={T} cfg={cfg} heading={heading}>
        <div className="mte-tpl-links-cards-grid mte-tpl-grid-enter">
          {items.map((l) => (
            <a
              key={l.id}
              href={isEditor ? undefined : l.url}
              className="mte-tpl-links-media"
              onClick={onLinkClick(l)}
            >
              {showThumb ? (
                <div className="mte-tpl-links-media-frame">
                  {renderCardsThumb(l)}
                </div>
              ) : null}
              <div className="mte-tpl-links-media-body">
                <EL
                  path={`links.${l.id}`}
                  href={l.url}
                  as="span"
                  className={`mte-tpl-links-title ${showSub && l.subtitle ? "" : "mte-tpl-links-title--mb0"}`}
                >
                  {l.title}
                </EL>
                {showSub && !!l.subtitle && (
                  <div className="mte-tpl-links-sub">
                    <E path={`links.${l.id}.sub`}>{l.subtitle}</E>
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>
      </Shell>
    );
  // Filled / outline already returned above; default to list My Links.
  return (
    <Shell T={T} cfg={cfg} heading={heading}>
      <WebsiteTemplateMyLinksLayout
        T={T}
        layout="list"
        items={items.map((l) => ({
          id: String(l.id),
          title: l.title,
          url: l.url,
          thumb: l.thumb,
          imageCrop: (l as { imageCrop?: unknown }).imageCrop,
        }))}
        showThumb={showThumb}
        username={String(CREATOR.bio?.username || "")}
        userId={userId}
        componentName={heading || "Links"}
        isMobileView={T.device === "mobile"}
        disableNavigate={isEditor}
      />
    </Shell>
  );
}
LinksSection.options = {
  style: {
    kind: "radio",
    label: "Layout",
    options: [
      { value: "list", label: "List" },
      { value: "grid", label: "Grid" },
      { value: "cards", label: "Cards" },
      { value: "filled", label: "Filled" },
      { value: "outline", label: "Outline" },
    ],
    default: "list",
  },
  showThumb: { kind: "toggle", label: "Show thumbnail", default: true },
  heading: {
    kind: "text",
    label: "Title",
    default: "",
    placeholder: "e.g. My links",
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Carousel ──────────────────────────────────────────────────
interface CarouselProps extends SectionProps {
  idx?: number;
}

const PODCAST_BANNER_STYLES = new Set([
  "overlay-left",
  "contained",
  "ribbon",
  "overlay-center",
  // Legacy keys still accepted until normalizeBannerStyle runs.
  "fullbleed",
  "split",
  "colored",
]);

/** Latest-podcast “Display as: Banner” — same layouts as BannerSection, image preserved. */
function PodcastEpisodeBanner({
  T,
  cfg,
  title,
  subtitle,
  image,
  cta,
  showCta,
  href,
  onActivate,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
  title: string;
  subtitle?: string;
  image: string;
  cta: string;
  showCta: boolean;
  /** Spotify / episode URL — prefer a real link over JS navigation. */
  href?: string;
  onActivate?: () => void;
}) {
  const style = normalizeBannerStyle(cfg?.style || "overlay-left");
  const showSubtitle = cfg?.showSubtitle !== false;
  const sub = showSubtitle ? String(subtitle || "").trim() : "";
  const eyebrow =
    cfg?.showEyebrow === true
      ? String((cfg?.eyebrow as string) || "").trim()
      : "";
  const mob = T.device === "mobile";
  const imgCls = (base: string) =>
    `mte-tpl-img-filter ${base} mte-tpl-ban-img--contain`;
  const banImg = (cls: string) =>
    image ? (
      <img src={image} alt="" className={imgCls(cls)} />
    ) : (
      <div
        className={`${cls} mte-tpl-skel-fill mte-tpl-skel-fill--cover`}
        aria-hidden
      />
    );
  const linkHref = String(href || "").trim();
  const normalizedHref = linkHref
    ? linkHref.startsWith("http") || linkHref.startsWith("spotify:")
      ? linkHref
      : `https://${linkHref}`
    : "";
  const interactive = Boolean(normalizedHref || onActivate);
  const wrapProps = normalizedHref
    ? {
        href: normalizedHref,
        target: "_blank" as const,
        rel: "noopener noreferrer",
        onClick: () => onActivate?.(),
      }
    : interactive
      ? {
          role: "button" as const,
          tabIndex: 0,
          onClick: onActivate,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onActivate?.();
            }
          },
        }
      : {};
  const Wrap: "a" | "div" = normalizedHref ? "a" : "div";

  if (style === "contained") {
    return (
      <Shell T={T} cfg={{ ...cfg, heading: undefined }}>
        <Wrap className="mte-tpl-ban-split" {...(wrapProps as object)}>
          {banImg("mte-tpl-ban-split-img")}
          <div>
            {eyebrow ? <div className="mte-tpl-ban-eyebrow">{eyebrow}</div> : null}
            <h2 className="mte-tpl-ban-split-h2">{title}</h2>
            {sub ? <p className="mte-tpl-ban-split-sub">{sub}</p> : null}
            {showCta ? (
              <span
                className={`${btnClass("primary")}${mob ? " mte-tpl-btn--full-width" : ""}`}
              >
                {cta}
              </span>
            ) : null}
          </div>
        </Wrap>
      </Shell>
    );
  }
  if (style === "overlay-center") {
    return (
      <Shell T={T} cfg={{ ...cfg, heading: undefined }} fullBleed>
        <Wrap className="mte-tpl-ban-ov" {...(wrapProps as object)}>
          {banImg("mte-tpl-ban-ov-img")}
          <div className="mte-tpl-ban-ov-scrim" />
          <div className="mte-tpl-ban-ov-inner">
            {eyebrow ? (
              <div className="mte-tpl-ban-ov-eyebrow">{eyebrow}</div>
            ) : null}
            <h2 className="mte-tpl-ban-ov-h2">{title}</h2>
            {sub ? <p className="mte-tpl-ban-ov-sub">{sub}</p> : null}
            {showCta ? (
              <span className={`${btnClass("onImage")} mte-tpl-btn--mt-8`}>
                {cta} →
              </span>
            ) : null}
          </div>
        </Wrap>
      </Shell>
    );
  }
  if (style === "ribbon") {
    return (
      <Shell T={T} cfg={{ ...cfg, heading: undefined }} fullBleed>
        <Wrap className="mte-tpl-ban-ribbon" {...(wrapProps as object)}>
          {title || sub ? (
            <TemplateMarqueeTrack
              mode="loop"
              key={`${title}|${sub}`}
              outerClassName="mte-tpl-ban-ribbon-marquee"
              trackClassName="mte-tpl-ban-ribbon-track mte-tpl-ban-ribbon-track--js"
            >
              <span className="mte-tpl-ban-ribbon-txt">
                {title}
                {title && sub ? ` · ${sub}` : sub}
              </span>
              <span className="mte-tpl-ban-ribbon-txt" aria-hidden>
                {title}
                {title && sub ? ` · ${sub}` : sub}
              </span>
            </TemplateMarqueeTrack>
          ) : null}
          {showCta ? (
            <span
              className={`${btnClass("invertOnAccent")} mte-tpl-ban-ribbon-cta`}
            >
              {cta} →
            </span>
          ) : null}
        </Wrap>
      </Shell>
    );
  }
  return (
    <Shell T={T} cfg={{ ...cfg, heading: undefined }} fullBleed>
      <Wrap className="mte-tpl-ban-fb" {...(wrapProps as object)}>
        {banImg("mte-tpl-ban-fb-img")}
        <div className="mte-tpl-ban-fb-grad" />
        <div className="mte-tpl-ban-fb-bar">
          <div>
            {eyebrow ? <div className="mte-tpl-ban-fb-eyebrow">{eyebrow}</div> : null}
            <h2 className="mte-tpl-ban-fb-h2">{title}</h2>
            {sub ? <p className="mte-tpl-ban-fb-sub">{sub}</p> : null}
          </div>
          {showCta ? (
            <span className={`${btnClass("onImage")} mte-tpl-btn--nowrap`}>
              {cta}
            </span>
          ) : null}
        </div>
      </Wrap>
    </Shell>
  );
}

export function CarouselSection({
  T,
  cfg,
  idx = 0,
  onNavigate,
  isActive = true,
}: CarouselProps) {
  const CREATOR = useCreatorData();
  const { editable } = useEdit();
  const analytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();
  const cascade = useSectionNetworkCascade();
  const cascadeSectionId =
    String(sectionPath || "")
      .split("/")
      .pop()
      ?.trim() || "";
  const cascadeAllowsFetch =
    !cascade || !cascadeSectionId || cascade.canFetch(cascadeSectionId);
  // Below-fold strips wait until near the scroll viewport (generous margin so
  // the first home carousel still starts with Bio on screen).
  const nearViewport = useSectionNearViewport(
    cascadeSectionId,
    Boolean(isActive && cascadeSectionId),
  );
  const isMobile = T.device === "mobile";
  const liveCreatorLoading = useCreatorDataLoading();
  const sectionIdHint =
    String(sectionPath || "")
      .split("/")
      .pop()
      ?.trim()
      .toLowerCase() || "";
  const catalogIsGroup = String(cfg?.catalogType || "").trim() === "group";
  const isGroupStrip =
    catalogIsGroup || sectionIdHint.startsWith("x_group_");
  const carouselSlug = String(cfg?.carouselSlug || "auto").trim() || "auto";
  const boundComponentId = String(cfg?.componentId || "").trim();
  const collections = CREATOR.featuredCollections || [];
  const boundGroup = !isGroupStrip
    ? undefined
    : collections.find(
        (c) =>
          boundComponentId &&
          String(c.stayComponentRowId || "").trim() === boundComponentId,
      ) ||
      collections.find((c) => {
        const slug = String(c.renderSlug || "").trim();
        return (
          carouselSlug !== "auto" &&
          Boolean(slug) &&
          slug.toLowerCase() === carouselSlug.toLowerCase()
        );
      });
  const hasFeedStripContent =
    Boolean(CREATOR.featuredCarousel?.items?.length) ||
    (CREATOR.carousels?.length ?? 0) > 0;
  const showCarouselSkel = useCreatorSectionLoading(
    isGroupStrip
      ? (boundGroup?.items?.length ?? 0) > 0
      : hasFeedStripContent,
  );
  // Tweaks: which Recent Content (`template_components`) tab feeds this strip.
  // carouselSlug = auto → carousel-collection; otherwise a Latest/Series tab.
  const boundCarousel = findCarouselByComponentId(
    CREATOR.carousels,
    String(cfg?.componentId || ""),
  );
  const source = String(cfg?.source || "auto").toLowerCase();
  const preferFeed = String(cfg?.preferFeed || "").trim().toLowerCase();
  // Default Home products slot: Shop This Past Week, not Trending / This Week.
  const wantShopPastWeek =
    sectionIdHint === "carouselproducts" &&
    !boundComponentId &&
    (carouselSlug === "auto" || !carouselSlug);
  const picked = findRecentContentPick(CREATOR.carousels, carouselSlug);
  const platformPickKey = parsePlatformPickValue(carouselSlug);
  const count = Math.min(
    Math.max((cfg?.count as number) || FEATURED_CAROUSEL_POST_LIMIT, 1),
    FEATURED_CAROUSEL_POST_LIMIT,
  );
  const pendingAddedStrip =
    editable && carouselSlug !== "auto"
      ? peekPendingFeedStrip(carouselSlug)
      : null;
  const userId = CREATOR.bio?.userId || "";
  const username = CREATOR.bio?.username || "";
  // Explicit style wins; otherwise products marquee / This Week peek.
  const carouselStyle: "marquee" | "peek" =
    (cfg?.style as string) === "marquee" || (cfg?.style as string) === "peek"
      ? ((cfg?.style as string) as "marquee" | "peek")
      : preferFeed === "recent-products" || sectionIdHint === "carouselproducts"
        ? "marquee"
        : "peek";
  const featured = CREATOR.featuredCarousel;
  // Featured Carousel (`featuredCarousel` / `carouselSlug: auto`) is a
  // separate Home strip. Group extras only use their bound collection.
  const preferStaticCollection = isGroupStrip
    ? true
    : carouselSlug === "auto" &&
      source === "auto" &&
      !picked &&
      !preferFeed &&
      sectionIdHint !== "carouselshop" &&
      sectionIdHint !== "carouselproducts" &&
      Boolean(featured?.items?.length);
  const collectionStaticItems = (() => {
    if (!isGroupStrip) {
      return preferStaticCollection ? featured?.items ?? [] : [];
    }
    if (boundGroup) return boundGroup.items ?? [];
    if (pendingAddedStrip?.length) return pendingAddedStrip.slice(0, count);
    return [];
  })();

  // Explicit pick, else Auto fallback to first Latest/Series tab when no collection.
  const feedCarousel = React.useMemo(() => {
    if (boundCarousel) return boundCarousel;
    if (preferStaticCollection) return null;
    if (picked?.carousel) return picked.carousel;
    if (wantShopPastWeek) {
      return (
        findShopThisPastWeekCarousel(CREATOR.carousels) ||
        SHOP_THIS_PAST_WEEK_CAROUSEL
      );
    }
    if (carouselSlug !== "auto") {
      return findCarouselBySlug(CREATOR.carousels, carouselSlug);
    }
    const wantThisWeek =
      preferFeed === "this-week" || sectionIdHint === "carouselshop";
    const wantProducts =
      preferFeed === "recent-products" ||
      sectionIdHint === "carouselproducts";
    if (wantThisWeek) {
      // Don't silently bind the first Latest tab under a "This Week" heading —
      // that makes preview tiles disagree with the labeled feed.
      return findThisWeekContentCarousel(CREATOR.carousels);
    }
    if (wantProducts) {
      return findRecentProductsCarousel(CREATOR.carousels);
    }
    if (source === "series") {
      return CREATOR.carousels.filter(isCarouselSeriesTab)[0] || null;
    }
    if (source === "latest") {
      return CREATOR.carousels.filter(isCarouselLatestTab)[0] || null;
    }
    // Auto, no collection: first Latest then Series (same order as Recent Content).
    const latest = CREATOR.carousels.filter(isCarouselLatestTab);
    const series = CREATOR.carousels.filter(isCarouselSeriesTab);
    return latest[0] || series[0] || null;
  }, [
    preferStaticCollection,
    picked,
    carouselSlug,
    source,
    preferFeed,
    wantShopPastWeek,
    sectionIdHint,
    boundCarousel,
    CREATOR.carousels,
  ]);

  const mediaShapeRaw = String(
    feedCarousel?.contentShape || "rect",
  ).toLowerCase();
  const isSquareMedia =
    mediaShapeRaw === "square" || mediaShapeRaw === "sq";

  const staticFallbackItems = React.useMemo(() => {
    // Feed-bound strips must wait for distinct live posts. Do not invent N cards
    // from a single `card_image` — that flashes identical tiles before the feed
    // returns (e.g. five copies of "Trending This Month").
    return [] as {
      id: string;
      title: string;
      image: string;
      slug: string;
      kind: "shop" | "content";
    }[];
  }, []);

  const coverPreviewUrl = React.useMemo(() => {
    const c = feedCarousel;
    if (!c || preferStaticCollection) return "";
    return (
      String(c.coverImage || "").trim() ||
      c.items?.find((it) => String(it.image || "").trim())?.image ||
      ""
    );
  }, [feedCarousel, preferStaticCollection]);

  // Warm the cover in cache so the first real tile can paint faster once posts arrive.
  React.useEffect(() => {
    if (!coverPreviewUrl || typeof window === "undefined") return;
    const img = new window.Image();
    img.decoding = "async";
    img.src = coverPreviewUrl;
  }, [coverPreviewUrl]);

  // Banner layout: latest-podcast type, or section cfg already set to a Banner
  // style (peek/marquee are the only carousel styles). Style alone is enough —
  // Add can land fullbleed/count before CREATOR type catches up.
  const isPodcastBannerFeed =
    String(feedCarousel?.componentType || "")
      .trim()
      .toLowerCase() === "latest-podcast" ||
    String(feedCarousel?.componentDisplayName || "")
      .trim()
      .toLowerCase() === "latest-podcast" ||
    PODCAST_BANNER_STYLES.has(String(cfg?.style || ""));

  const isTrendingFeed = featuredCarouselIsTrendingFeed(feedCarousel);
  const isHandPickFeed = Boolean(
    feedCarousel && isHandPickCarousel(feedCarousel),
  );
  const feedMarkedEmpty =
    feedCarousel?.isEmpty === true ||
    (feedCarousel as { isEmpty?: string } | null | undefined)?.isEmpty ===
      "true";
  // Trending membership changes with the click window. A previous empty mark
  // must not skip the public fetch — the editor already shows those posts.
  const hideEmptyPublicStrip =
    !editable &&
    feedMarkedEmpty &&
    !isTrendingFeed &&
    !isGroupStrip &&
    !preferStaticCollection;

  const {
    items: stripItems,
    isLoading: stripLoading,
    isFetching: stripFetching,
  } = useFeaturedCarouselStripItems({
      // Start the strip fetch as soon as the bound carousel exists — don't wait
      // for the creator-data skeleton to clear (that was serializing first paint).
      // Skip while this page layer is CSS-hidden so /shop and /content aren't
      // competing with home trending RPCs on iPhone.
      // Top→bottom cascade + near-viewport: lower / below-fold carousels wait.
      enabled: Boolean(
        isActive &&
          !hideEmptyPublicStrip &&
          cascadeAllowsFetch &&
          nearViewport &&
          userId &&
          username &&
          (feedCarousel || preferStaticCollection),
      ),
      userId,
      username,
      carousel: feedCarousel,
      limit: isPodcastBannerFeed
        ? 1
        : // Honor section Item count (max 25). Capping at ~6 made ASC vs DESC
          // return different post windows and ignored the configured count.
          count,
      staticItems: collectionStaticItems,
      preferStatic: preferStaticCollection,
      // Match Add ghost: show skeleton while trending / custom-pick Order refetches.
      keepPreviousData: !(editable && (isTrendingFeed || isHandPickFeed)),
      // Public pages read the post list saved from the editor. Recomputing
      // here used to miss after the row was marked empty.
      bypassTrendingSnapshot: Boolean(editable && isTrendingFeed),
    });

  // Unlock the next home carousel once this strip has settled (or is static/empty).
  // Do not settle while waiting on viewport — that would unlock lower strips early.
  React.useEffect(() => {
    if (!cascade || !cascadeSectionId || !isActive) return;
    if (!cascadeAllowsFetch || !nearViewport) return;
    if (hideEmptyPublicStrip || preferStaticCollection) {
      cascade.reportSettled(cascadeSectionId);
      return;
    }
    if (!feedCarousel && !preferStaticCollection) {
      // Nothing to fetch — don't block lower strips.
      cascade.reportSettled(cascadeSectionId);
      return;
    }
    if (!stripLoading) {
      cascade.reportSettled(cascadeSectionId);
    }
  }, [
    cascade,
    cascadeSectionId,
    cascadeAllowsFetch,
    nearViewport,
    isActive,
    hideEmptyPublicStrip,
    preferStaticCollection,
    feedCarousel,
    stripLoading,
  ]);

  // Custom Content Update: clear cache → show Loading + skeleton until ordered
  // posts land (ASC/DESC), not the previous pick-order tiles.
  const showHandPickStripReload =
    editable &&
    isHandPickFeed &&
    !preferStaticCollection &&
    (stripLoading || stripFetching) &&
    !isPodcastBannerFeed;

  const collectionCoverItems = React.useMemo(
    () =>
      preferStaticCollection
        ? collectionStaticItems.map((it) => ({
            title: it.title,
            body: "",
            image: it.image,
            stayComponentRowId: it.stayComponentRowId,
          }))
        : [],
    [preferStaticCollection, collectionStaticItems],
  );
  const { items: syncedCollectionCovers } = useAboutMoreFromCardImages(
    collectionCoverItems,
    userId,
    username,
    preferStaticCollection && Boolean(userId && username),
  );
  const stripItemsWithCovers = React.useMemo(() => {
    if (!preferStaticCollection) {
      if (stripItems.length > 0) return stripItems;
      // Add already showed these posts. Keep them until the live strip arrives.
      if (pendingAddedStrip?.length) return pendingAddedStrip.slice(0, count);
      return stripItems;
    }
    const byRow = new Map(
      syncedCollectionCovers.map((c) => [
        String(c.stayComponentRowId ?? "").trim(),
        String(c.image ?? "").trim(),
      ]),
    );
    return stripItems.map((it) => {
      const rowId = String(it.stayComponentRowId ?? "").trim();
      const synced = rowId ? byRow.get(rowId) : "";
      if (!synced) return it;
      return { ...it, image: synced };
    });
  }, [preferStaticCollection, stripItems, syncedCollectionCovers, pendingAddedStrip, count]);

  // Bound slug set (from Add) but live carousel row not in CREATOR yet —
  // keep skeleton instead of flashing “No posts…”.
  const waitingForBoundFeed =
    editable &&
    Boolean(userId) &&
    carouselSlug !== "auto" &&
    !preferStaticCollection &&
    !feedCarousel &&
    !picked;

  const hasPaintableStrip =
    (isGroupStrip && collectionStaticItems.length > 0) ||
    stripItemsWithCovers.some(
      (it) =>
        Boolean(String(it.image || "").trim()) ||
        Boolean(it.isText) ||
        Boolean(String(it.mediaUrl || "").trim()),
    );
  // Below-fold / cascade-gated strips: query is disabled, so isLoading is false
  // and an empty strip would flash “No data” after Publish. Keep the skeleton.
  const waitingForStripFetchGate =
    editable &&
    Boolean(feedCarousel) &&
    !preferStaticCollection &&
    !hasPaintableStrip &&
    !pendingAddedStrip?.length &&
    (!cascadeAllowsFetch || !nearViewport);

  const clearedEmptyRowRef = React.useRef("");
  React.useEffect(() => {
    if (editable || preferStaticCollection || isGroupStrip) return;
    if (!feedCarousel) return;
    if (stripLoading || stripFetching) return;
    const rowId = String(feedCarousel.stayComponentRowId || "").trim();
    const title = String(feedCarousel.title || "").trim();
    if (!rowId || !title || !userId) return;
    // Trending can fill in after an older empty mark. Don't stick that flag,
    // and clear it once the public strip has posts.
    if (isTrendingFeed) {
      if (stripItems.length > 0 && feedMarkedEmpty && clearedEmptyRowRef.current !== rowId) {
        clearedEmptyRowRef.current = rowId;
        void clearIsEmptyStatus(title, userId, rowId, "website-template");
      }
      return;
    }
    if (feedMarkedEmpty || stripItems.length > 0) return;
    void updateIsEmptyStatus(title, userId, rowId, "website-template");
  }, [
    editable,
    preferStaticCollection,
    isGroupStrip,
    feedCarousel,
    feedMarkedEmpty,
    isTrendingFeed,
    stripLoading,
    stripFetching,
    stripItems.length,
    userId,
  ]);

  if (hideEmptyPublicStrip) {
    return null;
  }

  // Podcast Banner must paint the banner layout even while the episode feed is
  // loading / imageless — never fall through to the empty carousel strip.
  if (
    (waitingForBoundFeed && !isPodcastBannerFeed && !pendingAddedStrip?.length) ||
    (waitingForStripFetchGate && !isPodcastBannerFeed) ||
    (showHandPickStripReload && !hasPaintableStrip) ||
    ((showCarouselSkel || stripLoading || (editable && stripFetching)) &&
      !hasPaintableStrip &&
      !isPodcastBannerFeed)
  ) {
    const productSkel = featuredCarouselStripIsProductish(feedCarousel, {
      preferFeed,
      sectionIdHint,
    });
    const sectionTitleWhileLoading = shopPastWeekDisplayHeading(
      cfg?.heading || feedCarousel?.title || featured?.title || "",
      wantShopPastWeek,
    );
    return (
      <Shell T={T} cfg={cfg} heading={sectionTitleWhileLoading}>
        <div role="status" aria-label="Loading">
          <FeaturedCarouselStripSkeleton
            product={editable ? false : productSkel}
          />
        </div>
      </Shell>
    );
  }
  const carouselSectionId = sectionIdFromPath(
    sectionPath,
    idx === 0
      ? "carouselShop"
      : idx === 1
        ? "carouselProducts"
        : `carousel-${idx}`,
  );
  const trackFeaturedCarouselClick = (
    it: {
      id: string;
      slug?: string;
      title: string;
      kind?: string;
      image?: string;
      platform?: string;
    },
  ) => {
    const carouselTitle =
      String(
        shopPastWeekDisplayHeading(
          cfg?.heading ||
            (isGroupStrip ? boundGroup?.title : "") ||
            picked?.tabTitle ||
            feedCarousel?.title ||
            (isGroupStrip ? "" : featured?.title) ||
            "",
          wantShopPastWeek,
        ) || "",
      ).trim() || "Carousel";
    const itemName = String(it.title || "").trim() || carouselTitle;
    analytics?.trackSectionEvent(
      TEMPLATE_ANALYTICS_EVENTS.FEATURED_CAROUSEL_CLICK,
      {
        sectionId: carouselSectionId,
        sectionType: "Carousel",
        entityType: "carousel",
        entityId: it.id,
        entityLabel: carouselTitle,
        details: {
          scope: "item",
          carousel_title: carouselTitle,
          name: itemName,
          platform: carouselClickPlatform(it.platform),
          image: String(it.image || "").trim(),
        },
      },
    );
  };

  const contentView: TemplateContentView =
    picked?.contentView ??
    (isCarouselSeriesTab(feedCarousel || {})
      ? "series"
      : "latest");

  const navSlug =
    String(feedCarousel?.renderSlug || picked?.value || "").trim() ||
    (isGroupStrip
      ? String(boundGroup?.renderSlug || "").trim()
      : preferStaticCollection
        ? String(featured?.renderSlug || "").trim()
        : "");

  const goToBoundFeedTab = () => {
    if (!onNavigate || !navSlug) return;
    const page = feedCarousel?.type === "shop" ? "shop" : "content";
    const label = shopPastWeekDisplayHeading(
      cfg?.heading ||
        (isGroupStrip ? boundGroup?.title : "") ||
        picked?.tabTitle ||
        feedCarousel?.title ||
        (isGroupStrip ? "" : featured?.title) ||
        "",
      wantShopPastWeek,
    );
    analytics?.trackSectionEvent(
      TEMPLATE_ANALYTICS_EVENTS.FEATURED_CAROUSEL_CLICK,
      {
        sectionId: carouselSectionId,
        sectionType: "Carousel",
        entityType: "carousel",
        entityId: navSlug,
        entityLabel: String(label || "").trim() || "View all",
        details: {
          scope: "section",
          carousel_title: String(label || "").trim() || "View all",
        },
      },
    );
    navigateTemplateTabTarget(onNavigate, page, navSlug, contentView);
  };

  const sectionTitle = shopPastWeekDisplayHeading(
    cfg?.heading ||
      (isGroupStrip ? boundGroup?.title : "") ||
      picked?.tabTitle ||
      feedCarousel?.title ||
      (isGroupStrip ? "" : featured?.title) ||
      "",
    wantShopPastWeek,
  );

  const featuredFromSource =
    stripItemsWithCovers.length > 0
      ? {
          mode: (preferStaticCollection
            ? "collection"
            : "single") as "collection" | "single",
          title: sectionTitle,
          items: stripItemsWithCovers,
        }
      : isGroupStrip && collectionStaticItems.length > 0
        ? {
            mode: "collection" as const,
            title: sectionTitle,
            items: collectionStaticItems,
          }
        : null;

  // Real thumbs only — empty/static fallbacks used to paint a dummy card that
  // disagreed with the Add-component empty preview. Match that dashed empty UI.
  const hasRealStripMedia = stripItemsWithCovers.some(
    (it) =>
      Boolean(String(it.image || "").trim()) ||
      Boolean(it.isText) ||
      Boolean(String(it.mediaUrl || "").trim()),
  );

  if (isPodcastBannerFeed) {
    const episode = featuredFromSource?.items?.[0];
    const episodeUrl = String(
      episode?.permalink || episode?.shopLink || "",
    ).trim();
    const goEpisode = episode
      ? () => {
          trackFeaturedCarouselClick(episode);
        }
      : undefined;
    return (
      <PodcastEpisodeBanner
        T={T}
        cfg={cfg}
        title={
          sectionTitle ||
          episode?.title ||
          "Latest Podcast Episode"
        }
        subtitle={episode?.title}
        image={String(episode?.image || "")}
        cta={String(cfg?.cta || "Listen Now")}
        showCta={cfg?.showCta !== false}
        href={episodeUrl || undefined}
        onActivate={episodeUrl ? goEpisode : undefined}
      />
    );
  }

  if (featuredFromSource && featuredFromSource.items.length > 0 && (hasRealStripMedia || isGroupStrip)) {
    const featuredItems = featuredFromSource.items.slice(0, count);
    const stripMode =
      featuredItems.length <= 1 && preferStaticCollection
        ? "single"
        : "collection";
    // Only carousel-collection *member* tiles deep-link to a tab. Live post
    // feeds open shopLink / permalink (or stay put) — never bounce to Content.
    const allowTabDeepLink = preferStaticCollection && !editable;

    // Media Sound On → mute icon (same as legacy LiB); Off keeps hover muted.
    const carouselSoundOn = normalizeSoundValue(
      feedCarousel?.contentSound,
      true,
    );
    // Editor: remount+fade when Style peek/marquee or strip membership changes
    // after a control reload (date range / order). Opacity-only — no translateY.
    const editorStripFadeKey = editable
      ? [
          carouselStyle,
          feedCarousel?.details || "",
          feedCarousel?.contentOrder || "",
          feedCarousel?.shoppableContentFilter || "",
          feedCarousel?.shoppableStatusFilter || "",
          featuredItems.map((it) => it.id).join(","),
        ].join("|")
      : "";
    const editorStripFadeClass = editable
      ? " mte-tpl-editor-strip-fade"
      : "";

    const renderCard = (
      it: (typeof featuredItems)[number],
      key: string,
      index = 0,
      opts?: { marquee?: boolean; stillOnly?: boolean; ariaHidden?: boolean },
    ) => {
      const cardItem = isGroupStrip
        ? { ...it, kind: "content" as const, postType: undefined }
        : it;
      const interactive = featuredCarouselItemIsInteractive(it, {
        onNavigate,
        allowTabDeepLink,
      });
      const hasOutboundUrl = Boolean(
        String(it.shopLink || it.permalink || "").trim(),
      );
      const goCard = () => {
        trackFeaturedCarouselClick(it);
        // Outbound cards render as <a href> — skip window.open so mobile
        // does not double-navigate / get popup-blocked after the real link.
        if (hasOutboundUrl) return;
        activateFeaturedCarouselItem(it, {
          onNavigate,
          contentView,
          allowTabDeepLink,
        });
      };
      return (
        <FeaturedCarouselCard
          key={key}
          item={cardItem}
          index={index}
          marquee={opts?.marquee}
          ariaHidden={opts?.ariaHidden}
          stillOnly={opts?.stillOnly}
          isSquareMedia={isGroupStrip || isSquareMedia}
          isMobile={isMobile}
          interactive={interactive}
          onActivate={goCard}
          soundOn={carouselSoundOn}
        />
      );
    };

    if (carouselStyle === "marquee") {
      // <5 items: pin a static strip. Ticker leaves a large empty gap; loop
      // duplicates the same post/product on screen.
      if (shouldMarqueeTicker(featuredItems.length)) {
        return (
          <Shell
            T={T}
            cfg={cfg}
            arrive
            heading={sectionTitle}
            onHeadingClick={
              editable || isGroupStrip
                ? undefined
                : onNavigate && navSlug
                  ? goToBoundFeedTab
                  : undefined
            }
          >
            <div
              key={editorStripFadeKey || undefined}
              className={`mte-tpl-fc-strip${editorStripFadeClass}`}
              data-fc-mode={stripMode}
            >
              {featuredItems.map((it, i) => renderCard(it, it.id, i))}
            </div>
          </Shell>
        );
      }
      const loop = [...featuredItems, ...featuredItems];
      return (
        <Shell
          T={T}
          cfg={cfg}
          arrive
          heading={sectionTitle}
          onHeadingClick={
            editable || isGroupStrip
              ? undefined
              : onNavigate && navSlug
                ? goToBoundFeedTab
                : undefined
          }
        >
          <div
            key={editorStripFadeKey || undefined}
            className={editorStripFadeClass.trim() || undefined}
          >
            <TemplateMarqueeTrack mode="loop">
              {loop.map((it, i) =>
                renderCard(it, `${it.id}-${i}`, i % featuredItems.length, {
                  marquee: true,
                  stillOnly: true,
                  ariaHidden: i >= featuredItems.length,
                }),
              )}
            </TemplateMarqueeTrack>
          </div>
        </Shell>
      );
    }

    return (
      <Shell
        T={T}
        cfg={cfg}
        arrive
        heading={sectionTitle}
        onHeadingClick={
          editable || isGroupStrip
            ? undefined
            : onNavigate && navSlug
              ? goToBoundFeedTab
              : undefined
        }
      >
        <div
          key={editorStripFadeKey || undefined}
          className={`mte-tpl-fc-strip${editorStripFadeClass}`}
          data-fc-mode={stripMode}
        >
          {featuredItems.map((it, i) => renderCard(it, it.id, i))}
        </div>
      </Shell>
    );
  }

  // Legacy platform: picks / empty — ignore platform highlights for new UX.
  if (platformPickKey && !picked) {
    if (!editable) return null;
  }

  if (userId) {
    // Public pages hide an empty strip. The editor keeps every carousel,
    // including Shop This Past Week, and shows the shared empty state.
    if (!editable && !isGroupStrip) return null;
    if (
      !editable &&
      isGroupStrip &&
      collectionStaticItems.length === 0 &&
      !liveCreatorLoading &&
      !showCarouselSkel
    ) {
      return null;
    }
    if (
      isGroupStrip &&
      collectionStaticItems.length === 0 &&
      (liveCreatorLoading || showCarouselSkel)
    ) {
      const skeletonCount = 5;
      return (
        <Shell T={T} cfg={cfg}>
          <div className="mte-tpl-fc-strip" data-fc-mode="collection" aria-hidden="true">
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <div
                key={i}
                className="mte-tpl-fc-card mte-tpl-fc-card--static mte-tpl-fc-card--skel"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="mte-tpl-skel-fill mte-tpl-skel-fill--cover mte-tpl-fc-media-skel" />
              </div>
            ))}
          </div>
        </Shell>
      );
    }
    const boundFeed = picked?.carousel || feedCarousel;
    const emptyHint = isGroupStrip
      ? collectionStaticItems.length
        ? "Loading collection preview…"
        : "Select components in the panel to preview this collection."
      : boundFeed
      ? "No data"
      : listRecentContentCarousels(CREATOR.carousels).length > 0
        ? "Pick a Recent Content tab in section tweaks, or add a carousel-collection."
        : "Add Latest/Series carousels in Recent Content to feature them here.";
    return (
      <Shell
        T={T}
        cfg={cfg}
        arrive
        heading={sectionTitle}
      >
        {boundFeed ? (
          <div className="mte-tpl-carousel-empty" role="status">
            {emptyHint}
          </div>
        ) : (
          <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
            {emptyHint}
          </p>
        )}
      </Shell>
    );
  }

  const c = CREATOR.carousels[idx] || CREATOR.carousels[0];
  if (!c) return null;

  const items = c.items.slice(0, count);
  if (!items.length) {
    if (!editable) return null;
    return (
      <Shell T={T} cfg={cfg} heading={cfg?.heading || c.title}>
        <div className="mte-tpl-carousel-empty" role="status">
          No data available
        </div>
      </Shell>
    );
  }
  // Legacy path still renders sticky-first / fullbleed from saved configs & onboarding.
  const style = String(cfg?.style || "peek");

  const trackLegacyCarouselClick = (it: { id?: string; url?: string; title?: string }, position?: number) => {
    analytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.CAROUSEL_ITEM_CLICK, {
      sectionId: carouselSectionId,
      sectionType: "Carousel",
      entityType: "carousel",
      entityId: String(it.id ?? ""),
      entityLabel: it.title || "",
      details: { url: it.url, carousel_title: c.title, position },
    });
  };

  if (style === "marquee") {
    if (shouldMarqueeTicker(items.length)) {
      return (
        <Shell T={T} cfg={cfg} heading={cfg?.heading || c.title}>
          <div className="mte-tpl-carl-strip mte-tpl-carl-strip--peek">
            {items.map((it, pi) => (
              <a
                key={it.id}
                href={it.url}
                className="mte-tpl-carl-card mte-tpl-carl-card--peek"
                onClick={() => trackLegacyCarouselClick(it, pi)}
              >
                <div className="mte-tpl-carl-aspect mte-tpl-carl-aspect--mb14">
                  <img
                    src={it.image}
                    alt=""
                    className="mte-tpl-img-filter mte-tpl-carl-aspect-img mte-tpl-carl-img-peek"
                  />
                </div>
                <div className="mte-tpl-carl-cap-sub mte-tpl-carl-cap-sub--mb8">
                  {it.subtitle}
                </div>
                <div className="mte-tpl-carl-cap-row">
                  <div className="mte-tpl-carl-title-fluid">{it.title}</div>
                  {it.price && (
                    <div className="mte-tpl-carl-price-fluid">{it.price}</div>
                  )}
                </div>
              </a>
            ))}
          </div>
        </Shell>
      );
    }
    const loop = [...items, ...items];
    return (
      <Shell T={T} cfg={cfg} heading={cfg?.heading || c.title}>
        <TemplateMarqueeTrack mode="loop">
            {loop.map((it, i) => (
              <a key={i} href={it.url} className="mte-tpl-carl-marquee-card" onClick={() => trackLegacyCarouselClick(it, i % items.length)} aria-hidden={i >= items.length ? true : undefined}>
                <div className="mte-tpl-carl-marquee-frame">
                  <img
                    src={it.image}
                    alt=""
                    className="mte-tpl-img-filter mte-tpl-carl-marquee-img"
                  />
                </div>
                <div className="mte-tpl-carl-cap-sub">{it.subtitle}</div>
                <div className="mte-tpl-carl-cap-row">
                  <div className="mte-tpl-carl-cap-title">{it.title}</div>
                  {it.price && (
                    <div className="mte-tpl-carl-cap-price">{it.price}</div>
                  )}
                </div>
              </a>
            ))}
        </TemplateMarqueeTrack>
      </Shell>
    );
  }
  if (style === "sticky-first") {
    const [first, ...rest] = items;
    return (
      <Shell
        T={T}
        cfg={{ ...cfg, ctaLink: "View all →" }}
        heading={cfg?.heading || c.title}
      >
        <div className="mte-tpl-carl-sticky-grid">
          <a href={first.url} className="mte-tpl-carl-sticky-first" onClick={() => trackLegacyCarouselClick(first, 0)}>
            <div className="mte-tpl-carl-aspect mte-tpl-carl-aspect--mb14">
              <img
                src={first.image}
                alt=""
                className="mte-tpl-img-filter mte-tpl-carl-aspect-img"
              />
            </div>
            <div className="mte-tpl-carl-feat-label">
              FEATURED · {first.subtitle}
            </div>
            <div className="mte-tpl-carl-feat-title">{first.title}</div>
            {first.price && (
              <div className="mte-tpl-carl-feat-price">{first.price}</div>
            )}
          </a>
          <div className="mte-tpl-carl-scroll">
            {rest.map((it, ri) => (
              <a key={it.id} href={it.url} className="mte-tpl-carl-scroll-card" onClick={() => trackLegacyCarouselClick(it, ri + 1)}>
                <div className="mte-tpl-carl-aspect mte-tpl-carl-aspect--mb12">
                  <img
                    src={it.image}
                    alt=""
                    className="mte-tpl-img-filter mte-tpl-carl-aspect-img"
                  />
                </div>
                <div className="mte-tpl-carl-cap-sub">{it.subtitle}</div>
                <div className="mte-tpl-carl-cap-row">
                  <div className="mte-tpl-carl-cap-title mte-tpl-carl-cap-title--sm">
                    {it.title}
                  </div>
                  {it.price && (
                    <div className="mte-tpl-carl-cap-price mte-tpl-carl-cap-price--sm">
                      {it.price}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      </Shell>
    );
  }
  const cardMod =
    style === "fullbleed"
      ? "mte-tpl-carl-card--full"
      : style === "peek"
        ? "mte-tpl-carl-card--peek"
        : "";
  return (
    <Shell
      T={T}
      cfg={{ ...cfg, ctaLink: "View all →" }}
      heading={cfg?.heading || c.title}
    >
      <div
        className={`mte-tpl-carl-strip${style === "peek" ? " mte-tpl-carl-strip--peek" : ""}`}
      >
        {items.map((it, pi) => (
          <a
            key={it.id}
            href={it.url}
            className={`mte-tpl-carl-card ${cardMod}`}
            onClick={() => trackLegacyCarouselClick(it, pi)}
          >
            <div className="mte-tpl-carl-aspect mte-tpl-carl-aspect--mb14">
              <img
                src={it.image}
                alt=""
                className="mte-tpl-img-filter mte-tpl-carl-aspect-img mte-tpl-carl-img-peek"
              />
            </div>
            <div className="mte-tpl-carl-cap-sub mte-tpl-carl-cap-sub--mb8">
              {it.subtitle}
            </div>
            <div className="mte-tpl-carl-cap-row">
              <div className="mte-tpl-carl-title-fluid">{it.title}</div>
              {it.price && (
                <div className="mte-tpl-carl-price-fluid">{it.price}</div>
              )}
            </div>
          </a>
        ))}
      </div>
    </Shell>
  );
}
CarouselSection.options = {
  // Bound via Add / feed controls — hide from inspector (Title lives in feed settings).
  carouselSlug: {
    kind: "hidden",
    label: "Carousel",
    dynamicOptions: "recentContentCarousels",
    default: "auto",
  },
  // Legacy pool mode — kept for existing tweaks; prefer carouselSlug picker.
  source: {
    kind: "hidden",
    label: "Carousel source",
    default: "auto",
  },
  style: {
    kind: "radio",
    label: "Style",
    options: ["peek", "marquee"],
    default: "peek",
  },
  count: { kind: "number", label: "Item count", min: 3, max: 25, default: 25 },
  // Section title is the feed Title field (add + edit) — not a second override.
  heading: { kind: "hidden", label: "Heading override", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── More From (Additional Carousels) ──────────────────────────
// Mirrors link-in-bio AdditionalCarousels: top carousels by last-7-day
// click analytics (active + archived). Sorted by popularity descending.
export function MoreFromSection({ T, cfg, onNavigate }: SectionProps) {
  const CREATOR = useCreatorData();
  const { editable } = useEdit();
  const analytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();
  const sectionId = sectionIdFromPath(sectionPath, "moreFrom");
  const maxCount = (cfg?.count as number) || 6;
  // Skip idx 0 — that is the top carousel, already shown above.
  const carousels = CREATOR.carousels.slice(1, 1 + maxCount);
  if (!carousels.length) {
    if (!editable) return null;
    return (
      <Shell
        T={T}
        cfg={cfg}
        heading={cfg?.heading || ""}
        independentShellClassName="mte-tpl-more-shell"
      >
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Add more carousels in Collections to preview this strip.
        </p>
      </Shell>
    );
  }

  return (
    <Shell
      T={T}
      cfg={cfg}
      heading={cfg?.heading || ""}
      independentShellClassName="mte-tpl-more-shell"
    >
      <div className="mte-tpl-more-strip mte-tpl-grid-enter">
        {carousels.map((c) => {
          const cover = c.items[0]?.image || "";
          const goMoreFrom = () => {
            if (!onNavigate) return;
            const slug = (c.renderSlug ?? "").trim() || String(c.id).trim();
            if (!slug) return;
            analytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.MORE_FROM_CLICK, {
              sectionId,
              sectionType: "MoreFrom",
              entityType: c.type as unknown as TemplateEntityType,
              entityId: slug,
              entityLabel: c.title,
              details: { slug, type: c.type },
            });
            const page = c.type === "content" ? "content" : "shop";
            navigateTemplateTabTarget(onNavigate, page, slug);
          };
          return (
            <div
              key={c.id}
              role={onNavigate ? "button" : undefined}
              tabIndex={onNavigate ? 0 : undefined}
              onClick={goMoreFrom}
              onKeyDown={
                onNavigate
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goMoreFrom();
                      }
                    }
                  : undefined
              }
              className={`mte-tpl-more-tile${onNavigate ? "" : " mte-tpl-more-tile--static"}`}
            >
              {cover && (
                <img
                  src={cover}
                  alt=""
                  className="mte-tpl-img-filter mte-tpl-more-img"
                />
              )}
              <div className="mte-tpl-more-grad" />
              <div className="mte-tpl-more-title">{c.title}</div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
MoreFromSection.options = {
  count: {
    kind: "number",
    label: "Max carousels shown",
    min: 2,
    max: 10,
    default: 6,
  },
  heading: { kind: "text", label: "Heading override", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Featured Highlight ─────────────────────────────────────────
export function FeaturedHighlight({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const { editable } = useEdit();
  const h = CREATOR.highlights?.[(cfg?.index as number) || 0];
  const banner = CREATOR.banners?.promo;
  if ((!h || !banner?.image) && !editable) return null;
  if (!h || !banner?.image) {
    return (
      <Shell T={T} cfg={cfg} heading={cfg?.heading || ""}>
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Add a promo banner and highlight to preview this block.
        </p>
      </Shell>
    );
  }
  const style = (cfg?.style as string) || "left";
  const showEyebrow = cfg?.showEyebrow === true;
  const showSubtext = cfg?.showSubtext !== false;
  const showCaption = (cfg?.showCaption as boolean) === true;
  const eyebrow = (cfg?.eyebrow as string) || h.label;
  const subtext = (cfg?.subtext as string) || banner.subtitle;
  const caption = (cfg?.caption as string) || "Updated April 2026";
  const heading = cfg?.heading || banner.title;
  const ctaLabel = (cfg?.cta as string) || "Open highlight";
  const img = (
    <img
      src={banner.image}
      alt=""
      className="mte-tpl-img-filter mte-tpl-feat-img"
    />
  );
  const text = (
    <div>
      {showEyebrow && (
        <div className="mte-tpl-feat-text-eyebrow">
          <E path="featured.eyebrow">{eyebrow}</E>
        </div>
      )}
      <h3 className="mte-tpl-feat-text-h3">
        <E path="featured.heading" placeholder="Add title">
          {heading}
        </E>
      </h3>
      {showSubtext && (
        <p className="mte-tpl-feat-text-sub">
          <E path="featured.subtext" multiline>
            {subtext}
          </E>
        </p>
      )}
      <EL path="featured.cta" href="#" className={btnClass("primary")}>
        {ctaLabel}
      </EL>
      {showCaption && (
        <div className="mte-tpl-feat-text-cap">
          <E path="featured.caption">{caption}</E>
        </div>
      )}
    </div>
  );
  if (style === "overlay")
    return (
      <Shell T={T} cfg={cfg} fullBleed>
        <div className="mte-tpl-feat-ov">
          <img
            src={banner.image}
            alt=""
            className="mte-tpl-img-filter mte-tpl-feat-ov-img"
          />
          <div className="mte-tpl-feat-ov-scrim" />
          <div className="mte-tpl-feat-ov-inner">
            {showEyebrow && (
              <div className="mte-tpl-feat-ov-eyebrow">{eyebrow}</div>
            )}
            <h3 className="mte-tpl-feat-ov-h3">
              <E path="featured.heading" placeholder="Add title">
                {heading}
              </E>
            </h3>
            {showSubtext && (
              <p className="mte-tpl-feat-ov-sub">
                <E path="featured.subtext" multiline>
                  {subtext}
                </E>
              </p>
            )}
            <EL
              path="featured.cta"
              href="#"
              className={`${btnClass("onImage")} mte-tpl-btn--align-start`}
            >
              {ctaLabel}
            </EL>
          </div>
        </div>
      </Shell>
    );
  if (style === "stacked")
    return (
      <Shell T={T} cfg={cfg}>
        <div className="mte-tpl-feat-stacked">
          {showEyebrow && (
            <div className="mte-tpl-feat-stacked-eyebrow">{eyebrow}</div>
          )}
          <h3 className="mte-tpl-feat-stacked-h3">
            <E path="featured.heading" placeholder="Add title">
              {heading}
            </E>
          </h3>
          {showSubtext && (
            <p className="mte-tpl-feat-stacked-sub">
              <E path="featured.subtext" multiline>
                {subtext}
              </E>
            </p>
          )}
          <div className="mte-tpl-feat-stacked-frame">{img}</div>
          <EL path="featured.cta" href="#" className={btnClass("primary")}>
            {ctaLabel}
          </EL>
          {showCaption && (
            <div className="mte-tpl-feat-stacked-cap">{caption}</div>
          )}
        </div>
      </Shell>
    );
  if (style === "card")
    return (
      <Shell T={T} cfg={cfg}>
        <div className="mte-tpl-feat-card">
          <div className="mte-tpl-feat-card-img">{img}</div>
          <div className="mte-tpl-feat-card-body">{text}</div>
        </div>
      </Shell>
    );
  return (
    <Shell T={T} cfg={cfg}>
      <div className="mte-tpl-feat-split">
        {style === "left" ? (
          <>
            {img}
            {text}
          </>
        ) : T.device === "mobile" ? (
          <>
            {img}
            {text}
          </>
        ) : (
          <>
            {text}
            {img}
          </>
        )}
      </div>
    </Shell>
  );
}
FeaturedHighlight.options = {
  style: {
    kind: "radio",
    label: "Layout",
    options: ["left", "right", "stacked", "overlay", "card"],
    default: "left",
  },
  index: {
    kind: "number",
    label: "Highlight index",
    min: 0,
    max: 6,
    default: 0,
  },
  heading: { kind: "text", label: "Heading override", default: "" },
  subtext: { kind: "text", label: "Subtext override", default: "" },
  caption: { kind: "text", label: "Caption", default: "Updated April 2026" },
  cta: { kind: "text", label: "CTA label", default: "Open highlight" },
  showEyebrow: { kind: "toggle", label: "Show eyebrow", default: false },
  showSubtext: { kind: "toggle", label: "Show subtext", default: true },
  showCaption: { kind: "toggle", label: "Show caption", default: false },
  eyebrow: { kind: "text", label: "Eyebrow text", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Press strip ────────────────────────────────────────────────
export function PressSection({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const items = CREATOR.press.slice(0, (cfg?.count as number) || 5);
  const style = (cfg?.style as string) || "list";
  if (style === "logos")
    return (
      <Shell T={T} cfg={cfg} heading={cfg?.heading || "As seen in"}>
        <div className="mte-tpl-press-logos mte-tpl-grid-enter">
          {items.map((p) => (
            <div key={p.id} className="mte-tpl-press-logo">
              <E path={`press.${p.id}.outlet`}>{p.outlet}</E>
            </div>
          ))}
        </div>
      </Shell>
    );
  const pressEdge = {
    ["--tpl-press-edge" as string]: `${T.ink}22`,
  } as React.CSSProperties;
  return (
    <Shell T={T} cfg={cfg} heading={cfg?.heading || "As seen in"}>
      <div className="mte-tpl-press-list mte-tpl-grid-enter">
        {items.map((p) => (
          <a
            key={p.id}
            href={p.url}
            className="mte-tpl-press-row"
            onClick={() => {
              templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.PRESS_CLICK, {
                sectionId: "press",
                sectionType: "PressSection",
                entityType: "press",
                entityId: String(p.id),
                entityLabel: p.outlet || p.headline || "",
                details: { url: p.url, headline: p.headline, outlet: p.outlet },
              });
            }}
          >
            <div className="mte-tpl-press-outlet">
              <E path={`press.${p.id}.outlet`}>{p.outlet}</E>
            </div>
            <div className="mte-tpl-press-headline">
              <E path={`press.${p.id}.headline`} multiline>
                {p.headline}
              </E>
            </div>
            {T.device !== "mobile" && (
              <div className="mte-tpl-press-date">{p.date} →</div>
            )}
          </a>
        ))}
      </div>
    </Shell>
  );
}
PressSection.options = {
  style: {
    kind: "radio",
    label: "Style",
    options: ["list", "logos"],
    default: "list",
  },
  count: { kind: "number", label: "Item count", min: 3, max: 8, default: 5 },
  heading: { kind: "text", label: "Heading", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Newsletter ─────────────────────────────────────────────────
const NL_PRIVACY = "We won't spam you. Unsubscribe anytime.";

function NewsletterSignupFrame({
  T,
  cfg,
  children,
  busy,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
  children: React.ReactNode;
  busy?: boolean;
}) {
  const stacked = newsletterSignupLayout(cfg) === "stacked";
  const headline =
    String(cfg?.heading ?? "").trim() || "Get the weekly edit";
  const sub = String(cfg?.sub ?? "").trim();
  const privacy = cfg?.privacy !== false;
  return (
    <Shell
      T={T}
      independentShellClassName="mte-tpl-newsletter-shell"
      cfg={{
        ...cfg,
        heading: undefined,
        bgTone: (cfg?.bgTone as "bg" | "surface" | "accent") || "bg",
      }}
    >
      <div
        className={`mte-tpl-nl-signup${stacked ? " is-stacked" : ""}`}
        aria-busy={busy || undefined}
        aria-hidden={busy || undefined}
      >
        <h3 className="mte-tpl-nl-signup-h">{headline}</h3>
        {sub ? <p className="mte-tpl-nl-signup-sub">{sub}</p> : null}
        {children}
        {privacy ? <p className="mte-tpl-nl-privacy">{NL_PRIVACY}</p> : null}
      </div>
    </Shell>
  );
}

export function NewsletterSection({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const { editable } = useEdit();
  const enabled = CREATOR.emailCampaignsEnabled === true;
  const boundId = String(cfg?.componentId || "").trim();
  const block = enabled
    ? (CREATOR.campaignBlocks ?? []).find(
        (row) => String(row.id || "").trim() === boundId,
      )
    : undefined;
  const userId = (CREATOR.bio.userId ?? "").trim();
  const creatorLoading = useCreatorDataLoading();

  if (!enabled || !boundId) return null;

  if (!block && creatorLoading) {
    return (
      <NewsletterSignupFrame T={T} cfg={cfg} busy>
        <div className="mte-tpl-nl-signup-form">
          <div className="mte-tpl-skel-line mte-tpl-skel-line--field" />
          <div className="mte-tpl-skel-line mte-tpl-skel-line--btn" />
        </div>
      </NewsletterSignupFrame>
    );
  }

  if (!userId || !block) return null;

  let rawDetails: Record<string, unknown> = {};
  try {
    const parsed =
      typeof block.details === "string" ? JSON.parse(block.details) : block.details;
    if (parsed && typeof parsed === "object") rawDetails = parsed;
  } catch {
    rawDetails = {};
  }
  const layout = newsletterSignupLayout(cfg);
  const campaignNameOverride = String(cfg?.campaignName ?? "").trim();
  const campaignName =
    campaignNameOverride ||
    String(rawDetails.campaign_name || block.name || "").trim();
  const buttonText =
    cfg?.campaignButton != null
      ? String(cfg.campaignButton || "Subscribe")
      : String(rawDetails.campaign_button_text || "Subscribe");
  const image =
    cfg?.campaignImage != null
      ? String(cfg.campaignImage || "")
      : String(rawDetails.image || "");
  const liveFrame =
    cfg?.imageZoom != null || cfg?.imageX != null || cfg?.imageY != null
      ? {
          imageZoom: cfg.imageZoom,
          imageX: cfg.imageX,
          imageY: cfg.imageY,
        }
      : {
          imageZoom: rawDetails.imageZoom,
          imageX: rawDetails.imageX,
          imageY: rawDetails.imageY,
        };
  const fields = Array.isArray(cfg?.campaignFields)
    ? cfg.campaignFields
    : rawDetails.fields;

  return (
    <Shell
      T={T}
      independentShellClassName="mte-tpl-newsletter-shell"
      cfg={{
        ...cfg,
        heading: undefined,
        bgTone: (cfg?.bgTone as "bg" | "surface" | "accent") || "bg",
      }}
    >
      <div
        className={`mte-tpl-nl-campaign-embed${layout === "stacked" ? " is-stacked" : " is-inline"}`}
      >
        <CampaignWrapper
          layout={layout}
          data={{
            ...block,
            user_id: block.user_id || userId,
            details: {
              ...rawDetails,
              campaign_name: campaignName,
              campaign_button_text: buttonText,
              image,
              imageZoom: liveFrame.imageZoom,
              imageX: liveFrame.imageX,
              imageY: liveFrame.imageY,
              fields,
            },
          }}
          user_id={block.user_id || userId}
          isDashboard={editable}
        />
      </div>
    </Shell>
  );
}
NewsletterSection.options = {
  style: {
    kind: "radio",
    label: "Layout",
    options: ["inline", "stacked"],
    default: "inline",
  },
  heading: {
    kind: "text",
    label: "Headline",
    default: "Get the weekly edit",
  },
  sub: { kind: "text", label: "Subtext", default: "" },
  cta: { kind: "text", label: "Button label", default: "Subscribe" },
  privacy: {
    kind: "toggle",
    label: "Show privacy note",
    default: true,
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

// ── Product grid (`{username}` PRODUCT rows + content_category) ──
/**
 * Lock the featured/looks rise only after the band has been on screen and
 * then hidden. A timer was settling before outfit and look images painted,
 * so every frame except the first one skipped the entrance.
 */
const featuredRiseLocked = new Set<string>();

function useFeaturedRiseLock(id: string, active: boolean, hidden: boolean) {
  const [locked, setLocked] = React.useState(() => featuredRiseLocked.has(id));
  const seenShownRef = React.useRef(false);

  React.useLayoutEffect(() => {
    if (!active || featuredRiseLocked.has(id)) {
      if (featuredRiseLocked.has(id)) setLocked(true);
      return;
    }
    if (!hidden) {
      seenShownRef.current = true;
      return;
    }
    if (seenShownRef.current) {
      featuredRiseLocked.add(id);
      setLocked(true);
    }
  }, [active, hidden, id]);

  return locked;
}

/** Between category browse and product cards on Products. */
function ShopFeaturedInsertSlot({
  hidden = false,
  active = true,
}: {
  hidden?: boolean;
  active?: boolean;
}) {
  const { editable } = useEdit();
  const featured = useShopInlineFeatured();
  const settled = useFeaturedRiseLock("shop-featured", active, hidden);
  return (
    <div
      className={`mte-tpl-shop-featured-slot${settled ? " mte-tpl-rise-settled" : ""}`}
      hidden={hidden}
      aria-hidden={hidden || undefined}
    >
      {featured}
      {editable ? (
        <WebsiteEditorAddComponentBar insertAt={-1} />
      ) : null}
    </div>
  );
}

/**
 * Looks + Featured Categories/Collections + Add under platform browse
 * (above Content / Shoppable). New components append below ones already
 * on the page — same inline placement as Products merch.
 */
function ContentLooksInsertSlot({
  hidden = false,
  active = true,
}: {
  hidden?: boolean;
  active?: boolean;
}) {
  const { editable } = useEdit();
  const featured = useShopInlineFeatured();
  const settled = useFeaturedRiseLock("content-featured", active, hidden);
  return (
    <div
      className={`mte-tpl-shop-featured-slot${settled ? " mte-tpl-rise-settled" : ""}`}
      hidden={hidden}
      aria-hidden={hidden || undefined}
    >
      {featured}
      {editable ? (
        <WebsiteEditorAddComponentBar insertAt={-1} />
      ) : null}
    </div>
  );
}

export function ProductGrid({
  T,
  cfg,
  onNavigate,
  carouselDeepLink,
  onCarouselDeepLinkConsumed,
  shopBrowseSelection,
  shopViewSelection,
  /** False while the products page layer is CSS-hidden (pre-mounted). */
  isActive = true,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const count = (cfg?.count as number) || 24;
  const cols = (cfg?.columns as number) || 5;
  const aspect = (cfg?.aspect as string) || "4/5";
  const showButton = cfg?.showButton !== false;
  const showCategory = cfg?.showCategory !== false;
  const showPrice = cfg?.showPrice !== false;
  const buttonLabel = (cfg?.buttonLabel as string) || "Shop";

  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);
  // Preloaded hidden layers still mount on desktop — don't steal network/CPU.
  const catalogEnabled = hasSession && isActive;
  /**
   * Featured Categories / Collections (or other top merch) present — defer the
   * default product catalog grid until those bands can paint.
   */
  const deferDefaultCatalog = Boolean(cfg?.deferDefaultCatalog);
  const [merchBandsReady, setMerchBandsReady] = React.useState(
    () => !deferDefaultCatalog,
  );
  const [catalogNearViewport, setCatalogNearViewport] = React.useState(false);
  const catalogBlockRef = React.useRef<HTMLDivElement | null>(null);
  const hadMerchBandsRef = React.useRef(deferDefaultCatalog);

  React.useEffect(() => {
    if (!deferDefaultCatalog) {
      setMerchBandsReady(true);
      hadMerchBandsRef.current = false;
      return;
    }
    if (!hadMerchBandsRef.current) {
      setMerchBandsReady(false);
      setCatalogNearViewport(false);
    }
    hadMerchBandsRef.current = true;
  }, [deferDefaultCatalog]);

  const reportMerchBandsReady = React.useCallback(() => {
    setMerchBandsReady(true);
  }, []);

  React.useEffect(() => {
    if (!deferDefaultCatalog || merchBandsReady || catalogNearViewport) {
      return;
    }
    const t = window.setTimeout(() => setMerchBandsReady(true), 4000);
    return () => window.clearTimeout(t);
  }, [deferDefaultCatalog, merchBandsReady, catalogNearViewport]);

  const [shopView, setShopView] = React.useState<TemplateShopView>(
    shopViewSelection === "content" ? "all" : (shopViewSelection ?? "all"),
  );
  const [categorySlug, setCategorySlug] = React.useState<string | null>(null);
  const [shopBrowse, setShopBrowse] = React.useState<ShopBrowseSelection>(
    shopBrowseSelection ?? {},
  );
  /** Product collection deep-link (`carouselSlug`) — feed + breadcrumb, not category browse. */
  const shopDeepLinkSlug =
    carouselDeepLink?.target === "shop" &&
    (carouselDeepLink.shopView ?? "all") === "all" &&
    !carouselDeepLink.productCollectionId?.trim() &&
    !browseSelectionIsActive(carouselDeepLink.shopBrowse || {})
      ? carouselDeepLink.slug?.trim() || null
      : null;
  const [activeCarouselSlug, setActiveCarouselSlug] = React.useState<string | null>(
    shopDeepLinkSlug,
  );
  if (shopDeepLinkSlug && activeCarouselSlug !== shopDeepLinkSlug) {
    setActiveCarouselSlug(shopDeepLinkSlug);
  }
  const [productCollectionId, setProductCollectionId] = React.useState<string | null>(
    null,
  );
  // In category view the browse filter is applied client-side — changing
  // shopBrowse must NOT remount ShopProductCatalogPanel or the product list
  // flashes. Only change the key in "all" view where browse triggers a new
  // server query.
  const shopBrowseKey =
    shopView === "category"
      ? `cat-${categorySlug ?? ""}`
      : shopView === "brand"
        ? `brand-${shopBrowse.brandSlug ?? ""}-${(shopBrowse.brandSlugs || []).join("+")}`
        : [
            shopBrowse.primarySlug ?? "",
            shopBrowse.secondarySlug ?? "",
            shopBrowse.subSlug ?? "",
            shopBrowse.brandSlug ?? "",
          ].join("|");

  // When navigateBrowse updates both local state AND the URL simultaneously
  // (category view), the resulting deep-link roundtrip would re-fire the
  // effect below with a fresh object reference for shopBrowse — triggering
  // extra state updates and re-renders that interrupt the user's click.
  // This flag tells the effect to skip state updates and just consume the
  // deep-link when we know the state is already up-to-date.
  const skipDeepLinkOnSelfNavRef = React.useRef(false);

  const prevShopViewSelectionRef = React.useRef(shopViewSelection);
  React.useEffect(() => {
    if (shopViewSelection == null) return;
    if (prevShopViewSelectionRef.current === shopViewSelection) return;
    prevShopViewSelectionRef.current = shopViewSelection;
    setShopView(shopViewSelection);
  }, [shopViewSelection]);

  React.useEffect(() => {
    if (shopBrowseSelection == null) return;
    setShopBrowse((prev) => {
      const next: ShopBrowseSelection = {
        primarySlug: shopBrowseSelection.primarySlug ?? null,
        secondarySlug: shopBrowseSelection.secondarySlug ?? null,
        subSlug: shopBrowseSelection.subSlug ?? null,
        brandSlug: shopBrowseSelection.brandSlug ?? null,
        brandSlugs: shopBrowseSelection.brandSlugs ?? null,
      };
      const prevSlugs = (prev.brandSlugs || []).join(",");
      const nextSlugs = (next.brandSlugs || []).join(",");
      if (
        prev.primarySlug === next.primarySlug &&
        prev.secondarySlug === next.secondarySlug &&
        prev.subSlug === next.subSlug &&
        prev.brandSlug === next.brandSlug &&
        prevSlugs === nextSlugs
      ) {
        return prev;
      }
      return next;
    });
  }, [
    shopBrowseSelection?.primarySlug,
    shopBrowseSelection?.secondarySlug,
    shopBrowseSelection?.subSlug,
    shopBrowseSelection?.brandSlug,
    shopBrowseSelection?.brandSlugs,
  ]);
  React.useEffect(() => {
    if (carouselDeepLink?.target !== "shop") return;

    // navigateBrowse already applied the state change locally — skip the
    // round-trip state updates to avoid extra re-renders that can interrupt
    // a click in progress. Just consume the deep-link.
    if (skipDeepLinkOnSelfNavRef.current) {
      skipDeepLinkOnSelfNavRef.current = false;
      onCarouselDeepLinkConsumed?.();
      return;
    }

    const view =
      carouselDeepLink.shopView === "content"
        ? "all"
        : (carouselDeepLink.shopView ?? "all");
    const linkSlug = carouselDeepLink.slug?.trim() ?? "";
    const bankCollectionId =
      carouselDeepLink.productCollectionId?.trim() ?? "";
    const browseActive = browseSelectionIsActive(
      carouselDeepLink.shopBrowse || {},
    );

    setShopView(view);
    if (bankCollectionId) {
      setProductCollectionId(bankCollectionId);
      setActiveCarouselSlug(null);
      setCategorySlug(null);
      setShopBrowse({});
    } else if (view === "category" && linkSlug) {
      setProductCollectionId(null);
      setActiveCarouselSlug(null);
      setCategorySlug(linkSlug);
      // Restore browse selection if the URL encoded both category + browse
      // (e.g. /shop/category/search-bubble/apparel/jeans).
      if (carouselDeepLink.shopBrowse) {
        setShopBrowse({
          primarySlug: carouselDeepLink.shopBrowse.primarySlug ?? null,
          secondarySlug: carouselDeepLink.shopBrowse.secondarySlug ?? null,
          subSlug: carouselDeepLink.shopBrowse.subSlug ?? null,
          brandSlug: carouselDeepLink.shopBrowse.brandSlug ?? null,
          brandSlugs: carouselDeepLink.shopBrowse.brandSlugs ?? null,
        });
      } else {
        setShopBrowse({});
      }
    } else if (view === "brand" && carouselDeepLink.shopBrowse) {
      // Featured brand / brand URL — keep brandSlug(s); do not clear below.
      setProductCollectionId(null);
      setActiveCarouselSlug(null);
      setCategorySlug(null);
      setShopBrowse({
        primarySlug: null,
        secondarySlug: null,
        subSlug: null,
        brandSlug: carouselDeepLink.shopBrowse.brandSlug ?? null,
        brandSlugs: carouselDeepLink.shopBrowse.brandSlugs ?? null,
      });
    } else if (view === "all" && linkSlug && !browseActive) {
      // Product collection / carousel tab deep-link (`/shop/{id}`).
      setProductCollectionId(null);
      setActiveCarouselSlug(linkSlug);
      setCategorySlug(null);
      setShopBrowse({});
    } else if (view === "all" && carouselDeepLink.shopBrowse) {
      setProductCollectionId(null);
      setActiveCarouselSlug(null);
      setCategorySlug(null);
      setShopBrowse({
        primarySlug: carouselDeepLink.shopBrowse.primarySlug ?? null,
        secondarySlug: carouselDeepLink.shopBrowse.secondarySlug ?? null,
        subSlug: carouselDeepLink.shopBrowse.subSlug ?? null,
        brandSlug: carouselDeepLink.shopBrowse.brandSlug ?? null,
        brandSlugs: carouselDeepLink.shopBrowse.brandSlugs ?? null,
      });
    } else {
      setProductCollectionId(null);
      setActiveCarouselSlug(null);
      setCategorySlug(null);
      if (view !== "category") {
        setShopBrowse({});
      }
    }
    onCarouselDeepLinkConsumed?.();
  }, [
    carouselDeepLink?.target,
    carouselDeepLink?.shopView,
    carouselDeepLink?.slug,
    carouselDeepLink?.shopBrowse,
    carouselDeepLink?.productCollectionId,
    onCarouselDeepLinkConsumed,
  ]);

  // Root browse (merch visible) — defer catalog behind Featured Categories /
  // Collections. Drill-ins need the product grid immediately.
  const drilledInForDefer =
    Boolean(activeCarouselSlug) ||
    Boolean(productCollectionId) ||
    shopView === "trending" ||
    shopView === "category" ||
    shopView === "brand" ||
    browseSelectionIsActive(shopBrowse);
  const merchOnRootBrowse = deferDefaultCatalog && !drilledInForDefer;
  const deferProductGrid =
    merchOnRootBrowse && !merchBandsReady && !catalogNearViewport;

  React.useEffect(() => {
    if (!merchOnRootBrowse || catalogNearViewport || merchBandsReady) {
      return;
    }
    const onScroll = () => {
      const el = catalogBlockRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top < window.innerHeight * 0.9) {
        setCatalogNearViewport(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [merchOnRootBrowse, catalogNearViewport, merchBandsReady]);

  const {
    products,
    browseIndex,
    categoryFilteredBrowseIndex,
    hasBrowseCatalog,
    isBrowseCatalogSettled,
    browseLabels,
    brandCounts,
    loading,
    primaryCoverBySlug,
    fetchNextProductsPage,
    hasNextProductsPage,
    isFetchingNextProductsPage,
    productsRefreshing,
  } = useShopCatalog({
    username,
    userId,
    enabled: catalogEnabled,
    shopView,
    categorySlug,
    shopBrowse,
    surface: "products",
    // Collection view loads its own feed — don't also pull the browse product pages.
    // Merch-first: also skip until Featured Categories / Collections settle.
    skipGrid:
      Boolean(activeCarouselSlug || productCollectionId) || deferProductGrid,
    // Skip facets for bank collections or known CREATOR carousels. Keep facets
    // when the slug might still remap to a browse primary.
    skipFacets: Boolean(
      productCollectionId ||
        (activeCarouselSlug &&
          findCarouselBySlug(CREATOR.carousels, activeCarouselSlug)),
    ),
  });
  const shopCategoriesReady =
    hasSession &&
    (isBrowseCatalogSettled || browseIndex.primaries.length > 0);
  const browseRiseSettled = useOneTimeRise(
    "shop-browse",
    isActive,
    shopBrowseKey,
    shopCategoriesReady,
  );

  React.useEffect(() => {
    if (!isBrowseCatalogSettled || !activeCarouselSlug) return;
    if (shopBrowse.secondarySlug || shopBrowse.subSlug || shopBrowse.brandSlug) {
      return;
    }
    const knownPrimary = browseIndex.primaries.some(
      (p) => p.slug === activeCarouselSlug,
    );
    if (!knownPrimary) return;
    setShopBrowse({
      primarySlug: activeCarouselSlug,
      secondarySlug: null,
      subSlug: null,
      brandSlug: null,
      brandSlugs: null,
    });
    setActiveCarouselSlug(null);
  }, [
    isBrowseCatalogSettled,
    activeCarouselSlug,
    shopBrowse.secondarySlug,
    shopBrowse.subSlug,
    shopBrowse.brandSlug,
    browseIndex.primaries,
  ]);

  const catalogPageAbsent = Boolean(cfg?.catalogPageAbsent);

  const navigateBrowse = React.useCallback(
    (next: ShopBrowseSelection, meta?: { level: "primary" | "secondary" | "sub" | "brand" | "clear"; label: string }) => {
      setProductCollectionId(null);
      setActiveCarouselSlug(null);
      setShopBrowse(next);
      if (meta) {
        templateAnalytics?.trackShopBrowse({
          level: meta.level,
          label: meta.label,
          primarySlug: next.primarySlug,
          secondarySlug: next.secondarySlug,
          subSlug: next.subSlug,
          brandSlug: next.brandSlug,
        });
      }
      // Collection was forced open without a real Shop page — don't land on empty /shop.
      if (catalogPageAbsent) {
        onNavigate?.("home");
        return;
      }
      if (shopView === "category" && categorySlug) {
        skipDeepLinkOnSelfNavRef.current = true;
        onNavigate?.("shop", { shopView: "category", categorySlug, shopBrowse: next });
      } else {
        setShopView("all");
        setCategorySlug(null);
        onNavigate?.("shop", { shopView: "all", shopBrowse: next });
      }
    },
    [onNavigate, templateAnalytics, shopView, categorySlug, catalogPageAbsent],
  );

  const clearCollectionBrowse = React.useCallback(() => {
    setProductCollectionId(null);
    setActiveCarouselSlug(null);
    setShopView("all");
    setCategorySlug(null);
    setShopBrowse({});
    templateAnalytics?.trackShopBrowse({
      level: "clear",
      label: "Clear collection",
    });
    if (catalogPageAbsent) {
      onNavigate?.("home");
      return;
    }
    onNavigate?.("shop", { shopView: "all", shopBrowse: {} });
  }, [onNavigate, templateAnalytics, catalogPageAbsent]);

  const activeProductCarousel = React.useMemo(() => {
    if (!activeCarouselSlug) return null;
    const car = findCarouselBySlug(CREATOR.carousels, activeCarouselSlug);
    if (!car) {
      // Still show breadcrumb + feed by slug even if carousels haven't loaded.
      return {
        title: activeCarouselSlug,
        renderSlug: activeCarouselSlug,
        type: "shop" as const,
        componentType: undefined as string | undefined,
        description: undefined as string | undefined,
      };
    }
    // Shop URL is this carousel's product feed — even if the website lists the
    // same day-window under Recent Content.
    if (car.type === "content") {
      return {
        ...car,
        type: "shop" as const,
        renderSlug: car.renderSlug || activeCarouselSlug,
      };
    }
    return car;
  }, [activeCarouselSlug, CREATOR.carousels]);

  const productCarouselFromCreator = Boolean(
    activeCarouselSlug &&
      findCarouselBySlug(CREATOR.carousels, activeCarouselSlug),
  );
  const libProductCollectionRow: any = useLoadRenderComponent(
    userId,
    productCarouselFromCreator ? "" : activeCarouselSlug || "",
    undefined,
    supabasePublic,
    username,
  );
  const productCarouselTitle = titleUnlessSlug(
    productCarouselFromCreator
      ? activeProductCarousel?.title
      : libProductCollectionRow?.data?.name,
    activeCarouselSlug,
  );
  // Same helper as LiB individual pages — only Custom Products (`links`) blurbs.
  const productCarouselDescription = visibleCarouselDescription(
    {
      type: productCarouselFromCreator
        ? activeProductCarousel?.componentType
        : libProductCollectionRow?.data?.type,
      description: productCarouselFromCreator
        ? activeProductCarousel?.description
        : String(libProductCollectionRow?.data?.description || "").trim() ||
          parseCollectionDescriptionFromDetails(
            libProductCollectionRow?.data?.details,
          ),
    },
    { isIndividualPage: true },
  );

  // Shop deep-links stay on Products (LiB shoppable day-windows). Do not
  // bounce to Content just because the website carousel list typed the row
  // as Recent Content.

  const isMobile = T.device === "mobile";
  // Page title lives on the separate PageHeader section.
  const shellCfg = cfg ? { ...cfg, heading: undefined } : cfg;

  const featuredBrandSlugs = (shopBrowse.brandSlugs || []).filter((s) =>
    String(s || "").trim(),
  );
  const isFeaturedBrandsView =
    shopView === "brand" && featuredBrandSlugs.length > 0;

  const browseChrome = (() => {
    if (!hasSession) return null;

    // Collection / Trending / Featured brands: keep the collection crumb and
    // still show primary categories like a normal drill-in.
    if (activeProductCarousel || shopView === "trending" || isFeaturedBrandsView) {
      const collectionLabel = activeProductCarousel
        ? productCarouselTitle
        : shopView === "trending"
          ? "Trending products"
          : "Featured brands";
      const crumbs: ShopBrowseNavCrumb[] = collectionLabel
        ? [
            {
              label: collectionLabel,
              onClear: clearCollectionBrowse,
            },
          ]
        : [];
      const options: ShopBrowseNavOption[] = [];
      // No Shop page → category chips would navigate to a broken empty shop.
      if (hasBrowseCatalog && !catalogPageAbsent) {
        for (const p of browseIndex.primaries) {
          if (p.slug === SHOP_BROWSE_OTHER_SLUG) continue;
          options.push({
            label: p.label,
            onClick: () =>
              navigateBrowse(
                {
                  primarySlug: p.slug,
                  secondarySlug: null,
                  subSlug: null,
                  brandSlug: null,
                  brandSlugs: null,
                },
                { level: "primary", label: p.label },
              ),
          });
        }
      }
      return (
        <div className="mte-tpl-shop-browse-enter-wrap">
          <ShopBrowseNav crumbs={crumbs} options={options} isMobile={isMobile} riseSettled={browseRiseSettled} />
        </div>
      );
    }

    if (!isBrowseCatalogSettled && browseIndex.primaries.length === 0) {
      return <ShopBrowseNavSkeleton />;
    }

    const crumbs: ShopBrowseNavCrumb[] = [];
    const options: ShopBrowseNavOption[] = [];
    const panelItems: { label: string; onClick: () => void; image?: string }[] =
      [];

    if (hasBrowseCatalog) {
      const activeIndex =
        shopView === "category" ? categoryFilteredBrowseIndex : browseIndex;
      const coerced = coerceShopBrowseSelection(shopBrowse);
      const { primarySlug, secondarySlug, subSlug, brandSlug } = coerced;

      if (browseLabels.primary) {
        crumbs.push({
          label: browseLabels.primary,
          onClear: () =>
            navigateBrowse({}, { level: "clear", label: "All products" }),
        });
      }
      if (browseLabels.secondary) {
        crumbs.push({
          label: browseLabels.secondary,
          onClear: () =>
            navigateBrowse(
              {
                primarySlug: shopBrowse.primarySlug ?? null,
                secondarySlug: null,
                subSlug: null,
                brandSlug: null,
              },
              { level: "clear", label: "Clear subcategory" },
            ),
        });
      }
      if (browseLabels.sub) {
        crumbs.push({
          label: browseLabels.sub,
          onClear: () =>
            navigateBrowse(
              {
                primarySlug: shopBrowse.primarySlug ?? null,
                secondarySlug: shopBrowse.secondarySlug ?? null,
                subSlug: null,
                brandSlug: null,
              },
              { level: "clear", label: "Clear subcategory" },
            ),
        });
      }
      if (browseLabels.brand) {
        crumbs.push({
          label: browseLabels.brand,
          onClear: () =>
            navigateBrowse(
              {
                primarySlug: shopBrowse.primarySlug ?? null,
                secondarySlug: shopBrowse.secondarySlug ?? null,
                subSlug: shopBrowse.subSlug ?? null,
                brandSlug: null,
                brandSlugs: null,
              },
              { level: "clear", label: "Clear brand" },
            ),
        });
      }

      if (!primarySlug) {
        for (const p of activeIndex.primaries) {
          if (p.slug === SHOP_BROWSE_OTHER_SLUG) continue;
          const item = {
            label: p.label,
            image: primaryCoverBySlug.get(p.slug),
            onClick: () =>
              navigateBrowse(
                {
                  primarySlug: p.slug,
                  secondarySlug: null,
                  subSlug: null,
                  brandSlug: null,
                },
                { level: "primary", label: p.label },
              ),
          };
          options.push(item);
          panelItems.push(item);
        }
      } else if (primarySlug !== SHOP_BROWSE_OTHER_SLUG && !secondarySlug) {
        for (const s of activeIndex.secondariesByPrimarySlug[primarySlug] ?? []) {
          if (s.slug === SHOP_BROWSE_OTHER_SLUG) continue;
          options.push({
            label: s.label,
            onClick: () =>
              navigateBrowse(
                {
                  primarySlug: shopBrowse.primarySlug ?? null,
                  secondarySlug: s.slug,
                  subSlug: null,
                  brandSlug: null,
                },
                { level: "secondary", label: s.label },
              ),
          });
        }
      } else if (
        secondarySlug &&
        secondarySlug !== SHOP_BROWSE_OTHER_SLUG &&
        !subSlug
      ) {
        const pathKey = `${primarySlug}|${secondarySlug}`;
        for (const s of activeIndex.subsByPathSlug[pathKey] ?? []) {
          if (s.slug === SHOP_BROWSE_OTHER_SLUG) continue;
          options.push({
            label: s.label,
            onClick: () =>
              navigateBrowse(
                {
                  primarySlug: shopBrowse.primarySlug ?? null,
                  secondarySlug: shopBrowse.secondarySlug ?? null,
                  subSlug: s.slug,
                  brandSlug: null,
                },
                { level: "sub", label: s.label },
              ),
          });
        }
      } else if (
        secondarySlug &&
        secondarySlug !== SHOP_BROWSE_OTHER_SLUG &&
        subSlug &&
        subSlug !== SHOP_BROWSE_OTHER_SLUG &&
        !brandSlug
      ) {
        const pathKey = `${primarySlug}|${secondarySlug}|${subSlug}`;
        for (const b of activeIndex.brandsByPathSlug[pathKey] ?? []) {
          if (b.slug === SHOP_BROWSE_OTHER_SLUG) continue;
          options.push({
            label: b.label,
            onClick: () =>
              navigateBrowse(
                {
                  primarySlug: shopBrowse.primarySlug ?? null,
                  secondarySlug: shopBrowse.secondarySlug ?? null,
                  subSlug: shopBrowse.subSlug ?? subSlug,
                  brandSlug: b.slug,
                },
                { level: "brand", label: b.label },
              ),
          });
        }
      }
    }

    const drilledIn = crumbs.length > 0;
    if (!drilledIn && panelItems.length > 0) {
      return (
        <div className="mte-tpl-shop-browse-enter-wrap">
          <ShopBrowsePanel
            items={panelItems}
            cols={4}
            device={T.device}
            display="row"
            riseSettled={browseRiseSettled}
          />
        </div>
      );
    }

    if (!crumbs.length && !options.length) return null;
    return (
      <div className="mte-tpl-shop-browse-enter-wrap">
        <ShopBrowseNav crumbs={crumbs} options={options} isMobile={isMobile} riseSettled={browseRiseSettled} />
      </div>
    );
  })();

  const hideDefaultCatalog = Boolean(cfg?.hideDefaultCatalog);
  const drilledIn =
    Boolean(activeProductCarousel) ||
    Boolean(productCollectionId) ||
    shopView === "trending" ||
    shopView === "category" ||
    shopView === "brand" ||
    browseSelectionIsActive(shopBrowse);
  const showProductCatalog = !hideDefaultCatalog || drilledIn;
  // Inspiration bands are an entry point — hide once the follower drills in,
  // but stay mounted so breadcrumb round-trips don't re-skeleton / refetch.
  const showInspiration = !drilledIn;
  const catalogLabel = activeProductCarousel
    ? productCarouselTitle
    : drilledIn
      ? shopView === "trending"
        ? "Trending products"
        : isFeaturedBrandsView
          ? "Featured brands"
          : browseLabels.brand ||
            browseLabels.sub ||
            browseLabels.secondary ||
            browseLabels.primary ||
            "All products"
      : "All products";

  const onLoadMoreProducts = React.useCallback(() => {
    if (!catalogEnabled || deferProductGrid) return;
    void fetchNextProductsPage();
  }, [catalogEnabled, deferProductGrid, fetchNextProductsPage]);

  if (hasSession) {
    if (productCollectionId) {
      const categoryOptions = hasBrowseCatalog
        ? browseIndex.primaries
            .filter((p) => p.slug !== SHOP_BROWSE_OTHER_SLUG)
            .map((p) => ({ slug: p.slug, label: p.label }))
        : [];
      return (
        <Shell T={T} cfg={shellCfg}>
          <WebsiteProductCollectionPanel
            T={T}
            collectionId={productCollectionId}
            username={username}
            userId={userId}
            cols={cols}
            aspect={aspect}
            showButton={showButton}
            showCategory={showCategory}
            showPrice={showPrice}
            buttonLabel={buttonLabel}
            brandCounts={brandCounts}
            categoryOptions={categoryOptions}
            onClear={clearCollectionBrowse}
            onPickCategory={(slug, label) =>
              navigateBrowse(
                {
                  primarySlug: slug,
                  secondarySlug: null,
                  subSlug: null,
                  brandSlug: null,
                  brandSlugs: null,
                },
                { level: "primary", label },
              )
            }
          />
        </Shell>
      );
    }
    return (
      <Shell T={T} cfg={shellCfg}>
        <ContentCatalogDeferProvider onAllLooksReady={reportMerchBandsReady}>
        <div
          data-website-region="shop-region"
          data-website-tour="shop-region"
        >
          {browseChrome}
          <ShopFeaturedInsertSlot hidden={!showInspiration} active={isActive} />
          {activeProductCarousel ? (
            <div className="mte-tpl-shop-catalog-block" ref={catalogBlockRef}>
              {catalogLabel ? (
              <div className="mte-tpl-shop-catalog-head">
                <p className="mte-tpl-shop-catalog-label mte-tpl-shop-catalog-label--context">
                  {catalogLabel}
                </p>
                {productCarouselDescription ? (
                  <p className="mte-tpl-product-collection-hero-sub">
                    {productCarouselDescription}
                  </p>
                ) : null}
              </div>
              ) : null}
              <TemplateCollectionCatalogPanel
                userId={userId}
                username={username}
                renderSlug={
                  String(
                    activeProductCarousel.renderSlug || activeCarouselSlug || "",
                  ).trim()
                }
                T={T}
                pageVariant="shop"
                cols={cols}
                aspect={aspect}
                showButton={showButton}
                showCategory={showCategory}
                showPrice={showPrice}
                buttonLabel={buttonLabel}
                contentTabLabel={catalogLabel}
                enabled={catalogEnabled}
                carousel={
                  productCarouselFromCreator
                    ? (activeProductCarousel as CarouselData)
                    : null
                }
              />
            </div>
          ) : showProductCatalog ? (
            <div className="mte-tpl-shop-catalog-block" ref={catalogBlockRef}>
              <div className="mte-tpl-shop-catalog-head">
                <p
                  className={`mte-tpl-shop-catalog-label${
                    drilledIn ? " mte-tpl-shop-catalog-label--context" : ""
                  }`}
                >
                  {catalogLabel}
                </p>
              </div>
              <ShopProductCatalogPanel
                catalogKey={shopBrowseKey}
                T={T}
                products={products}
                loading={loading || deferProductGrid}
                userId={userId}
                cols={cols}
                aspect={aspect}
                showButton={showButton}
                showCategory={showCategory}
                showPrice={showPrice}
                buttonLabel={buttonLabel}
                shopView={shopView}
                browseLabels={browseLabels}
                brandCounts={brandCounts}
                hasMore={
                  catalogEnabled && !deferProductGrid && hasNextProductsPage
                }
                loadingMore={
                  catalogEnabled &&
                  !deferProductGrid &&
                  isFetchingNextProductsPage
                }
                refreshing={productsRefreshing}
                onLoadMore={
                  catalogEnabled && !deferProductGrid
                    ? onLoadMoreProducts
                    : undefined
                }
              />
            </div>
          ) : null}
        </div>
        </ContentCatalogDeferProvider>
      </Shell>
    );
  }


  const items = CREATOR.products.slice(0, count);
  return (
    <Shell T={T} cfg={shellCfg}>
      <TplShopGrid
        className={`mte-tpl-shop-grid${T.device === "tablet" ? " mte-tpl-shop-grid--cap3" : ""}`}
        cols={cols}
        aspect={aspect}
      >
        {items.map((p) => (
          <a key={p.id} href="#" className="mte-tpl-shop-card-link">
            <div className="mte-tpl-shop-card-frame mte-tpl-shop-card-frame--mb14">
              {p.image && (
                <img
                  src={p.image}
                  alt=""
                  className="mte-tpl-img-filter mte-tpl-shop-card-img"
                />
              )}
              {showButton ? (
                <span className="mte-tpl-shop-pill mte-tpl-shop-pill--mobile">
                  {buttonLabel.toUpperCase()}
                </span>
              ) : null}
            </div>
            <div className="mte-tpl-prod-name-row mte-tpl-prod-name-row--no-btn">
              {p.name && (
                <div className="mte-tpl-prod-name">
                  <E path={`product.${p.id}.name`}>{p.name}</E>
                </div>
              )}
              {showPrice && p.price ? (
                <div className="mte-tpl-prod-price">{p.price}</div>
              ) : null}
            </div>
          </a>
        ))}
      </TplShopGrid>
    </Shell>
  );
}
ProductGrid.options = {
  heading: {
    kind: "hidden",
    label: "",
    default: "",
  },
  count: {
    kind: "number",
    label: "Visible items",
    min: 4,
    max: 24,
    default: 12,
  },
  columns: { kind: "radio", label: "Columns", options: [3, 4, 5], default: 5 },
  aspect: {
    kind: "radio",
    label: "Card shape",
    options: ["4/5", "1/1", "3/4"],
    default: "4/5",
  },
  showButton: { kind: "toggle", label: "Show shop button", default: true },
  buttonLabel: { kind: "text", label: "Button label", default: "Shop" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

// ── Content grid (StayUpToDate content tabs) ───────────────────
type ContentCarouselTabsProps = {
  T: ThemeTokens;
  cfg?: SectionCfg;
  onNavigate?: TemplateNavigateFn;
  carouselDeepLink?: TemplateCarouselDeepLink | null;
  onCarouselDeepLinkConsumed?: () => void;
  contentView: TemplateContentView;
  allCarousels: CarouselData[];
  /** False while the content page layer is CSS-hidden (pre-mounted). */
  isActive?: boolean;
};

export function ContentCarouselTabs({
  T,
  cfg,
  onNavigate,
  carouselDeepLink,
  onCarouselDeepLinkConsumed,
  contentView,
  allCarousels,
  isActive = true,
}: ContentCarouselTabsProps) {
  const templateAnalytics = useTemplateAnalyticsOptional();
  const CREATOR = useCreatorData();
  const { get: getContentEdit } = useEdit();
  const aspect = "4/5";

  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);

  /** Global live fetch — keep pending until settled so we never flash empty copy. */
  const liveCreatorLoading = useCreatorDataLoading();
  const isCreatorLoading = useCreatorSectionLoading(
    (CREATOR.carousels?.length ?? 0) > 0,
  );

  const {
    visibleCarousels: contentCarousels,
    isResolvingTabs: contentTabsResolving,
  } = useTemplateFilteredCarousels(
    allCarousels,
    userId,
    username,
    hasSession,
  );

  const [activeTab, setActiveTab] = React.useState(0);
  const [pendingTabIndex, setPendingTabIndex] = React.useState<number | null>(
    null,
  );
  const paintedTabIndex =
    pendingTabIndex != null ? pendingTabIndex : activeTab;
  const contentTabBarRef = React.useRef<HTMLDivElement>(null);
  const lastTrackedContentViewRef = React.useRef<TemplateContentView | null>(
    null,
  );

  React.useEffect(() => {
    if (!isActive) return;
    if (lastTrackedContentViewRef.current === contentView) return;
    const prev = lastTrackedContentViewRef.current;
    lastTrackedContentViewRef.current = contentView;
    // Skip the initial mount baseline — only count actual Latest ↔ Series switches.
    if (prev == null) return;
    templateAnalytics?.trackContentViewSwitch({ contentView });
  }, [contentView, isActive, templateAnalytics]);

  const [emptyContentTabSlugs, setEmptyContentTabSlugs] = React.useState<
    Record<string, true>
  >({});

  // Keep visited tabs mounted for instant switch; mount only the active tab
  // until first feed paint so we don't fire N parallel feed RPCs on boot.
  const [mountedContentTabSlugs, setMountedContentTabSlugs] = React.useState<
    Record<string, true>
  >({});

  React.useEffect(() => {
    if (isActive) return;
    setActiveTab(0);
    setPendingTabIndex(null);
    setEmptyContentTabSlugs({});
    setMountedContentTabSlugs({});
  }, [isActive]);

  const contentTabs = contentCarousels.filter(
    (c) => c.renderSlug && !emptyContentTabSlugs[String(c.renderSlug)],
  );

  const slugKey = contentTabs.map((c) => c.renderSlug ?? c.id).join("|");
  React.useEffect(() => {
    setActiveTab((i) =>
      contentTabs.length === 0 ? 0 : Math.min(i, contentTabs.length - 1),
    );
  }, [slugKey, contentTabs.length]);

  React.useEffect(() => {
    if (pendingTabIndex == null) return;
    if (pendingTabIndex === activeTab) setPendingTabIndex(null);
  }, [pendingTabIndex, activeTab]);

  React.useEffect(() => {
    if (
      !carouselDeepLink ||
      carouselDeepLink.target !== "content" ||
      !onCarouselDeepLinkConsumed
    ) {
      return;
    }
    const linkView = carouselDeepLink.contentView ?? "latest";
    if (linkView !== contentView) return;
    if (contentTabsResolving) return;
    const slug = carouselDeepLink.slug?.trim() ?? "";
    const platformKey = carouselDeepLink.platformKey?.trim() ?? "";
    if (!slug && !platformKey) {
      onCarouselDeepLinkConsumed();
      return;
    }
    // When there are no carousel tabs the platform highlight feed is shown instead.
    // Let TemplatePlatformHighlightFeed consume the deep link via onPlatformDeepLinkConsumed
    // once highlights have loaded — consuming it here would clear platformDeepLink before
    // highlights are available, causing the feed to reset to the first tab.
    if (contentTabs.length === 0) return;
    const idx = slug
      ? contentTabs.findIndex((c) => tabSlugMatchesCarouselSlug(slug, c))
      : -1;
    const pkIdx =
      idx < 0 && platformKey
        ? findContentTabIndexForPlatformKey(
            contentTabs,
            CREATOR.highlights,
            platformKey,
          )
        : -1;
    const resolved = idx >= 0 ? idx : pkIdx;
    // Only call setActiveTab when it would actually change — avoids the extra
    // render cycle that can cause a visible flicker or stale-state override.
    if (resolved >= 0) setActiveTab((prev) => (prev === resolved ? prev : resolved));
    onCarouselDeepLinkConsumed();
  }, [
    carouselDeepLink,
    contentView,
    slugKey,
    contentTabsResolving,
    onCarouselDeepLinkConsumed,
    contentTabs,
    CREATOR.highlights,
  ]);

  useTemplateTabScrollIntoView(contentTabBarRef, paintedTabIndex, slugKey);

  const bootstrapping =
    liveCreatorLoading ||
    isCreatorLoading ||
    (hasSession && contentTabsResolving);

  const [initialFeedReady, setInitialFeedReady] = React.useState(false);
  const markInitialFeedReady = React.useCallback(() => {
    setInitialFeedReady(true);
  }, []);

  // Sync: Latest ↔ Series / active-tab change must clear ready before paint so the
  // grid loader shows immediately (effects alone leave one blank frame).
  // Scope to the painted tab only — progressive tab discovery must not reset the
  // first feed paint when later getComponent rows arrive.
  const paintedContentSlug = String(
    contentTabs[Math.min(paintedTabIndex, Math.max(contentTabs.length - 1, 0))]
      ?.renderSlug || "",
  ).trim();
  const [bootScopeKey, setBootScopeKey] = React.useState(
    () => `${contentView}|${paintedContentSlug}`,
  );
  const nextBootScopeKey = `${contentView}|${paintedContentSlug}`;
  if (bootScopeKey !== nextBootScopeKey) {
    setBootScopeKey(nextBootScopeKey);
    setInitialFeedReady(false);
  }

  React.useEffect(() => {
    if (bootstrapping || contentTabs.length === 0) return;
    const slug = String(
      contentTabs[Math.min(paintedTabIndex, contentTabs.length - 1)]?.renderSlug || "",
    ).trim();
    if (slug && isTemplateTabFeedReady(userId, slug)) {
      setInitialFeedReady(true);
    } else {
      setInitialFeedReady(false);
    }
  }, [bootstrapping, contentTabs, paintedTabIndex, userId]);

  React.useEffect(() => {
    const slug = String(
      contentTabs[Math.min(paintedTabIndex, Math.max(contentTabs.length - 1, 0))]
        ?.renderSlug || "",
    ).trim();
    if (!slug) return;
    setMountedContentTabSlugs((prev) =>
      prev[slug] ? prev : { ...prev, [slug]: true },
    );
  }, [paintedTabIndex, slugKey, contentTabs]);

  // After the active tab paints, mount remaining tabs idle so switches stay instant
  // without competing with first-paint feed RPCs.
  React.useEffect(() => {
    if (!initialFeedReady || contentTabs.length === 0) return;
    setMountedContentTabSlugs((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const c of contentTabs) {
        const slug = String(c.renderSlug || "").trim();
        if (slug && !next[slug]) {
          next[slug] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [initialFeedReady, slugKey, contentTabs]);

  const showPlatformFallback =
    !bootstrapping && contentTabs.length === 0 && contentView === "latest";
  const showSeriesEmpty =
    !bootstrapping && contentTabs.length === 0 && contentView === "series";

  // Grid pending until the active tab’s feed paints.
  const showBootOverlay =
    bootstrapping ||
    (contentTabs.length > 0 && !initialFeedReady) ||
    (showPlatformFallback && !initialFeedReady);

  const tabBar = contentTabs.length >= 1 && (
    <div className="mte-tpl-tabbar" ref={contentTabBarRef}>
      {contentTabs.map((c, i) => (
        <button
          key={c.renderSlug || c.id}
          type="button"
          onClick={() => {
            setPendingTabIndex(i);
            setActiveTab(i);
            const slug = c.renderSlug?.trim() || "";
            // Instant grid loader when the target tab isn’t cached-ready yet.
            setInitialFeedReady(
              Boolean(slug && isTemplateTabFeedReady(userId, slug)),
            );
            if (slug) {
              templateAnalytics?.trackContentTab({
                slug,
                label: c.stayComponentRowId
                  ? carouselTabDisplayTitle(getContentEdit, c)
                  : c.title,
                contentView,
                isSeriesTab: contentView === "series",
              });
            }
            if (slug && onNavigate) {
              onNavigate("content", { carouselSlug: slug, contentView });
            }
          }}
          className={
            i === paintedTabIndex
              ? "mte-tpl-tab-pill mte-tpl-tab-pill--active"
              : "mte-tpl-tab-pill"
          }
        >
          {c.stayComponentRowId ? (
            <CarouselTabLabel carousel={c} />
          ) : (
            c.title
          )}
        </button>
      ))}
    </div>
  );

  if (showSeriesEmpty) {
    return (
      <p className="mte-tpl-content-series-empty">
        Series will appear here once your content has been grouped.
      </p>
    );
  }

  const currentCarousel = contentTabs[paintedTabIndex] || contentTabs[0];
  const platformDeepLink = carouselDeepLink?.platformKey?.trim()
    ? { platformKey: carouselDeepLink.platformKey.trim() }
    : carouselDeepLink?.slug?.trim()
      ? { platformKey: carouselDeepLink.slug.trim() }
      : null;

  return (
    <>
      {contentTabs.length >= 1 ? tabBar : null}
      {contentTabs.length >= 1 && currentCarousel ? (
        <div className="mte-tpl-carousel-title">
          {currentCarousel.stayComponentRowId ? (
            <E path={`sutc.${currentCarousel.stayComponentRowId}.tabTitle`}>
              {carouselTabDisplayTitle(getContentEdit, currentCarousel)}
            </E>
          ) : (
            currentCarousel.title
          )}
        </div>
      ) : null}

      <div
        className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content mte-tpl-tab-feed-stage--boot"
        aria-busy={showBootOverlay}
      >
        {hasSession && contentTabs.some((c) => c.renderSlug) ? (
          <div className="mte-tpl-tab-panels-stack">
            {contentTabs.map((c, i) =>
              c.renderSlug && mountedContentTabSlugs[c.renderSlug] ? (
                <div
                  key={c.renderSlug}
                  className={
                    i === paintedTabIndex
                      ? "mte-tpl-tab-panel mte-tpl-tab-panel--active"
                      : "mte-tpl-tab-panel"
                  }
                  role="tabpanel"
                  aria-hidden={i !== paintedTabIndex}
                >
                  <TemplateStayUpToDateTabPanel
                    userId={userId}
                    username={username}
                    renderSlug={c.renderSlug}
                    T={T}
                    pageVariant="content"
                    aspect={aspect}
                    showShopButton={false}
                    contentView={contentView}
                    contentTabLabel={
                      c.stayComponentRowId
                        ? carouselTabDisplayTitle(getContentEdit, c)
                        : c.title
                    }
                    // Parent owns the boot skeleton so we don't remount a second grid.
                    feedPending={showBootOverlay ? "none" : "grid"}
                    onFeedPaintReady={
                      i === paintedTabIndex ? markInitialFeedReady : undefined
                    }
                    onTabFeedResolved={(slug, hasItems) => {
                      if (!hasItems && slug) {
                        const tab = contentCarousels.find(
                          (x) => String(x.renderSlug) === String(slug),
                        );
                        if (!shouldHideEmptyContentTab(tab)) return;
                        setEmptyContentTabSlugs((s) => ({ ...s, [slug]: true }));
                      }
                    }}
                  />
                </div>
              ) : null,
            )}
          </div>
        ) : null}

        {showPlatformFallback ? (
          <div className="mte-tpl-platform-boot-mount">
            <TemplatePlatformHighlightFeed
              T={T}
              userId={userId}
              username={username}
              highlights={CREATOR.highlights}
              contentShape="square"
              platformDeepLink={platformDeepLink}
              onPlatformDeepLinkConsumed={onCarouselDeepLinkConsumed}
              embedVariant="content"
              onNavigate={onNavigate}
              isActive={isActive}
              suppressPendingGrid={showBootOverlay}
              onInitialFeedReady={markInitialFeedReady}
            />
          </div>
        ) : null}

        {showBootOverlay ? (
          <div className="mte-tpl-boot-pending-overlay" aria-hidden>
            <TemplateTabFeedPendingGrid
              pageVariant="content"
              device={T.device}
              T={T}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}

export function ContentGrid({
  T,
  cfg,
  onNavigate,
  carouselDeepLink,
  onCarouselDeepLinkConsumed,
  shopBrowseSelection,
  /** False while the content page layer is CSS-hidden (pre-mounted). */
  isActive = true,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);
  // Preloaded hidden layers still mount on desktop — don't steal network/CPU.
  const catalogEnabled = hasSession && isActive;
  const isMobile = T.device === "mobile";
  const cols = (cfg?.columns as number) || 5;
  const aspect = (cfg?.aspect as string) || "4/5";
  const buttonLabel = (cfg?.buttonLabel as string) || "Shop";
  /**
   * Looks extras present — OrderedSectionsRenderer sets hideDefaultCatalog.
   * Defer the "All content" grid until Looks page 1 settles (or catalog nears
   * the viewport) so top bands win the first network burst.
   */
  const hideDefaultCatalog = Boolean(cfg?.hideDefaultCatalog);
  const [looksFirstPageReady, setLooksFirstPageReady] = React.useState(
    () => !hideDefaultCatalog,
  );
  const [catalogNearViewport, setCatalogNearViewport] = React.useState(false);
  const catalogBlockRef = React.useRef<HTMLDivElement | null>(null);
  const hadLooksExtrasRef = React.useRef(hideDefaultCatalog);

  React.useEffect(() => {
    if (!hideDefaultCatalog) {
      setLooksFirstPageReady(true);
      hadLooksExtrasRef.current = false;
      return;
    }
    // Only reset when Looks extras newly appear — not on every mount (cached
    // Looks page 1 may already report ready in the same paint).
    if (!hadLooksExtrasRef.current) {
      setLooksFirstPageReady(false);
      setCatalogNearViewport(false);
    }
    hadLooksExtrasRef.current = true;
  }, [hideDefaultCatalog]);

  const reportLooksFirstPageReady = React.useCallback(() => {
    setLooksFirstPageReady(true);
  }, []);

  // Safety: never leave the catalog stuck if Looks fail to report.
  React.useEffect(() => {
    if (!hideDefaultCatalog || looksFirstPageReady || catalogNearViewport) {
      return;
    }
    const t = window.setTimeout(() => setLooksFirstPageReady(true), 4000);
    return () => window.clearTimeout(t);
  }, [hideDefaultCatalog, looksFirstPageReady, catalogNearViewport]);

  const [shopBrowse, setShopBrowse] = React.useState<ShopBrowseSelection>(() => {
    const fromProp = shopBrowseSelection ?? {};
    const primary = String(fromProp.primarySlug ?? "").trim();
    // Shared previewShopBrowse also carries Products category drills — ignore those.
    if (primary && isContentPlatformBrowseSlug(primary)) {
      return {
        primarySlug: fromProp.primarySlug ?? null,
        secondarySlug: fromProp.secondarySlug ?? null,
        brandSlug: null,
      };
    }
    if (typeof window === "undefined") return {};
    const fromSearch = readContentBrowseFromSearch(window.location.search);
    return fromSearch.primarySlug
      ? { ...fromSearch, brandSlug: null }
      : {};
  });
  const contentBrowseToken = [
    shopBrowse.primarySlug ?? "",
    shopBrowse.secondarySlug ?? "",
    shopBrowse.subSlug ?? "",
  ].join("|");
  /** Content collection / carousel deep-link — feed + breadcrumb (not platform browse). */
  const contentDeepLinkSlug = (() => {
    if (carouselDeepLink?.target !== "content") return null;
    const slug = carouselDeepLink.slug?.trim() ?? "";
    if (!slug || carouselDeepLink.shopBrowse?.primarySlug) return null;
    if (isContentPlatformBrowseSlug(slug)) return null;
    return slug;
  })();
  const [activeCarouselSlug, setActiveCarouselSlug] = React.useState<string | null>(
    contentDeepLinkSlug,
  );
  if (contentDeepLinkSlug && activeCarouselSlug !== contentDeepLinkSlug) {
    setActiveCarouselSlug(contentDeepLinkSlug);
  }
  const [shoppable, setShoppable] = React.useState<"all" | "yes">(() =>
    typeof window !== "undefined"
      ? readContentShoppableFromSearch(window.location.search)
      : "all",
  );
  const skipDeepLinkOnSelfNavRef = React.useRef(false);
  const navigateRouter = useNavigate();
  const routerLocation = useLocation();

  React.useEffect(() => {
    const next = readContentShoppableFromSearch(routerLocation.search);
    setShoppable((prev) => (prev === next ? prev : next));
  }, [routerLocation.search]);

  React.useEffect(() => {
    // Path-driven browse on public website wins. Query mirrors are editor-only.
    if (isPublicWebsiteTemplateRoute(routerLocation.pathname)) return;
    if ((shopBrowseSelection?.primarySlug ?? "").trim()) return;
    const fromSearch = readContentBrowseFromSearch(routerLocation.search);
    setShopBrowse((prev) => {
      const next: ShopBrowseSelection = {
        primarySlug: fromSearch.primarySlug,
        secondarySlug: fromSearch.secondarySlug,
        brandSlug: null,
      };
      if (
        prev.primarySlug === next.primarySlug &&
        prev.secondarySlug === next.secondarySlug
      ) {
        return prev;
      }
      return next;
    });
  }, [
    routerLocation.search,
    routerLocation.pathname,
    shopBrowseSelection?.primarySlug,
  ]);

  const updateShoppable = React.useCallback(
    (v: "all" | "yes") => {
      setShoppable(v);
      const rawPath =
        typeof window !== "undefined"
          ? window.location.pathname
          : routerLocation.pathname;
      navigateRouter(
        {
          pathname: toCreatorBrowserPathname(rawPath, username),
          search: patchSearchParam(
            routerLocation.search,
            "shoppable",
            v === "yes" ? "yes" : null,
          ),
        },
        { replace: true },
      );
    },
    [navigateRouter, routerLocation.pathname, routerLocation.search, username],
  );

  React.useEffect(() => {
    if (shopBrowseSelection == null) return;
    const primary = String(shopBrowseSelection.primarySlug ?? "").trim();
    // Shared previewShopBrowse also carries Products category drills — ignore those
    // so ContentGrid does not adopt "apparel" and then clear→navigate to /content.
    if (primary && !isContentPlatformBrowseSlug(primary)) return;
    setShopBrowse((prev) => {
      const next: ShopBrowseSelection = {
        primarySlug: shopBrowseSelection.primarySlug ?? null,
        secondarySlug: shopBrowseSelection.secondarySlug ?? null,
        brandSlug: null,
      };
      if (
        prev.primarySlug === next.primarySlug &&
        prev.secondarySlug === next.secondarySlug
      ) {
        return prev;
      }
      return next;
    });
  }, [shopBrowseSelection?.primarySlug, shopBrowseSelection?.secondarySlug]);

  React.useEffect(() => {
    if (carouselDeepLink?.target !== "content") return;
    if (skipDeepLinkOnSelfNavRef.current) {
      skipDeepLinkOnSelfNavRef.current = false;
      onCarouselDeepLinkConsumed?.();
      return;
    }
    const slug = carouselDeepLink.slug?.trim() ?? "";
    const platformKey = carouselDeepLink.platformKey?.trim() ?? "";
    const browse = carouselDeepLink.shopBrowse;
    const hasExplicitBrowse = Boolean(
      (browse?.primarySlug ?? "").trim() || (browse?.secondarySlug ?? "").trim(),
    );

    const openContentCarousel = (carouselSlug: string) => {
      setActiveCarouselSlug(carouselSlug);
      setShopBrowse({});
    };

    const openPlatformBrowse = (primary: string, secondary?: string | null) => {
      setActiveCarouselSlug(null);
      setShopBrowse({
        primarySlug: primary || null,
        secondarySlug: secondary ?? null,
        brandSlug: null,
      });
    };

    // Stay on the Content page for any matching carousel (including shop-typed
    // rows). Product collections on /shop/{id} are still owned by ProductGrid.
    if (slug && !platformKey) {
      const car = findCarouselBySlug(CREATOR.carousels, slug);
      const pick = !car ? findRecentContentPick(CREATOR.carousels, slug) : null;
      const resolved = car || pick?.carousel || null;
      if (resolved) {
        openContentCarousel(
          String(resolved.renderSlug || slug).trim() || slug,
        );
        onCarouselDeepLinkConsumed?.();
        return;
      }
      // Known feed platform (e.g. /content/youtube) — browse, never a phantom collection.
      if (isContentPlatformBrowseSlug(slug) || hasExplicitBrowse) {
        openPlatformBrowse(
          (browse?.primarySlug ?? "").trim() || slug,
          browse?.secondarySlug ?? null,
        );
        onCarouselDeepLinkConsumed?.();
        return;
      }
      // SPA collection click (slug only, no browse) — open by slug even if
      // the carousel row isn't in CREATOR.carousels yet.
      if (!hasExplicitBrowse) {
        openContentCarousel(slug);
        onCarouselDeepLinkConsumed?.();
        return;
      }
    }

    setActiveCarouselSlug(null);
    setShopBrowse({
      primarySlug: browse?.primarySlug ?? platformKey ?? (slug || null),
      secondarySlug: browse?.secondarySlug ?? null,
      brandSlug: null,
    });
    onCarouselDeepLinkConsumed?.();
  }, [
    carouselDeepLink?.target,
    carouselDeepLink?.slug,
    carouselDeepLink?.platformKey,
    carouselDeepLink?.shopBrowse,
    onCarouselDeepLinkConsumed,
    CREATOR.carousels,
    onNavigate,
  ]);

  // Root browse (Looks visible) — defer catalog until Featured Outfit /
  // Recent Looks can paint. Platform / collection drill-ins need the grid now.
  const platformSlugForDefer = (shopBrowse.primarySlug ?? "")
    .trim()
    .toLowerCase();
  const looksOnRootBrowse =
    hideDefaultCatalog && !platformSlugForDefer && !activeCarouselSlug;
  const deferContentGrid =
    looksOnRootBrowse && !looksFirstPageReady && !catalogNearViewport;

  // Early unlock only after the user scrolls toward the catalog — do not use
  // IntersectionObserver on mount (short Looks bands leave the catalog in the
  // first viewport and would defeat Looks-first).
  React.useEffect(() => {
    if (!looksOnRootBrowse || catalogNearViewport || looksFirstPageReady) {
      return;
    }
    const onScroll = () => {
      const el = catalogBlockRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // User has scrolled the catalog into (or near) view.
      if (top < window.innerHeight * 0.9) {
        setCatalogNearViewport(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () =>
      window.removeEventListener("scroll", onScroll, true);
  }, [looksOnRootBrowse, catalogNearViewport, looksFirstPageReady]);

  const {
    contentItems,
    contentIndex,
    contentLoading,
    isContentFacetsSettled,
    hasShoppableContent,
    fetchNextContentPage,
    hasNextContentPage,
    isFetchingNextContentPage,
    contentRefreshing,
  } = useShopCatalog({
    username,
    userId,
    enabled: catalogEnabled,
    shopView: "content",
    categorySlug: null,
    shopBrowse,
    shoppableFilter: shoppable,
    surface: "content",
    // Collection view loads its own feed — don't also pull the content browse pages.
    // Looks-first: also skip until Featured Outfit / Recent Looks settle.
    skipGrid: Boolean(activeCarouselSlug) || deferContentGrid,
    // Platform browse still needs content facets; true collections do not.
    skipFacets: Boolean(
      activeCarouselSlug && !isContentPlatformBrowseSlug(activeCarouselSlug),
    ),
  });

  // If the URL is on ?shoppable=yes but this platform has none, drop back to Content.
  React.useEffect(() => {
    if (shoppable !== "yes" || hasShoppableContent) return;
    if (contentLoading || contentRefreshing) return;
    updateShoppable("all");
  }, [
    shoppable,
    hasShoppableContent,
    contentLoading,
    contentRefreshing,
    updateShoppable,
  ]);

  // Switching Instagram → TikTok (etc.) must land on Content, not keep
  // Shoppable from the previous platform's ?shoppable=yes. Check the URL too:
  // navigateBrowse may optimistically set state to "all" while search still
  // has shoppable=yes, and the search-sync effect would otherwise put it back.
  const platformSlugForShoppable = (shopBrowse.primarySlug ?? "")
    .trim()
    .toLowerCase();
  const prevPlatformSlugForShoppableRef = React.useRef(platformSlugForShoppable);
  React.useEffect(() => {
    const prev = prevPlatformSlugForShoppableRef.current;
    if (prev === platformSlugForShoppable) return;
    prevPlatformSlugForShoppableRef.current = platformSlugForShoppable;
    const urlShoppable =
      readContentShoppableFromSearch(routerLocation.search) === "yes";
    if (!urlShoppable && shoppable !== "yes") return;
    updateShoppable("all");
  }, [
    platformSlugForShoppable,
    shoppable,
    updateShoppable,
    routerLocation.search,
  ]);

  const catalogPageAbsent = Boolean(cfg?.catalogPageAbsent);

  const navigateBrowse = React.useCallback(
    (next: ShopBrowseSelection, meta?: { level: "primary" | "secondary" | "clear"; label: string }) => {
      const prevPrimary = (shopBrowse.primarySlug ?? "").trim().toLowerCase();
      const nextPrimary = (next.primarySlug ?? "").trim().toLowerCase();
      const platformChanged = prevPrimary !== nextPrimary;

      setActiveCarouselSlug(null);
      setShopBrowse(next);
      // Optimistic: don't flash Shoppable on the newly selected platform.
      if (platformChanged) setShoppable("all");
      skipDeepLinkOnSelfNavRef.current = true;
      if (meta) {
        // Content platform / format chips belong on Content analytics, not Shop.
        templateAnalytics?.trackContentTab({
          slug:
            (meta.level === "secondary"
              ? next.secondarySlug
              : next.primarySlug) ||
            meta.label ||
            "all",
          label: meta.label,
          contentView: "latest",
          sourcePageKey: "content",
        });
      }
      // Collection was forced open without a real Content page — don't land on empty /content.
      if (catalogPageAbsent) {
        onNavigate?.("home");
        return;
      }
      onNavigate?.("content", { shopBrowse: next });
      // Public /website/... encodes platform/format in the path via onNavigate.
      // Editor dashboard has no content path segments — mirror into ?platform&format.
      // Clear ?shoppable when the platform changes so preservedFilterSearchForPage
      // does not re-apply Shoppable on the next platform.
      const onWebsite = isPublicWebsiteTemplateRoute(routerLocation.pathname);
      if (onWebsite) return;
      let search = patchSearchParam(
        routerLocation.search,
        "platform",
        (next.primarySlug || "").trim() || null,
      );
      search = patchSearchParam(
        search,
        "format",
        (next.secondarySlug || "").trim() || null,
      );
      if (platformChanged) {
        search = patchSearchParam(search, "shoppable", null);
      }
      if (search !== (routerLocation.search || "")) {
        const rawPath =
          typeof window !== "undefined"
            ? window.location.pathname
            : routerLocation.pathname;
        navigateRouter(
          { pathname: toCreatorBrowserPathname(rawPath, username), search },
          { replace: true },
        );
      }
    },
    [
      onNavigate,
      templateAnalytics,
      navigateRouter,
      routerLocation.pathname,
      routerLocation.search,
      catalogPageAbsent,
      username,
      shopBrowse.primarySlug,
    ],
  );

  const clearContentCarousel = React.useCallback(() => {
    setActiveCarouselSlug(null);
    setShopBrowse({});
    templateAnalytics?.trackContentTab({
      slug: "clear",
      label: "Clear content collection",
      contentView: "latest",
      sourcePageKey: "content",
    });
    if (catalogPageAbsent) {
      onNavigate?.("home");
      return;
    }
    onNavigate?.("content", {});
  }, [onNavigate, templateAnalytics, catalogPageAbsent]);

  const { get: getContentEdit } = useEdit();
  const activeContentCarousel = React.useMemo(() => {
    if (!activeCarouselSlug) return null;
    // Platform path segments must never become phantom collection feeds.
    if (isContentPlatformBrowseSlug(activeCarouselSlug)) return null;
    const car = findCarouselBySlug(CREATOR.carousels, activeCarouselSlug);
    if (car) return car;
    const pick = findRecentContentPick(CREATOR.carousels, activeCarouselSlug);
    if (pick?.carousel) return pick.carousel;
    return {
      title: activeCarouselSlug,
      renderSlug: activeCarouselSlug,
      type: "content" as const,
    };
  }, [activeCarouselSlug, CREATOR.carousels]);

  const contentCarouselFromCreator = Boolean(
    activeCarouselSlug &&
      (findCarouselBySlug(CREATOR.carousels, activeCarouselSlug) ||
        findRecentContentPick(CREATOR.carousels, activeCarouselSlug)?.carousel),
  );
  const libContentCollectionRow: any = useLoadRenderComponent(
    userId,
    !catalogEnabled || contentCarouselFromCreator
      ? ""
      : activeCarouselSlug || "",
    undefined,
    supabasePublic,
    username,
  );

  // Recover if a platform slug was previously mis-opened as a collection.
  React.useEffect(() => {
    if (!activeCarouselSlug || !isContentPlatformBrowseSlug(activeCarouselSlug)) {
      return;
    }
    const slug = activeCarouselSlug;
    setActiveCarouselSlug(null);
    setShopBrowse({
      primarySlug: slug,
      secondarySlug: null,
      brandSlug: null,
    });
  }, [activeCarouselSlug]);

  const activeContentCarouselTitle = activeContentCarousel
    ? titleUnlessSlug(
        carouselTabDisplayTitle(getContentEdit, {
          title: String(
            contentCarouselFromCreator
              ? activeContentCarousel.title || ""
              : String(libContentCollectionRow?.data?.name || "").trim(),
          ),
          stayComponentRowId: (activeContentCarousel as CarouselData)
            .stayComponentRowId,
        }),
        activeCarouselSlug,
      )
    : "";

  const shellCfg = cfg ? { ...cfg, heading: undefined } : cfg;

  const platformSlug = (shopBrowse.primarySlug ?? "").trim().toLowerCase();
  const formatSlug = (shopBrowse.secondarySlug ?? "").trim().toLowerCase();

  // Only connected platforms (bio.connectedPlatforms) — not Other / MISC-content.
  // Filter client-side so the facets query key stays stable across navigation.
  const visiblePlatforms = React.useMemo(
    () =>
      contentIndex.platforms
        .filter((p) =>
          isContentBrowsePlatformVisible(
            p.slug,
            CREATOR.bio?.social,
            CREATOR.bio?.connectedPlatforms,
          ),
        )
        .slice()
        .sort((a, b) => compareContentPlatformButtons(a.slug, b.slug)),
    [
      contentIndex.platforms,
      CREATOR.bio?.social,
      CREATOR.bio?.connectedPlatforms,
    ],
  );

  const platformItem = visiblePlatforms.find((p) => p.slug === platformSlug);
  const formats = platformSlug
    ? contentIndex.formatsByPlatform[platformSlug] ?? []
    : [];
  const formatItem = formats.find((f) => f.slug === formatSlug);

  // Deep-link / stale browse into a disconnected platform or Other → clear to
  // all content. Product category slugs share previewShopBrowse — clear locally
  // only (do not onNavigate to /content and steal the Products page).
  React.useEffect(() => {
    if (!platformSlug || !isContentFacetsSettled) return;
    if (visiblePlatforms.some((p) => p.slug === platformSlug)) return;
    if (!isContentPlatformBrowseSlug(platformSlug)) {
      setShopBrowse({});
      return;
    }
    navigateBrowse({}, { level: "clear", label: "All content" });
  }, [
    platformSlug,
    visiblePlatforms,
    isContentFacetsSettled,
    navigateBrowse,
  ]);

  const contentCategoriesReady =
    hasSession &&
    (isContentFacetsSettled || visiblePlatforms.length > 0);
  const contentBrowseRiseSettled = useOneTimeRise(
    "content-browse",
    isActive,
    contentBrowseToken,
    contentCategoriesReady,
  );

  const browseChrome = (() => {
    if (!hasSession) return null;

    // Collection deep-link: keep the collection crumb and still show platforms
    // like a normal drill-in (mirrors product carousel / trending).
    if (activeContentCarousel) {
      const crumbs: ShopBrowseNavCrumb[] = activeContentCarouselTitle
        ? [
            {
              label: activeContentCarouselTitle,
              onClear: clearContentCarousel,
            },
          ]
        : [];
      const options: ShopBrowseNavOption[] = [];
      // No Content page → platform chips would navigate to a broken empty content page.
      if (!catalogPageAbsent) {
        for (const p of visiblePlatforms) {
          options.push({
            label: p.label,
            onClick: () =>
              navigateBrowse(
                { primarySlug: p.slug, secondarySlug: null, brandSlug: null },
                { level: "primary", label: p.label },
              ),
          });
        }
      }
      return (
        <div className="mte-tpl-shop-browse-enter-wrap">
          <ShopBrowseNav crumbs={crumbs} options={options} isMobile={isMobile} riseSettled={contentBrowseRiseSettled} />
        </div>
      );
    }

    // Facets are a stable query — don't skeleton the platform ribbon on grid refetch.
    if (!isContentFacetsSettled && visiblePlatforms.length === 0) {
      return <ShopBrowseNavSkeleton />;
    }

    const crumbs: ShopBrowseNavCrumb[] = [];
    const options: ShopBrowseNavOption[] = [];
    const panelItems: { label: string; onClick: () => void }[] = [];

    if (platformItem) {
      crumbs.push({
        label: platformItem.label,
        onClear: () =>
          navigateBrowse({}, { level: "clear", label: "All content" }),
      });
    }
    if (formatItem) {
      crumbs.push({
        label: formatItem.label,
        onClear: () =>
          navigateBrowse(
            { primarySlug: platformSlug, secondarySlug: null, brandSlug: null },
            { level: "clear", label: "Clear format" },
          ),
      });
    }

    if (!platformSlug) {
      for (const p of visiblePlatforms) {
        const item = {
          label: p.label,
          onClick: () =>
            navigateBrowse(
              { primarySlug: p.slug, secondarySlug: null, brandSlug: null },
              { level: "primary", label: p.label },
            ),
        };
        options.push(item);
        panelItems.push(item);
      }
    } else if (!formatSlug && formats.length > 1) {
      for (const f of formats) {
        options.push({
          label: f.label,
          onClick: () =>
            navigateBrowse(
              {
                primarySlug: platformSlug,
                secondarySlug: f.slug,
                brandSlug: null,
              },
              { level: "secondary", label: f.label },
            ),
        });
      }
    }

    const drilledIn = crumbs.length > 0;
    if (!drilledIn && panelItems.length > 0) {
      return (
        <div className="mte-tpl-shop-browse-enter-wrap">
          <ShopBrowsePanel
            items={panelItems}
            cols={4}
            device={T.device}
            display="row"
            riseSettled={contentBrowseRiseSettled}
          />
        </div>
      );
    }

    if (!crumbs.length && !options.length) return null;
    return (
      <div className="mte-tpl-shop-browse-enter-wrap">
        <ShopBrowseNav crumbs={crumbs} options={options} isMobile={isMobile} riseSettled={contentBrowseRiseSettled} />
      </div>
    );
  })();

  // Content / Shoppable only makes sense after drilling into a platform —
  // at the all-platforms root it duplicates the browse chrome.
  // Hide the whole row when this platform has no shoppable posts.
  const showShoppableFilter =
    Boolean(platformSlug) && !activeContentCarousel && hasShoppableContent;
  // Looks bands are an entry point — hide once the follower drills into a
  // platform or collection, but stay mounted so breadcrumb round-trips don't reload them.
  const showLooks = !platformSlug && !activeContentCarousel;
  const contentCatalogLabel = activeContentCarousel
    ? activeContentCarouselTitle
    : formatItem?.label || platformItem?.label || "All content";
  const contentCatalogContext = Boolean(platformSlug || activeContentCarousel);

  const onLoadMoreContent = React.useCallback(() => {
    if (!catalogEnabled || deferContentGrid) return;
    void fetchNextContentPage();
  }, [catalogEnabled, deferContentGrid, fetchNextContentPage]);

  return (
    <Shell T={T} cfg={shellCfg}>
      <ContentCatalogDeferProvider
        onAllLooksReady={reportLooksFirstPageReady}
      >
      <div data-website-region="content-region">
        {browseChrome}
        {showShoppableFilter ? (
          <ShopTextFilterRow
            ariaLabel="Shoppable filter"
            value={shoppable}
            onChange={(v) => updateShoppable(v === "yes" ? "yes" : "all")}
            riseSettled={contentBrowseRiseSettled}
            items={[
              { value: "all", label: "Content" },
              { value: "yes", label: "Shoppable content" },
            ]}
          />
        ) : null}
        <ContentLooksInsertSlot hidden={!showLooks} active={isActive} />
        {activeContentCarousel ? (
          <div className="mte-tpl-shop-catalog-block" ref={catalogBlockRef}>
            {contentCatalogLabel ? (
            <div className="mte-tpl-shop-catalog-head">
              <p className="mte-tpl-shop-catalog-label mte-tpl-shop-catalog-label--context">
                {contentCatalogLabel}
              </p>
            </div>
            ) : null}
            <TemplateCollectionCatalogPanel
              userId={userId}
              username={username}
              renderSlug={
                String(
                  activeContentCarousel.renderSlug || activeCarouselSlug || "",
                ).trim()
              }
              T={T}
              pageVariant="content"
              cols={cols}
              aspect={aspect}
              buttonLabel={buttonLabel}
              contentTabLabel={contentCatalogLabel}
              enabled={catalogEnabled}
              carousel={
                contentCarouselFromCreator
                  ? (activeContentCarousel as CarouselData)
                  : null
              }
            />
          </div>
        ) : (
          <div className="mte-tpl-shop-catalog-block" ref={catalogBlockRef}>
            <div className="mte-tpl-shop-catalog-head">
              <p
                className={`mte-tpl-shop-catalog-label${
                  contentCatalogContext
                    ? " mte-tpl-shop-catalog-label--context"
                    : ""
                }`}
              >
                {contentCatalogLabel}
              </p>
            </div>
            <ShopContentCatalogPanel
              catalogKey={`content-${platformSlug}-${formatSlug}-${shoppable}`}
              T={T}
              items={contentItems}
              loading={contentLoading || deferContentGrid}
              userId={userId}
              username={username}
              cols={cols}
              aspect={aspect}
              buttonLabel={buttonLabel}
              hasMore={catalogEnabled && !deferContentGrid && hasNextContentPage}
              loadingMore={
                catalogEnabled && !deferContentGrid && isFetchingNextContentPage
              }
              refreshing={contentRefreshing}
              onLoadMore={
                catalogEnabled && !deferContentGrid
                  ? onLoadMoreContent
                  : undefined
              }
            />
          </div>
        )}
      </div>
      </ContentCatalogDeferProvider>
    </Shell>
  );
}
ContentGrid.options = {
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

// ── Platform grid (homepage highlight deep links — mirrors ContentGrid) ──
export function PlatformHighlightGrid({
  T,
  cfg,
  onNavigate,
  platformDeepLink,
  onPlatformDeepLinkConsumed,
  isActive = true,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  // Page title lives on the separate PageHeader section.
  const shellCfg = cfg ? { ...cfg, heading: undefined } : cfg;

  return (
    <Shell T={T} cfg={shellCfg}>
      <TemplatePlatformHighlightFeed
        T={T}
        userId={userId}
        username={username}
        highlights={CREATOR.highlights}
        onNavigate={onNavigate}
        platformDeepLink={platformDeepLink}
        onPlatformDeepLinkConsumed={onPlatformDeepLinkConsumed}
        contentShape="square"
        sourcePageKey="platform"
        embedVariant="platform"
        isActive={isActive}
      />
    </Shell>
  );
}
PlatformHighlightGrid.options = {
  count: {
    kind: "number",
    label: "Visible items",
    min: 4,
    max: 60,
    default: 12,
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

// ── Shop filters bar (mirrors ProductGrid category pills) ───────
export function ShopFilters({ T, cfg, onNavigate }: SectionProps) {
  const CREATOR = useCreatorData();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);

  const { categoryTabs, hasAllProducts, hasTrending } = useShopCatalog({
    username,
    userId,
    enabled: hasSession,
    shopView: "all",
    categorySlug: null,
    shopBrowse: {},
    surface: "meta",
  });

  const cats = hasSession
    ? [
        ...(hasAllProducts ? ["All products"] : []),
        ...(hasTrending ? ["Trending products"] : []),
        ...categoryTabs.map((c) => c.label),
      ]
    : [
        "All",
        "Outerwear",
        "Bottoms",
        "Footwear",
        "Dresses",
        "Bags",
        "Knitwear",
      ];
  const style = (cfg?.style as string) || "chips";
  const showSort = cfg?.showSort === true;
  if (style === "dropdown")
    return (
      <Shell T={T} cfg={cfg} noTopPad>
        <div className="mte-tpl-sf-bar">
          <div className="mte-tpl-sf-row">
            <span className="mte-tpl-sf-label">Collection</span>
            <select className="mte-tpl-sf-select">
              {cats.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          {showSort && <div className="mte-tpl-sf-sort">Sort: Newest ▾</div>}
        </div>
      </Shell>
    );
  return (
    <Shell T={T} cfg={cfg} noTopPad>
      <div className="mte-tpl-sf-bar mte-tpl-sf-bar--wrap">
        <div className="mte-tpl-sf-chips">
          {cats.map((c, i) => (
            <a
              key={c}
              href="#"
              className={
                i === 0
                  ? "mte-tpl-sf-chip mte-tpl-sf-chip--active"
                  : "mte-tpl-sf-chip"
              }
              onClick={(e) => {
                if (!onNavigate || !hasSession) return;
                e.preventDefault();
                if (c === "All products" || c === "All") {
                  templateAnalytics?.trackShopTab({ tabKind: "all", label: c });
                  onNavigate("shop", { shopView: "all" });
                } else if (c === "Trending products") {
                  templateAnalytics?.trackShopTab({ tabKind: "trending", label: c });
                  onNavigate("shop", { shopView: "trending" });
                } else {
                  const tab = categoryTabs.find((t) => t.label === c);
                  if (tab) {
                    templateAnalytics?.trackShopTab({ tabKind: "category", label: c, categorySlug: tab.slug });
                    onNavigate("shop", {
                      shopView: "category",
                      categorySlug: tab.slug,
                    });
                  }
                }
              }}
            >
              {c}
            </a>
          ))}
        </div>
        {showSort && (
          <div className="mte-tpl-sf-sort mte-tpl-sf-sort--shrink">
            Sort: Newest ▾
          </div>
        )}
      </div>
    </Shell>
  );
}
ShopFilters.options = {
  style: {
    kind: "radio",
    label: "Style",
    options: ["chips", "dropdown"],
    default: "chips",
  },
  showSort: { kind: "toggle", label: "Show sort", default: false },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

// ── Page header ────────────────────────────────────────────────
interface PageHeaderProps extends SectionProps {
  eyebrow?: string;
  heading?: string;
  lead?: string;
}
export function PageHeader({
  T,
  cfg,
  eyebrow,
  heading,
  lead,
}: PageHeaderProps) {
  const sectionPath = useTemplateSectionInstance();
  const pageHeaderClass = sectionPathClassParts(sectionPath);
  const { editable, get } = useEdit();
  const CREATOR = useCreatorData();
  const headerSlug = (() => {
    const fromHeading = String(heading || "").trim();
    const source =
      fromHeading ||
      String(sectionPath?.split("/")[1] || "page").trim() ||
      "page";
    return source.replace(/\s+/g, "_").toLowerCase() || "page";
  })();
  const pageTitlePath = `pageHeader.${headerSlug}.heading`;
  const pageTitleDefault = String(cfg?.heading || heading || "");
  const pageLeadPath = `pageHeader.${headerSlug}.lead`;

  // Contact: short name-based line when the creator hasn't set Subtext.
  // About: registry lead only ("Get to know me") — never mirror about.bio
  // (that copy belongs in AboutStory; mirroring duplicated it under the title
  // and broke title→subtitle spacing vs Contact).
  // Inspector / contentEdits `cfg.lead` wins once the creator has set it.
  const pageKey = sectionPath?.split("/")[0];
  const resolvedLead = (() => {
    if (typeof cfg?.lead === "string") return cfg.lead;
    if (pageKey === "contact") {
      const name =
        CREATOR.about?.profileName?.trim() ||
        CREATOR.bio?.name?.trim() ||
        "";
      return name ? `Get in touch with ${name}` : (lead || "");
    }
    return lead || "";
  })();

  const liveTitle = String(get(pageTitlePath, pageTitleDefault) || "").trim();
  const liveLead = String(get(pageLeadPath, resolvedLead) || "").trim();

  // Strip `heading` from cfg so Shell doesn't render its own section-title
  // row — PageHeader already renders its own <h1> and lead inline.
  const shellCfg = cfg ? { ...cfg, heading: undefined } : cfg;

  return (
    <Shell T={T} cfg={shellCfg}>
      <div
        className={[
          "mte-tpl-pgh-wrap",
          `mte-tpl-pgh-wrap--${pageHeaderClass.combo}`,
        ].join(" ")}
      >
        {eyebrow ? (
          <div
            className={[
              "mte-tpl-pgh-eyebrow",
              `mte-tpl-pgh-eyebrow--${pageHeaderClass.combo}`,
            ].join(" ")}
          >
            {eyebrow}
          </div>
        ) : null}
        {/* Editor: keep title/subtitle rows so an empty header stays selectable. */}
        {editable || liveTitle ? (
        <h1
          className={[
            "mte-tpl-pgh-h1",
            `mte-tpl-pgh-h1--${pageHeaderClass.combo}`,
          ].join(" ")}
        >
          <E path={pageTitlePath} placeholder="Add page title">
            {pageTitleDefault}
          </E>
        </h1>
        ) : null}
        {editable || liveLead ? (
          <p
            className={[
              "mte-tpl-pgh-lead",
              `mte-tpl-pgh-lead--${pageHeaderClass.combo}`,
            ].join(" ")}
          >
            <E
              path={pageLeadPath}
              multiline
              placeholder="Add subtitle"
            >
              {resolvedLead}
            </E>
          </p>
        ) : null}
      </div>
    </Shell>
  );
}
PageHeader.options = {
  heading: { kind: "text", label: "Heading", default: "" },
  lead: {
    kind: "textarea",
    label: "Subtext",
    placeholder: "Optional supporting line",
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;

function journalPostHrefAndClick(
  p: JournalPost,
  onNavigate: TemplateNavigateFn | undefined,
  blogPostHrefForSlug: ((s: string) => string) | undefined,
  onJournalTrack?: (post: JournalPost) => void,
): { href: string; onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void } {
  const slug = p.slug || p.id;
  const href = blogPostHrefForSlug ? blogPostHrefForSlug(slug) : "#";
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onJournalTrack?.(p);
    if (!onNavigate) return;
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    onNavigate("journalPost", { postSlugOrId: slug });
  };
  return { href, onClick };
}

function visibleJournalPosts(
  journal: JournalPost[],
  editable: boolean,
  limit?: number,
): JournalPost[] {
  const base = editable
    ? journal
    : journal.filter((p) => isPublishedBlogStatus(p.status));
  if (limit == null || limit >= base.length) return base;
  return base.slice(0, limit);
}

const JOURNAL_PAGE_SIZE_DEFAULT = 24;

function journalPageSize(cfg?: SectionCfg | null): number {
  const n = Number(cfg?.count);
  if (!Number.isFinite(n)) return JOURNAL_PAGE_SIZE_DEFAULT;
  return Math.min(100, Math.max(1, Math.floor(n)));
}

/** Normalize legacy `editorial` → `list`; unknown → `list`. */
function journalGridStyle(cfg?: SectionCfg | null): "list" | "grid" | "magazine" {
  const raw = String(cfg?.style || "list");
  if (raw === "grid" || raw === "magazine") return raw;
  // `editorial` was the old polished list; mockup renamed it to `list`.
  return "list";
}

function JournalPostMetaRow({
  date,
  minutes,
  showDate,
  className,
}: {
  date?: string | null;
  minutes?: number | null;
  showDate: boolean;
  className?: string;
}) {
  const meta =
    showDate && (date || minutes != null)
      ? `${date || ""}${
          date && minutes != null ? " · " : ""
        }${minutes != null ? `${minutes} MIN READ` : ""}`.trim()
      : "";
  if (!meta) return null;
  return (
    <div className={className || "mte-tpl-jrnl-meta-row"}>
      <div className="mte-tpl-jrnl-meta">{meta}</div>
    </div>
  );
}

/** Posts per page by layout — grid/magazine match the mockup (3-up). */
function journalLayoutPageSize(
  style: "list" | "grid" | "magazine",
  visibleCap: number,
): number {
  if (style === "grid" || style === "magazine") return 3;
  return Math.min(Math.max(1, visibleCap), 12);
}

function JournalGridPager({
  page,
  totalPages,
  rangeStart,
  rangeEnd,
  totalCount,
  onChange,
}: {
  page: number;
  totalPages: number;
  /** 1-based index of first post on this page. */
  rangeStart: number;
  /** 1-based index of last post on this page. */
  rangeEnd: number;
  totalCount: number;
  onChange: (next: number) => void;
}) {
  if (totalCount <= 0 || rangeEnd < rangeStart) return null;
  const canPaginate = totalPages > 1;
  const rangeLabel =
    rangeStart === rangeEnd
      ? `${rangeStart}`
      : `${rangeStart}–${rangeEnd}`;
  return (
    <nav className="mte-tpl-jrnl-pager" aria-label="Blog posts pages">
      {canPaginate ? (
        <button
          type="button"
          className="mte-tpl-jrnl-pager-btn"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onChange(Math.max(1, page - 1))}
        >
          ←
        </button>
      ) : null}
      <span className="mte-tpl-jrnl-pager-status">
        Showing {rangeLabel} of {totalCount} posts
      </span>
      {canPaginate ? (
        <button
          type="button"
          className="mte-tpl-jrnl-pager-btn"
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onChange(Math.min(totalPages, page + 1))}
        >
          →
        </button>
      ) : null}
    </nav>
  );
}

function journalPostMetaLine(
  p: JournalPost,
  showMeta: boolean,
  showDate: boolean,
): string {
  const parts: string[] = [];
  if (showMeta && p.minutes != null) parts.push(`${p.minutes} min`);
  if (showDate && p.date) parts.push(String(p.date));
  return parts.join(" · ");
}

function JournalMorePostsList({
  posts,
  currentPostId,
  onNavigate,
  blogPostHrefForSlug,
  compact = false,
}: {
  posts: JournalPost[];
  currentPostId?: string;
  onNavigate?: TemplateNavigateFn;
  blogPostHrefForSlug?: (slug: string) => string;
  compact?: boolean;
}) {
  const { editable } = useEdit();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const others = visibleJournalPosts(posts, editable, 100).filter(
    (p) => String(p.id) !== String(currentPostId || ""),
  );
  if (!others.length || !onNavigate) return null;
  const trackJournal = (post: JournalPost) => {
    templateAnalytics?.trackJournalPost({
      postId: post.id,
      slug: post.slug || post.id,
      title: post.title,
    });
  };
  return (
    <div
      className={
        compact
          ? "mte-tpl-jrnl-more mte-tpl-jrnl-more--compact"
          : "mte-tpl-jrnl-more"
      }
    >
      <div className="mte-tpl-jrnl-more-label">More posts</div>
      <div className="mte-tpl-jrnl-editorial mte-tpl-jrnl-editorial--more">
        {others.map((p) => {
          const { href, onClick } = journalPostHrefAndClick(
            p,
            onNavigate,
            blogPostHrefForSlug,
            trackJournal,
          );
          const postImg = getJournalPostHeroImage(p);
          const meta = journalPostMetaLine(p, true, false);
          return (
            <a
              key={p.id}
              href={href}
              onClick={onClick}
              className="mte-tpl-jrnl-editorial-row mte-tpl-jrnl-editorial-row--more"
            >
              <div className="mte-tpl-jrnl-editorial-thumb-wrap">
                {postImg ? (
                  <img
                    src={postImg}
                    alt=""
                    className="mte-tpl-jrnl-editorial-thumb mte-tpl-jrnl-editorial-thumb--more mte-tpl-img-filter"
                  />
                ) : (
                  <div
                    className="mte-tpl-jrnl-editorial-thumb mte-tpl-jrnl-editorial-thumb--more mte-tpl-jrnl-editorial-thumb--empty"
                    aria-hidden
                  />
                )}
              </div>
              <div className="mte-tpl-jrnl-editorial-body">
                {meta ? (
                  <div className="mte-tpl-jrnl-editorial-date">{meta}</div>
                ) : null}
                <JournalDraftBadge post={p} />
                <div className="mte-tpl-jrnl-editorial-title mte-tpl-jrnl-editorial-title--more">
                  {p.title}
                </div>
              </div>
              <span className="mte-tpl-jrnl-editorial-read">READ →</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function JournalImage({
  src,
  show,
  variant,
}: {
  src: string;
  show: boolean;
  variant: "43" | "45";
}) {
  if (!show) return null;
  const imgCls =
    variant === "43"
      ? "mte-tpl-img-filter mte-tpl-jrnl-img-43"
      : "mte-tpl-img-filter mte-tpl-jrnl-img-45";
  if (src) return <img src={src} alt="" className={imgCls} />;
  return (
    <div
      className={`${imgCls} mte-tpl-jrnl-img-ph`}
      aria-hidden
    />
  );
}

// ── Journal grid ───────────────────────────────────────────────
export function JournalGrid({
  T,
  cfg,
  onNavigate,
  blogPostHrefForSlug,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const isLiveLoading = useCreatorDataLoading();
  const isJournalLoading = useCreatorJournalLoading();
  const isLoading = isLiveLoading || isJournalLoading;
  const { editable } = useEdit();
  const inspectorCtx = useWebsiteEditorInspectorOptional();
  const userId = CREATOR.bio?.userId || "";
  const onJournalPatched = inspectorCtx?.patchJournalPost;
  const templateAnalytics = useTemplateAnalyticsOptional();
  const trackJournal = React.useCallback(
    (post: JournalPost) => {
      templateAnalytics?.trackJournalPost({
        postId: post.id,
        slug: post.slug || post.id,
        title: post.title,
      });
    },
    [templateAnalytics],
  );
  const allItems = visibleJournalPosts(CREATOR.journal, editable);
  // Stay on skeletons while live data / journal is in flight — never flash the empty
  // copy between loaders and arriving posts.
  const showEmpty = !isLoading && allItems.length === 0;
  const showSkeleton = isLoading && allItems.length === 0;
  const style = journalGridStyle(cfg);
  const visibleCap = journalPageSize(cfg);
  const pool = allItems.slice(0, visibleCap);
  const pageSize = journalLayoutPageSize(style, visibleCap);
  const [page, setPage] = React.useState(1);
  const pageNavRef = React.useRef(false);
  const totalPages = Math.max(1, Math.ceil(pool.length / pageSize));
  React.useEffect(() => {
    setPage((prev) => Math.min(Math.max(1, prev), totalPages));
  }, [totalPages]);
  React.useEffect(() => {
    setPage(1);
  }, [CREATOR.journal.length, pageSize, visibleCap, style]);
  React.useEffect(() => {
    if (!pageNavRef.current) return;
    pageNavRef.current = false;
    const root = document.getElementById("master-template-scroll-root");
    if (root) {
      root.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [page]);
  const items = pool.slice((page - 1) * pageSize, page * pageSize);
  const rangeStart = items.length ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = items.length ? rangeStart + items.length - 1 : 0;
  const pager = (
    <JournalGridPager
      page={page}
      totalPages={totalPages}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      totalCount={pool.length}
      onChange={(next) => {
        if (next === page) return;
        pageNavRef.current = true;
        setPage(next);
      }}
    />
  );
  const showImage = cfg?.showImage !== false;
  const showDate = cfg?.showDate !== false;
  const showExcerpt = cfg?.showExcerpt !== false;
  // Section heading is optional (PageHeader owns the page title); keep cfg.heading.
  const shellCfg = cfg;

  if (showSkeleton) {
    return (
      <Shell
        T={T}
        cfg={shellCfg}
        independentShellClassName="mte-tpl-journal-grid-shell"
      >
        {/* Use the exact same container + row classes as the real list so
            grid columns, padding, border-top, and gap all match precisely. */}
        <div className="mte-tpl-jrnl-list" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className={
                style === "list"
                  ? "mte-tpl-jrnl-editorial-row mte-tpl-jrnl-skel-row"
                  : showImage
                    ? "mte-tpl-jrnl-list-row mte-tpl-jrnl-skel-row"
                    : "mte-tpl-jrnl-list-row mte-tpl-jrnl-list-row--noimg mte-tpl-jrnl-skel-row"
              }
            >
              {showImage && style === "list" ? (
                <div className="mte-tpl-jrnl-editorial-thumb-wrap">
                  <div className="mte-tpl-jrnl-skel-img mte-tpl-jrnl-editorial-thumb" />
                </div>
              ) : showImage ? (
                <div className="mte-tpl-jrnl-skel-img" />
              ) : null}
              <div className={style === "list" ? "mte-tpl-jrnl-editorial-body" : undefined}>
                <div className="mte-tpl-jrnl-skel-text">
                  <div className="mte-tpl-jrnl-skel-meta" />
                  <div className="mte-tpl-jrnl-skel-title" />
                  <div className="mte-tpl-jrnl-skel-excerpt" />
                  <div className="mte-tpl-jrnl-skel-excerpt mte-tpl-jrnl-skel-excerpt--short" />
                </div>
              </div>
              <div className="mte-tpl-jrnl-skel-read" />
            </div>
          ))}
        </div>
      </Shell>
    );
  }
  if (showEmpty) {
    return (
      <Shell
        T={T}
        cfg={shellCfg}
        independentShellClassName="mte-tpl-journal-grid-shell"
      >
        {editable ? (
          <BlogEditorEmptyState />
        ) : (
          <p className="mte-tpl-muted-p mte-tpl-journal-empty" role="status">
            No blog posts yet. Check back soon for new stories.
          </p>
        )}
      </Shell>
    );
  }

  // `list` uses the polished editorial row layout (thumbs + READ).
  // One <a> per row (READ is a span) — a second READ <a> with the same
  // onClick was double-firing Journal Post Click in Blog analytics.
  if (style === "list")
    return (
      <Shell
        T={T}
        cfg={shellCfg}
        independentShellClassName="mte-tpl-journal-grid-shell"
      >
        <div className="mte-tpl-jrnl-editorial mte-tpl-grid-enter">
          {items.map((p) => {
            const { href, onClick } = journalPostHrefAndClick(
              p,
              onNavigate,
              blogPostHrefForSlug,
              trackJournal,
            );
            const postImg = getJournalPostHeroImage(p);
            const main = (
              <>
                {showImage ? (
                  <div className="mte-tpl-jrnl-editorial-thumb-wrap">
                    {postImg ? (
                      <img
                        src={postImg}
                        alt=""
                        className="mte-tpl-jrnl-editorial-thumb mte-tpl-img-filter"
                      />
                    ) : (
                      <div
                        className="mte-tpl-jrnl-editorial-thumb mte-tpl-jrnl-editorial-thumb--empty"
                        aria-hidden
                      />
                    )}
                  </div>
                ) : null}
                <div className="mte-tpl-jrnl-editorial-body">
                  <JournalPostMetaRow
                    date={p.date}
                    minutes={p.minutes}
                    showDate={showDate}
                  />
                  <JournalDraftBadge post={p} />
                  <h3 className="mte-tpl-jrnl-editorial-title">{p.title}</h3>
                  {showExcerpt ? (
                    <p className="mte-tpl-jrnl-editorial-excerpt">{p.excerpt}</p>
                  ) : null}
                </div>
              </>
            );
            // Publish controls need a sibling (not nested in <a>); public rows
            // use a single link so READ cannot fire a second track.
            if (editable) {
              return (
                <div key={p.id} className="mte-tpl-jrnl-editorial-row">
                  <a
                    href={href}
                    onClick={onClick}
                    className="mte-tpl-jrnl-editorial-row-main"
                  >
                    {main}
                  </a>
                  <JournalGridRowAside
                    post={p}
                    userId={userId}
                    username={CREATOR.bio?.username}
                    onPatched={onJournalPatched}
                  />
                </div>
              );
            }
            return (
              <a
                key={p.id}
                href={href}
                onClick={onClick}
                className="mte-tpl-jrnl-editorial-row"
              >
                {main}
                <span className="mte-tpl-jrnl-editorial-read">READ →</span>
              </a>
            );
          })}
        </div>
        {pager}
      </Shell>
    );

  if (style === "magazine") {
    const [hero, ...side] = items;
    const heroLink = hero
      ? journalPostHrefAndClick(hero, onNavigate, blogPostHrefForSlug, trackJournal)
      : null;
    return (
      <Shell
        T={T}
        cfg={shellCfg}
        independentShellClassName="mte-tpl-journal-grid-shell"
      >
        {hero && heroLink ? (
          <div className="mte-tpl-jrnl-mag">
            <a
              href={heroLink.href}
              onClick={heroLink.onClick}
              className="mte-tpl-jrnl-mag-feature"
            >
              <JournalImage
                show={showImage}
                src={getJournalPostHeroImage(hero)}
                variant="43"
              />
              <div className="mte-tpl-jrnl-mag-feature-body">
                <JournalPostMetaRow
                  date={hero.date}
                  minutes={hero.minutes}
                  showDate={showDate}
                />
                <JournalDraftBadge post={hero} />
                <h3 className="mte-tpl-jrnl-hero-h3">{hero.title}</h3>
                {showExcerpt ? (
                  <p className="mte-tpl-jrnl-excerpt-lg">{hero.excerpt}</p>
                ) : null}
              </div>
            </a>
            {side.length > 0 ? (
              <div className="mte-tpl-jrnl-mag-side">
                {side.map((p) => {
                  const { href, onClick } = journalPostHrefAndClick(
                    p,
                    onNavigate,
                    blogPostHrefForSlug,
                    trackJournal,
                  );
                  const postImg = getJournalPostHeroImage(p);
                  return (
                    <a
                      key={p.id}
                      href={href}
                      onClick={onClick}
                      className={
                        showImage
                          ? "mte-tpl-jrnl-mag-side-item"
                          : "mte-tpl-jrnl-mag-side-item mte-tpl-jrnl-mag-side-item--noimg"
                      }
                    >
                      {showImage ? (
                        <div className="mte-tpl-jrnl-mag-side-thumb">
                          {postImg ? (
                            <img
                              src={postImg}
                              alt=""
                              className="mte-tpl-img-filter mte-tpl-jrnl-mag-side-img"
                            />
                          ) : (
                            <div
                              className="mte-tpl-jrnl-mag-side-img mte-tpl-jrnl-mag-side-img--empty"
                              aria-hidden
                            />
                          )}
                        </div>
                      ) : null}
                      <div className="mte-tpl-jrnl-mag-side-body">
                        <JournalPostMetaRow
                          date={p.date}
                          minutes={p.minutes}
                          showDate={showDate}
                        />
                        <JournalDraftBadge post={p} />
                        <h4 className="mte-tpl-jrnl-h4">{p.title}</h4>
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
        {pager}
      </Shell>
    );
  }

  // grid
  return (
    <Shell
      T={T}
      cfg={shellCfg}
      independentShellClassName="mte-tpl-journal-grid-shell"
    >
      <div className="mte-tpl-jrnl-grid-3 mte-tpl-grid-enter">
        {items.map((p) => {
          const { href, onClick } = journalPostHrefAndClick(
            p,
            onNavigate,
            blogPostHrefForSlug,
            trackJournal,
          );
          const postImg = getJournalPostHeroImage(p);
          return (
            <a
              key={p.id}
              href={href}
              onClick={onClick}
              className="mte-tpl-jrnl-card-link"
            >
              {showImage ? (
                <div className="mte-tpl-jrnl-card-frame mte-tpl-jrnl-card-frame--mb16">
                  {postImg ? (
                    <img
                      src={postImg}
                      alt=""
                      className="mte-tpl-img-filter mte-tpl-jrnl-card-img"
                    />
                  ) : (
                    <div
                      className="mte-tpl-jrnl-card-img mte-tpl-jrnl-card-img--empty"
                      aria-hidden
                    />
                  )}
                </div>
              ) : null}
              <JournalPostMetaRow
                date={p.date}
                minutes={p.minutes}
                showDate={showDate}
              />
              <JournalDraftBadge post={p} />
              <h3 className="mte-tpl-jrnl-h3-grid">{p.title}</h3>
              {showExcerpt ? (
                <p className="mte-tpl-jrnl-excerpt-md">{p.excerpt}</p>
              ) : null}
            </a>
          );
        })}
      </div>
      {pager}
    </Shell>
  );
}
JournalGrid.options = {
  style: {
    kind: "radio",
    label: "Layout",
    options: ["list", "grid", "magazine"],
    default: "list",
  },
  count: {
    kind: "number",
    label: "Visible posts",
    min: 1,
    max: 100,
    default: 24,
  },
  showImage: { kind: "toggle", label: "Show image", default: true },
  showDate: { kind: "toggle", label: "Show date / read time", default: true },
  showExcerpt: { kind: "toggle", label: "Show excerpt", default: true },
  heading: { kind: "text", label: "Heading", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

function BlogHeroVideo({
  src,
  poster,
  className,
}: {
  src: string;
  poster?: string;
  className: string;
}) {
  const thumb =
    (poster && !isLikelyVideoUrl(poster) ? poster : "") ||
    "/assets/dummy_video.png";
  return (
    <div className={`${className} mte-tpl-ph-video`.trim()}>
      {/* Inner shell owns absolute fill so outer can keep aspect-ratio sizing. */}
      <div className="mte-tpl-ph-video-inner">
        <CreatorFeedVideoPlayer
          media_url={src}
          thumbnail={thumb}
          isSoundOn
          className="creator-feed-card-video hover-play mte-tpl-fc-hover-video mte-tpl-ph-hover-video"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  );
}

function PostHeroMediaCarousel({
  slots,
  resetKey,
  heroClass,
  overlay,
  firstFrame,
}: {
  slots: { src: string; isVideo: boolean; poster?: string }[];
  resetKey: string;
  heroClass: string;
  overlay?: boolean;
  firstFrame: React.ReactNode;
}) {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    setIdx(0);
  }, [resetKey]);
  const total = slots.length;
  if (total <= 1) return <>{firstFrame}</>;
  const safeIdx = Math.min(idx, total - 1);
  const current = slots[safeIdx];
  const go = (dir: number) =>
    setIdx((i) => (i + dir + total) % total);
  const frame =
    safeIdx === 0 ? (
      firstFrame
    ) : current.isVideo || isLikelyVideoUrl(current.src) ? (
      <BlogHeroVideo
        className={heroClass}
        src={current.src}
        poster={current.poster}
      />
    ) : (
      <img src={current.src} alt="" className={heroClass} />
    );
  return (
    <div
      className={
        overlay
          ? "mte-tpl-ph-carousel mte-tpl-ph-carousel--overlay"
          : "mte-tpl-ph-carousel"
      }
    >
      {frame}
      <button
        type="button"
        className="mte-tpl-ph-carousel-nav mte-tpl-ph-carousel-nav--prev"
        aria-label="Previous media"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          go(-1);
        }}
      >
        ‹
      </button>
      <button
        type="button"
        className="mte-tpl-ph-carousel-nav mte-tpl-ph-carousel-nav--next"
        aria-label="Next media"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          go(1);
        }}
      >
        ›
      </button>
      <div className="mte-tpl-ph-carousel-dots" role="tablist" aria-label="Post media">
        {slots.map((_, i) => (
          <button
            key={`${resetKey}-${i}`}
            type="button"
            role="tab"
            aria-selected={i === safeIdx}
            aria-label={`Media ${i + 1} of ${slots.length}`}
            className={
              i === safeIdx
                ? "mte-tpl-ph-carousel-dot mte-tpl-ph-carousel-dot--on"
                : "mte-tpl-ph-carousel-dot"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIdx(i);
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Post hero ──────────────────────────────────────────────────
export function PostHero({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const { get } = useEdit();
  const isLiveLoading = useCreatorDataLoading();
  const isJournalLoading = useCreatorJournalLoading();
  const isLoading = isLiveLoading || isJournalLoading;
  const activeSlug = useJournalActivePostSlugOrId();
  const heroLayout = (cfg?.style as string) || "split";
  const heroShowExcerpt = cfg?.showExcerpt !== false;
  const heroShowMeta = cfg?.showMeta !== false;
  const heroShowDate = cfg?.showDate !== false;
  React.useEffect(() => {
    publishBlogEmailHeroSettings({
      layout: normalizeBlogEmailLayout(heroLayout),
      showDate: heroShowDate,
      showReadTime: heroShowMeta,
      showExcerpt: heroShowExcerpt,
    });
  }, [heroLayout, heroShowDate, heroShowMeta, heroShowExcerpt]);
  const p = activeSlug
    ? findJournalPostBySlugOrId(CREATOR.journal, activeSlug)
    : CREATOR.journal[0];
  // While journal data is still fetching, show a neutral skeleton instead of
  // a false "post not found" error — the post may simply not have arrived yet.
  if (activeSlug && !p) {
    if (isLoading) {
      return <JournalPostHeroSkeleton T={T} cfg={cfg} />;
    }
    return (
      <Shell T={T} cfg={cfg} independentShellClassName="mte-tpl-post-hero-shell">
        <p className="mte-tpl-muted-p mte-tpl-journal-empty" role="status">
          This post could not be found.
        </p>
      </Shell>
    );
  }
  if (!p) {
    if (isLoading) {
      return <JournalPostHeroSkeleton T={T} cfg={cfg} />;
    }
    return (
      <Shell T={T} cfg={cfg} independentShellClassName="mte-tpl-post-hero-shell">
        <p className="mte-tpl-muted-p mte-tpl-journal-empty" role="status">
          No blogs to show.
        </p>
      </Shell>
    );
  }
  const pid = String(p.id);
  const blogTitle = `blogPost.${pid}.title`;
  const blogExcerpt = `blogPost.${pid}.excerpt`;
  const coverSlot = `blogPost-${pid}-cover`;
  const style = (cfg?.style as string) || "split";
  // Image shape control removed — always use wide cover treatment.
  const mob = T.device === "mobile";
  const showExcerpt = cfg?.showExcerpt !== false;
  const showMeta = cfg?.showMeta !== false;
  const showDate = cfg?.showDate !== false;
  const liveExcerpt = String(get(blogExcerpt, p.excerpt || "") || "").trim();
  const paintExcerpt = showExcerpt && Boolean(liveExcerpt);
  // Meta = read time and/or date independently.
  const metaParts: string[] = [];
  if (showMeta && p.minutes != null) metaParts.push(`${p.minutes} MIN READ`);
  if (showDate && p.date) metaParts.push(String(p.date));
  const meta = metaParts.join(" · ");
  const showMetaRow = metaParts.length > 0;
  const heroImg = getJournalPostHeroImage(p);
  const showAllMedia = cfg?.showAllMedia !== false;
  const mediaLayout = String(cfg?.mediaLayout || "filmstrip");
  const allMediaSlots = journalPostGallerySlots(p);
  const mediaSlots = showAllMedia ? allMediaSlots : allMediaSlots.slice(0, 1);
  const multiGallery =
    mediaSlots.length > 1 &&
    (mediaLayout === "mosaic" ||
      mediaLayout === "filmstrip" ||
      mediaLayout === "squares" ||
      mediaLayout === "stack");
  const galleryBlock = multiGallery ? (
    <PostSourceGallery slots={mediaSlots} layout={mediaLayout} />
  ) : null;
  const firstSlot = mediaSlots[0];
  const coverImg = (cls: string) => {
    if (firstSlot?.isVideo && firstSlot.src && isLikelyVideoUrl(firstSlot.src)) {
      const poster =
        firstSlot.poster ||
        (heroImg && !isLikelyVideoUrl(heroImg) ? heroImg : "") ||
        undefined;
      return (
        <BlogHeroVideo className={cls} src={firstSlot.src} poster={poster} />
      );
    }
    return heroImg && !isLikelyVideoUrl(heroImg) ? (
      <EditableTemplateAsset
        slot={coverSlot}
        src={heroImg}
        className={cls}
        alt=""
      />
    ) : null;
  };
  const wrapHeroMedia = (cls: string, overlay?: boolean) => {
    const first = coverImg(cls);
    if (!first) return null;
    return (
      <PostHeroMediaCarousel
        slots={mediaSlots.length ? mediaSlots : [{ src: heroImg, isVideo: false }]}
        resetKey={pid}
        heroClass={cls}
        overlay={overlay}
        firstFrame={first}
      />
    );
  };
  const hasHeroMedia =
    Boolean(heroImg && !isLikelyVideoUrl(heroImg)) ||
    Boolean(firstSlot?.isVideo && firstSlot.src && isLikelyVideoUrl(firstSlot.src));
  const imgEl = hasHeroMedia
    ? wrapHeroMedia("mte-tpl-img-filter mte-tpl-ph-img-wide")
    : null;
  const splitMeta = journalPostMetaLine(p, showMeta, showDate);
  if (style === "split")
    return (
      <Shell
        T={T}
        cfg={cfg}
        fullBleed
        independentShellClassName="mte-tpl-post-hero-shell"
      >
        <div className={`mte-tpl-ph-split${mob ? " mte-tpl-ph-split--mob" : ""}`}>
          <div className="mte-tpl-ph-split-copy">
            <h1 className="mte-tpl-ph-split-h1">
              <E path={blogTitle} placeholder="Add title">{cfg?.heading || p.title}</E>
            </h1>
            {paintExcerpt ? (
              <p className="mte-tpl-ph-split-ex">
                <E path={blogExcerpt} multiline>
                  {p.excerpt}
                </E>
              </p>
            ) : null}
            {splitMeta ? (
              <div className="mte-tpl-ph-split-meta">{splitMeta}</div>
            ) : null}
          </div>
          {(hasHeroMedia || multiGallery) ? (
            <div
              className={`mte-tpl-ph-split-media${
                multiGallery ? " mte-tpl-ph-split-media--gallery" : ""
              }`}
              style={
                !multiGallery && heroImg && !isLikelyVideoUrl(heroImg)
                  ? ({
                      ["--mte-ph-shade" as string]: `url("${heroImg}")`,
                    } as React.CSSProperties)
                  : undefined
              }
            >
              {multiGallery
                ? galleryBlock
                : wrapHeroMedia("mte-tpl-ph-split-cover")}
            </div>
          ) : null}
        </div>
      </Shell>
    );
  if (style === "overlay" && (hasHeroMedia || multiGallery))
    return (
      <Shell
        T={T}
        cfg={cfg}
        fullBleed
        independentShellClassName="mte-tpl-post-hero-shell"
      >
        {hasHeroMedia ? (
          <div className="mte-tpl-ph-ov-wrap">
            {multiGallery
              ? coverImg("mte-tpl-img-filter mte-tpl-ph-ov-img")
              : wrapHeroMedia("mte-tpl-img-filter mte-tpl-ph-ov-img", true)}
            <div className="mte-tpl-ph-ov-grad" />
            <div className="mte-tpl-ph-ov-inner">
              <h1 className="mte-tpl-ph-ov-h1">
                <E path={blogTitle} placeholder="Add title">{cfg?.heading || p.title}</E>
              </h1>
              {paintExcerpt && (
                <p className="mte-tpl-ph-ov-ex">
                  <E path={blogExcerpt} multiline>
                    {p.excerpt}
                  </E>
                </p>
              )}
              {showMetaRow ? (
                <div className="mte-tpl-ph-ov-meta">
                  {meta}
                </div>
            ) : null}
          </div>
        </div>
        ) : null}
        {multiGallery && mediaSlots.length > 1 ? (
          <div className="mte-tpl-ph-gallery-slot">
            <PostSourceGallery slots={mediaSlots.slice(1)} layout={mediaLayout} />
          </div>
        ) : null}
      </Shell>
    );
  if (style === "magazine")
    return (
      <Shell T={T} cfg={cfg} independentShellClassName="mte-tpl-post-hero-shell">
        <div className={`mte-tpl-ph-mag-grid${mob ? " mte-tpl-ph-mag-grid--mob" : ""}`}>
          <div>
            <h1 className="mte-tpl-ph-mag-h1">
              <E path={blogTitle} placeholder="Add title">{cfg?.heading || p.title}</E>
            </h1>
            {paintExcerpt && (
              <p className="mte-tpl-ph-mag-ex">
                <E path={blogExcerpt} multiline>
                  {p.excerpt}
                </E>
              </p>
            )}
            {showMetaRow ? (
              <div className="mte-tpl-ph-mag-meta">
                {meta}
              </div>
            ) : null}
          </div>
          {/* One media host for desktop + mobile — remounting blanked cached covers. */}
          {hasHeroMedia && !multiGallery ? (
            <div className="mte-tpl-ph-mag-aside">
              {wrapHeroMedia("mte-tpl-img-filter mte-tpl-ph-mag-aside-img")}
            </div>
          ) : null}
        </div>
        {galleryBlock ? (
          <div className="mte-tpl-ph-gallery-slot">{galleryBlock}</div>
        ) : null}
      </Shell>
    );
  if (style === "left")
    return (
      <Shell
        T={T}
        cfg={cfg}
        fullBleed
        independentShellClassName="mte-tpl-post-hero-shell"
      >
        <div className="mte-tpl-ph-left-pad">
          <h1 className="mte-tpl-ph-left-h1">
            <E path={blogTitle} placeholder="Add title">{cfg?.heading || p.title}</E>
          </h1>
          {paintExcerpt && (
            <p className="mte-tpl-ph-left-ex">
              <E path={blogExcerpt} multiline>
                {p.excerpt}
              </E>
            </p>
          )}
          {showMetaRow ? (
            <div className="mte-tpl-ph-left-meta">
              {meta}
            </div>
          ) : null}
        </div>
        {(hasHeroMedia || multiGallery) && (
          <div
            className={`mte-tpl-ph-img-slot${
              multiGallery ? " mte-tpl-ph-img-slot--gallery" : ""
            }`}
          >
            {multiGallery ? galleryBlock : imgEl}
          </div>
        )}
      </Shell>
    );
  return (
    <Shell
      T={T}
      cfg={cfg}
      fullBleed
      independentShellClassName="mte-tpl-post-hero-shell"
    >
      <div className="mte-tpl-ph-ctr-pad">
        <h1 className="mte-tpl-ph-ctr-h1">
          <E path={blogTitle} placeholder="Add title">{cfg?.heading || p.title}</E>
        </h1>
        {paintExcerpt && (
          <p className="mte-tpl-ph-ctr-ex">
            <E path={blogExcerpt} multiline>
              {p.excerpt}
            </E>
          </p>
        )}
        {showMetaRow ? (
          <div className="mte-tpl-ph-ctr-meta">
            {meta}
          </div>
        ) : null}
      </div>
      {(hasHeroMedia || multiGallery) && (
        <div
          className={`mte-tpl-ph-img-slot mte-tpl-ph-img-slot--lg${
            multiGallery ? " mte-tpl-ph-img-slot--gallery" : ""
          }`}
        >
          {multiGallery ? galleryBlock : imgEl}
        </div>
      )}
    </Shell>
  );
}
PostHero.options = {
  style: {
    kind: "radio",
    label: "Layout",
    options: ["split", "centered", "left", "magazine", "overlay"],
    default: "split",
  },
  mediaLayout: {
    kind: "radio",
    label: "Images",
    options: [
      { value: "filmstrip", label: "Filmstrip" },
      { value: "mosaic", label: "Mosaic" },
      { value: "squares", label: "Squares" },
      { value: "carousel", label: "Carousel" },
      { value: "stack", label: "Stack" },
    ],
    default: "filmstrip",
  },
  showAllMedia: {
    kind: "toggle",
    label: "Show all post images",
    default: true,
  },
  showDate: { kind: "toggle", label: "Show date", default: true },
  showMeta: { kind: "toggle", label: "Show read time", default: true },
  showExcerpt: { kind: "toggle", label: "Show excerpt", default: true },
  heading: { kind: "text", label: "Title", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

// ── Post body ──────────────────────────────────────────────────
function stripBlogBodyToText(html: string) {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Keep pasted embed iframes. Drop script tags and the resize snippet that is left behind as visible text. */
function blogBodyDisplayHtml(html: string): string {
  return stripOrphanedCarouselResizeScripts(
    String(html || "")
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
      .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
      .replace(/<!doctype[^>]*>/gi, "")
      .replace(/<\/?(?:html|head|body)\b[^>]*>/gi, ""),
  );
}

/** Live HTML post body — drawer RichText + canvas stay on `blogPost.*.body`. */
function BlogBodyHtml({
  path,
  html,
  className,
}: {
  path: string;
  html: string;
  className?: string;
}) {
  const { get, set, editable, inline } = useEdit();
  const stored = get(path, html);
  const display = blogBodyDisplayHtml(stored == null ? html : String(stored));
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = React.useState(false);
  // An iframe-only embed has no text nodes. Don't treat that as an empty post.
  const isEmpty =
    !stripBlogBodyToText(display) &&
    !/<(?:iframe|img|video|embed|object)\b/i.test(display);

  React.useEffect(() => {
    if (!ref.current || focused) return;
    if (document.activeElement === ref.current) return;
    ref.current.innerHTML = display || "";
  }, [display, focused]);

  // Component embeds report their height. Without this the iframe stays at the
  // browser default and clips the carousel.
  React.useEffect(() => listenForSbEmbedSize(ref.current), [display]);

  if (!editable || !inline || !path) {
    if (isEmpty) return null;
    return (
      <div
        ref={ref}
        className={className}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: display }}
      />
    );
  }

  if (isEmpty && !focused) return null;

  return (
    <div
      ref={ref}
      className={`${className || ""} mte-schema-rich-editable`}
      contentEditable
      suppressContentEditableWarning
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const next = ref.current?.innerHTML || "";
        if (next === html) set(path, null);
        else set(path, next);
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}

export function PostBody({ T, cfg, onNavigate, blogPostHrefForSlug }: SectionProps) {
  const { editable, get } = useEdit();
  const CREATOR = useCreatorData();
  const isLiveLoading = useCreatorDataLoading();
  const isJournalLoading = useCreatorJournalLoading();
  const isLoading = isLiveLoading || isJournalLoading;
  const templateAnalytics = useTemplateAnalyticsOptional();
  const inspectorCtx = useWebsiteEditorInspectorOptional();
  const activeSlug = useJournalActivePostSlugOrId();
  const p = activeSlug
    ? findJournalPostBySlugOrId(CREATOR.journal, activeSlug)
    : CREATOR.journal[0];
  const fontScale = (cfg?.fontScale as string) || "standard";
  const bodyFont =
    fontScale === "compact"
      ? "clamp(15px, 2.1cqi, 17px)"
      : fontScale === "generous"
        ? "clamp(18px, 2.6cqi, 23px)"
        : "clamp(17px, 2.4cqi, 21px)";
  const bodyLayout = (cfg?.bodyLayout as string) || "editorial";
  const editorialBody = bodyLayout === "editorial";
  const maxW = Number(cfg?.maxWidth) || 680;
  const mode = (cfg?.contentMode as string) || "auto";
  const showBack = cfg?.showBackLink === true;
  const showMorePosts = cfg?.showMorePosts !== false;
  const pidEarly = p ? String(p.id) : "";
  const liveBody = String(
    get(`blogPost.${pidEarly}.body`, p?.body || "") ?? "",
  );
  const body = stripOrphanedCarouselResizeScripts(liveBody);
  const looksHtml =
    mode === "html" ||
    (mode === "auto" && blogBodyLooksHtmlLocal(body));
  const bodyEmpty = looksHtml
    ? !stripBlogBodyToText(body)
    : !String(body).trim();

  // v2 per-post shell from draft meta (`blog_layout`); falls back to cfg width.
  const postBlogLayout = resolvePostBlogLayout(p);
  const postShellStyle = {
    ...(editorialBody || postBlogLayout
      ? {}
      : { ["--tpl-post-mw" as string]: `${maxW}px` }),
    ["--tpl-post-fs" as string]: bodyFont,
  } as React.CSSProperties;
  // Weekly / content roundups embed `![content: …](url)` cards — center their shell.
  const contentRoundupShell = /!\[[^\]]*content\s*:/i.test(liveBody);
  const postShellClass = [
    editorialBody || postBlogLayout || contentRoundupShell
      ? "mte-tpl-post-shell--editorial"
      : "",
    postBlogLayout === "looks_products_roundup"
      ? "mte-tpl-post-shell--roundup"
      : "",
    contentRoundupShell ? "mte-tpl-post-shell--content-roundup" : "",
    postBlogLayout === "transcript_longform_screenshots"
      ? "mte-tpl-post-shell--longform"
      : "",
    postBlogLayout === "review_longform_screenshots"
      ? "mte-tpl-post-shell--longform mte-tpl-post-shell--review"
      : "",
  ]
    .filter(Boolean)
    .join(" ") || undefined;
  const postBodyShellProps = {
    fullBleed: editorialBody,
  } as const;

  const backBtn =
    onNavigate && showBack ? (
      <button
        type="button"
        onClick={() => {
          templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.BLOG_BACK, {
            sectionId: "postBody",
            sectionType: "PostBody",
            entityType: "journal",
            entityId: p ? String(p.id) : undefined,
            entityLabel: p?.title || undefined,
          });
          if (editable && inspectorCtx?.requestSelectPage) {
            inspectorCtx.requestSelectPage("journal");
            return;
          }
          onNavigate("journal");
        }}
        className="mte-tpl-post-back"
      >
        ← Back to blog
      </button>
    ) : null;

  // All hooks must come before any early returns (rules of hooks).
  const pid = p ? String(p.id) : "";
  const readSentinelRef = React.useRef<HTMLDivElement>(null);
  const readFiredRef = React.useRef(false);
  React.useEffect(() => {
    readFiredRef.current = false;
  }, [pid]);
  React.useEffect(() => {
    if (editable || bodyEmpty || !pid || !readSentinelRef.current) return;
    const sentinel = readSentinelRef.current;
    const root = document.getElementById("master-template-scroll-root");
    const observer = new IntersectionObserver(
      (entries) => {
        if (readFiredRef.current) return;
        if (entries[0]?.isIntersecting) {
          readFiredRef.current = true;
          // RENDER — scroll-depth engagement must never land in click RPCs
          // (counting Click + Read was the original Blog analytics 2× bug).
          templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.JOURNAL_READ, {
            sectionId: "postBody",
            sectionType: "PostBody",
            entityType: "journal",
            entityId: pid,
            entityLabel: p?.title || pid,
            details: { post_id: pid, post_slug: p?.slug, post_title: p?.title, depth: "100" },
            eventType: TEMPLATE_TRACKING_TYPES.RENDER,
          });
        }
      },
      { root: root ?? null, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [editable, bodyEmpty, pid, p?.title, p?.slug, templateAnalytics]);

  if (!p) {
    if (isLoading) {
      return (
        <Shell
          T={T}
          cfg={cfg}
          independentShellClassName="mte-tpl-post-body-shell"
          {...postBodyShellProps}
        >
          <TplPostShell vars={postShellStyle} className={postShellClass}>
            {backBtn}
            <div className="mte-tpl-skeleton mte-tpl-skeleton--post-body" aria-busy="true" />
          </TplPostShell>
        </Shell>
      );
    }
    return (
      <Shell
        T={T}
        cfg={cfg}
        independentShellClassName="mte-tpl-post-body-shell"
        {...postBodyShellProps}
      >
        <TplPostShell vars={postShellStyle} className={postShellClass}>
          {backBtn}
          <p className="mte-tpl-muted-p">This post could not be found.</p>
        </TplPostShell>
      </Shell>
    );
  }

  const blogBodyPath = `blogPost.${pid}.body`;
  const bodyClass = `template-blog-body-html mte-tpl-post-body-html${
    T.dropCaps
      ? " mte-tpl-post-body-e-block mte-tpl-post-body-e-block--dropcap"
      : ""
  }`;
  // Markdown product photos and shop links still live inside HTML wrappers
  // (rich-text save, <p>/<br>). Parse those before treating the body as HTML,
  // otherwise the page prints ![alt](url) and [label](url) as plain text.
  const productBlocks = parseBlogProductBody(body, {
    fallbackImage: getJournalPostHeroImage(p),
  });

  if (bodyEmpty) {
    return (
      <Shell
        T={T}
        cfg={cfg}
        independentShellClassName="mte-tpl-post-body-shell"
        {...postBodyShellProps}
      >
        <TplPostShell vars={postShellStyle} className={postShellClass}>
          {backBtn}
          {editable ? null : (
            <p className="mte-tpl-muted-p" role="status">
              This post has no body content yet.
            </p>
          )}
        </TplPostShell>
        {showMorePosts && p ? (
          <div className="mte-tpl-jrnl-more-slot">
            <JournalMorePostsList
              posts={CREATOR.journal}
              currentPostId={pid}
              onNavigate={onNavigate}
              blogPostHrefForSlug={blogPostHrefForSlug}
              compact
            />
          </div>
        ) : null}
      </Shell>
    );
  }

  return (
    <Shell
      T={T}
      cfg={cfg}
      independentShellClassName="mte-tpl-post-body-shell"
      {...postBodyShellProps}
    >
      <TplPostShell vars={postShellStyle} className={postShellClass}>
        {backBtn}
        {productBlocks ? (
          <BlogProductBody
            blocks={productBlocks}
            className={`${bodyClass} mte-tpl-post-body--products`}
          />
        ) : looksHtml ? (
          <BlogBodyHtml path={blogBodyPath} html={body} className={bodyClass} />
        ) : (
          <E
            path={blogBodyPath}
            multiline
            as="div"
            className={`mte-tpl-post-body-p mte-tpl-post-body-e-block${
              T.dropCaps ? " mte-tpl-post-body-e-block--dropcap" : ""
            }`}
          >
            {body}
          </E>
        )}
        <div ref={readSentinelRef} aria-hidden style={{ height: 1, width: "100%" }} />
      </TplPostShell>
      {showMorePosts ? (
        <div className="mte-tpl-jrnl-more-slot">
          <JournalMorePostsList
            posts={CREATOR.journal}
            currentPostId={pid}
            onNavigate={onNavigate}
            blogPostHrefForSlug={blogPostHrefForSlug}
            compact
          />
        </div>
      ) : null}
    </Shell>
  );
}

function blogBodyLooksHtmlLocal(s: string) {
  return /<\/?[a-z][\s\S]*>/i.test(String(s || ""));
}

PostBody.options = {
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
  bodyLayout: {
    kind: "radio",
    label: "Body width",
    options: ["editorial", "narrow"],
    default: "editorial",
  },
  maxWidth: {
    kind: "number",
    label: "Max width (px, narrow layout only)",
    min: 520,
    max: 920,
    default: 760,
  },
  showBackLink: {
    kind: "toggle",
    label: "“Back to blog” in body",
    default: false,
  },
  showMorePosts: {
    kind: "toggle",
    label: "“More posts” at bottom",
    default: true,
  },
} as SectionOptions;

// ── Post author ────────────────────────────────────────────────
export function PostAuthor({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const hasBio = Boolean(
    String(CREATOR.bio?.name || "").trim() ||
      String(CREATOR.bio?.avatar || "").trim(),
  );
  const isLoading = useCreatorSectionLoading(hasBio);
  const templateAnalytics = useTemplateAnalyticsOptional();
  const b = CREATOR.bio;
  const style = (cfg?.style as string) || "inline";
  const trackAuthorCta = () => {
    templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.AUTHOR_CTA_CLICK, {
      sectionId: "postAuthor",
      sectionType: "PostAuthor",
      entityType: "author",
      entityLabel: b?.name || "",
      details: { style },
    });
  };
  const showBio = cfg?.showBio !== false;
  const showCta = cfg?.showCta !== false;
  if (isLoading) {
    return (
      <Shell T={T} cfg={cfg}>
        <div className="mte-tpl-skeleton mte-tpl-skeleton--post-author" aria-busy="true" />
      </Shell>
    );
  }
  const authorAvatarSrc = b.avatar
    ? getBudgetedTemplateImageSrc(b.avatar, "avatar")
    : "";
  if (style === "card")
    return (
      <Shell T={T} cfg={cfg}>
        <div className="mte-tpl-auth-card">
          <img src={authorAvatarSrc} alt="" className="mte-tpl-auth-ava-lg" loading="lazy" decoding="async" />
          <div className="mte-tpl-auth-eyebrow">WRITTEN BY</div>
          <div className="mte-tpl-auth-name-lg">{b.name}</div>
          {showBio && <p className="mte-tpl-auth-bio">{b.tagline}</p>}
          {showCta && (
            <a href="#" className={btnClass("primary")} onClick={trackAuthorCta}>
              Subscribe →
            </a>
          )}
        </div>
      </Shell>
    );
  if (style === "minimal")
    return (
      <Shell T={T} cfg={cfg}>
        <div className="mte-tpl-auth-min-row">
          <img src={authorAvatarSrc} alt="" className="mte-tpl-auth-ava-sm" loading="lazy" decoding="async" />
          <div className="mte-tpl-auth-min-grow">
            <div className="mte-tpl-auth-eyebrow mte-tpl-auth-eyebrow--mb4">
              WRITTEN BY
            </div>
            <div className="mte-tpl-prod-name">{b.name}</div>
          </div>
          {showCta && (
            <a href="#" className="mte-tpl-auth-cta-link" onClick={trackAuthorCta}>
              Subscribe →
            </a>
          )}
        </div>
      </Shell>
    );
  if (style === "banner")
    return (
      <Shell
        T={T}
        cfg={{
          ...cfg,
          bgTone: (cfg?.bgTone as "bg" | "surface" | "accent") || "accent",
        }}
      >
        <div className="mte-tpl-auth-ban-grid">
          <img src={authorAvatarSrc} alt="" className="mte-tpl-auth-ban-ava" loading="lazy" decoding="async" />
          <div>
            <div className="mte-tpl-auth-ban-eyebrow">WRITTEN BY</div>
            <div className="mte-tpl-auth-ban-name">{b.name}</div>
            {showBio && <p className="mte-tpl-auth-ban-bio">{b.tagline}</p>}
          </div>
          {showCta && (
            <a
              href="#"
              className={`${btnClass("invertOnAccent")}${T.device === "mobile" ? " mte-tpl-btn--stretch" : ""}`}
              onClick={trackAuthorCta}
            >
              Subscribe →
            </a>
          )}
        </div>
      </Shell>
    );
  return (
    <Shell
      T={T}
      cfg={{
        ...cfg,
        bgTone: (cfg?.bgTone as "bg" | "surface" | "accent") || "surface",
      }}
    >
      <div className="mte-tpl-auth-inline-grid">
        <img src={authorAvatarSrc} alt="" className="mte-tpl-auth-inline-ava" loading="lazy" decoding="async" />
        <div>
          <div className="mte-tpl-auth-inline-eyebrow">WRITTEN BY</div>
          <div className="mte-tpl-auth-inline-name">{b.name}</div>
          {showBio && <p className="mte-tpl-auth-inline-bio">{b.tagline}</p>}
        </div>
        {showCta && (
          <a
            href="#"
            className={`${btnClass("primary")}${T.device === "mobile" ? " mte-tpl-btn--stretch" : ""}`}
            onClick={trackAuthorCta}
          >
            Subscribe →
          </a>
        )}
      </div>
    </Shell>
  );
}
PostAuthor.options = {
  style: {
    kind: "radio",
    label: "Layout",
    options: ["inline", "card", "minimal", "banner"],
    default: "inline",
  },
  showBio: { kind: "toggle", label: "Show author bio", default: true },
  showCta: { kind: "toggle", label: "Show follow CTA", default: true },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "surface",
  },
} as SectionOptions;

// ── About: story ───────────────────────────────────────────────
/** Shimmer skeleton for the AboutStory section while the DB fetch is in flight. */
function AboutStorySkeleton({ narrow }: { narrow: boolean }) {
  return (
    <div
      className={`mte-tpl-about-grid${narrow ? " mte-tpl-about-grid--stack" : ""}`}
      aria-hidden="true"
    >
      {/* Headshot placeholder — matches aspect-ratio 4/5 */}
      <div className="mte-tpl-about-skel-shot" />
      <div>
        {/* Name / heading */}
        <div
          className="mte-tpl-about-skel-h"
          style={{ animationDelay: "40ms" }}
        />
        {/* 3 text placeholder lines */}
        {[100, 88, 62].map((w, i) => (
          <div
            key={i}
            className="mte-tpl-about-skel-line"
            style={{
              width: `${w}%`,
              marginBottom: 14,
              animationDelay: `${100 + i * 70}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function AboutStory({ T, cfg }: SectionProps) {
  const CREATOR = useCreatorData();
  const a = CREATOR.about;
  const headingDefault =
    a.profileName?.trim() ||
    CREATOR.bio.name?.trim() ||
    cfg?.heading ||
    "A studio of one, mostly";
  const { editable, get } = useEdit();
  const narrow = narrowTemplateLayout(T.device);
  const liveHeading = String(get("about.heading", headingDefault) || "").trim();
  const liveBio = String(get("about.bio", a.bio || "") || "").trim();
  const hasAboutContent = Boolean(
    a.headshot?.trim() ||
      a.bio?.trim() ||
      liveBio ||
      (Array.isArray(a.story) && a.story.length > 0),
  );
  // Skeleton only while the live fetch is in flight and this section has no
  // content yet (bootstrap / template_settings can paint immediately).
  const isLoading = useCreatorSectionLoading(hasAboutContent);

  if (!editable && isLoading) {
    return (
      <Shell T={T} cfg={cfg}>
        <AboutStorySkeleton narrow={narrow} />
      </Shell>
    );
  }

  const storyParas = a.story.length > 0 ? a.story : [];
  return (
    <Shell T={T} cfg={cfg}>
      <div
        className={`mte-tpl-about-grid${narrow ? " mte-tpl-about-grid--stack" : ""}`}
      >
        <EditableTemplateAsset
          slot="about-headshot"
          src={a.headshot}
          alt=""
          className="mte-tpl-img-filter mte-tpl-about-shot"
          allowEmpty
        />
        <div>
          {editable || liveHeading ? (
          <h2 className="mte-tpl-about-h2">
            {editable ? (
              <E path="about.heading" placeholder="Add title">
                {headingDefault}
              </E>
            ) : (
              liveHeading
            )}
          </h2>
          ) : null}
          <div className="mte-tpl-about-stack">
            {storyParas.map((para, i) => {
              const path = `about.story.${i}`;
              const live = String(get(path, para) || "").trim();
              if (!live) return null;
              return (
              <div key={i} className="mte-tpl-about-p mte-tpl-about-p--pre">
                {editable ? (
                  <E path={path} multiline as="div">
                    {para}
                  </E>
                ) : (
                  para
                )}
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </Shell>
  );
}
AboutStory.options = {
  heading: { kind: "text", label: "Heading", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

/** Default StayUpToDate CTA — hide under More From tiles (not meaningful copy). */
function isCheckItOutPlaceholder(body: string | undefined): boolean {
  if (!body?.trim()) return false;
  const normalized = body
    .trim()
    .replace(/[!¡?.…]+$/u, "")
    .trim()
    .toLowerCase();
  return normalized === "check it out";
}

// ── About: values ──────────────────────────────────────────────
export function AboutValues({ T, cfg, onNavigate }: SectionProps) {
  const CREATOR = useCreatorData();
  const { editable } = useEdit();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const { items, isResolving } = useAboutMoreFromItems(
    CREATOR.about.values,
    CREATOR.carousels,
    userId,
    username,
    !editable,
  );
  const { items: itemsWithImages, isSyncing } = useAboutMoreFromCardImages(
    items,
    userId,
    username,
    Boolean(userId && username),
  );
  const handle =
    CREATOR.bio.handle ||
    (CREATOR.bio.username
      ? `@${CREATOR.bio.username.trim().toLowerCase()}`
      : "");
  const narrow = narrowTemplateLayout(T.device);
  const moreFromHeading = cfg?.heading || `More From ${handle}`;
  const moreFromShellCfg = {
    ...cfg,
    bgTone: (cfg?.bgTone as "bg" | "surface" | "accent") || "surface",
  };
  const showMoreFromLoader = isResolving || isSyncing;

  if (showMoreFromLoader) {
    return (
      <Shell
        T={T}
        cfg={moreFromShellCfg}
        heading={moreFromHeading}
        independentShellClassName="mte-tpl-about-more-shell"
      >
        <AboutMoreFromPendingGrid narrow={narrow} count={6} />
      </Shell>
    );
  }

  if (!itemsWithImages.length) {
    if (!editable) return null;
    return (
      <Shell
        T={T}
        cfg={moreFromShellCfg}
        heading={moreFromHeading}
        independentShellClassName="mte-tpl-about-more-shell"
      >
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Add content tabs in Recent Content to preview More From cards.
        </p>
      </Shell>
    );
  }

  return (
    <Shell
      T={T}
      cfg={moreFromShellCfg}
      heading={moreFromHeading}
      independentShellClassName="mte-tpl-about-more-shell"
    >
      <div
        className={`mte-tpl-av-grid${narrow ? " mte-tpl-av-grid--stack mte-tpl-av-grid--2" : " mte-tpl-av-grid--3"}`}
      >
        {itemsWithImages.map((v, i) => {
          const rid = v.stayComponentRowId?.trim();
          const canDeepLink = Boolean(
            !editable &&
              onNavigate &&
              v.renderSlug?.trim() &&
              v.tabKind === "content",
          );
          const openLinkedTab = () => {
            if (!onNavigate || !v.renderSlug?.trim() || v.tabKind !== "content") {
              return;
            }
            templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.ABOUT_DEEPLINK, {
              sectionId: "aboutValues",
              sectionType: "AboutValues",
              entityType: "about_value",
              entityId: rid || String(i),
              entityLabel: v.title || "",
              details: {
                render_slug: v.renderSlug.trim(),
                content_view: v.contentView ?? "latest",
                tab_kind: v.tabKind,
                position: i,
              },
            });
            navigateTemplateTabTarget(
              onNavigate,
              "content",
              v.renderSlug.trim(),
              v.contentView ?? "latest",
            );
          };
          const titleNode = rid ? (
            <E path={`sutc.${rid}.name`} placeholder="Add title">
              {v.title}
            </E>
          ) : editable ? (
            <E path={`about.values.${i}.title`} placeholder="Add title">
              {v.title}
            </E>
          ) : (
            v.title
          );
          const bodyText = v.body && !isCheckItOutPlaceholder(v.body) ? v.body : "";
          const bodyNode = bodyText ? (
            rid ? (
              <E path={`sutc.${rid}.cta_placeholder`} multiline>
                {bodyText}
              </E>
            ) : editable ? (
              <E path={`about.values.${i}.body`} multiline>
                {bodyText}
              </E>
            ) : (
              bodyText
            )
          ) : null;
          const heroHasImage = Boolean(v.image?.trim());
          return (
            <div
              key={rid || i}
              role={canDeepLink ? "button" : undefined}
              tabIndex={canDeepLink ? 0 : undefined}
              onClick={canDeepLink ? openLinkedTab : undefined}
              onKeyDown={
                canDeepLink
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openLinkedTab();
                      }
                    }
                  : undefined
              }
              className={`mte-tpl-av-card${canDeepLink ? " mte-tpl-av-card--click" : ""}`}
            >
              {rid && editable ? (
                <div
                  className={[
                    "mte-tpl-av-hero",
                    "mte-tpl-av-hero--editable",
                    heroHasImage ? "" : "mte-tpl-av-hero--empty",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EditableTemplateAsset
                    slot={`sutc-${rid}-card-image`}
                    src={v.image || ""}
                    className="mte-tpl-img-filter mte-tpl-av-hero-editable-img"
                    allowEmpty
                  />
                  {heroHasImage ? (
                    <>
                      <div className="mte-tpl-av-hero-grad" />
                      <p className="mte-tpl-av-hero-title">{titleNode}</p>
                    </>
                  ) : (
                    <div
                      className={
                        narrow
                          ? "mte-tpl-av-text-stack"
                          : "mte-tpl-av-text-stack mte-tpl-av-text-stack--lg"
                      }
                    >
                      <div className="mte-tpl-av-num">0{i + 1}</div>
                      <div className="mte-tpl-av-title-fallback">{titleNode}</div>
                    </div>
                  )}
                </div>
              ) : (
                <TplAvHero image={v.image}>
                  {heroHasImage ? (
                    <>
                      <div className="mte-tpl-av-hero-grad" />
                      <p className="mte-tpl-av-hero-title">{titleNode}</p>
                    </>
                  ) : (
                    <div
                      className={
                        narrow
                          ? "mte-tpl-av-text-stack"
                          : "mte-tpl-av-text-stack mte-tpl-av-text-stack--lg"
                      }
                    >
                      <div className="mte-tpl-av-num">0{i + 1}</div>
                      <div className="mte-tpl-av-title-fallback">{titleNode}</div>
                    </div>
                  )}
                </TplAvHero>
              )}
              {bodyNode ? (
                <div
                  className="mte-tpl-av-body"
                  onClick={editable ? (e) => e.stopPropagation() : undefined}
                >
                  {bodyNode}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
AboutValues.options = {
  heading: { kind: "text", label: "Heading", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "surface",
  },
} as SectionOptions;

// ── Contact form ───────────────────────────────────────────────
// Data-driven: reads `CREATOR.contact` (from `contact_page` via ContactMeModal, or
// account-email fallback when no valid contact row — same behavior as contact-me
// when the modal was never completed). Posts to the same endpoint as `ContactUs.tsx`
// (`/send-email`). When `CREATOR.contact` is `null` or disabled, the section is skipped
// (`SECTION_DATA_KEY.ContactForm = 'contact'` auto-hide).

/** Shimmer placeholder rendered while `contact_page` data is still in flight. */
function ContactFormSkeleton({
  narrow,
  showSideInfo,
}: {
  narrow: boolean;
  showSideInfo: boolean;
}) {
  const single = narrow || !showSideInfo;
  return (
    <div
      className={`mte-tpl-cf-grid${single ? " mte-tpl-cf-grid--single" : ""}`}
      aria-hidden="true"
    >
      {!single && (
        <div className="mte-tpl-cf-side">
          {/* Intro paragraph placeholder */}
          {[90, 78, 55].map((w, i) => (
            <div
              key={i}
              className="mte-tpl-cf-skel-line"
              style={{
                width: `${w}%`,
                height: 16,
                marginBottom: i < 2 ? 10 : 28,
                animationDelay: `${i * 80}ms`,
              }}
            />
          ))}
        </div>
      )}
      <div>
        <div
          className={`mte-tpl-cf-form${single ? " mte-tpl-cf-form--single" : ""}`}
        >
          {/* Two half-width fields (Name / Email) */}
          {[0, 1].map((i) => (
            <div key={i} className="mte-tpl-cf-field-label">
              <div
                className="mte-tpl-cf-skel-line"
                style={{ width: 40, height: 9, animationDelay: `${i * 80}ms` }}
              />
              <div
                className="mte-tpl-cf-skel-field"
                style={{ animationDelay: `${40 + i * 80}ms` }}
              />
            </div>
          ))}
          {/* Full-width message textarea */}
          <div className="mte-tpl-cf-field-label mte-tpl-cf-field-label--full">
            <div
              className="mte-tpl-cf-skel-line"
              style={{ width: 58, height: 9, animationDelay: "160ms" }}
            />
            <div
              className="mte-tpl-cf-skel-field mte-tpl-cf-skel-field--tall"
              style={{ animationDelay: "200ms" }}
            />
          </div>
          {/* Submit button placeholder */}
          <div className="mte-tpl-cf-field-label mte-tpl-cf-field-label--full">
            <div
              className="mte-tpl-cf-skel-btn"
              style={{ animationDelay: "280ms" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const SUBMIT_FIELD_FROM_LABEL: Record<string, string> = {
  Name: "name",
  Email: "email",
  Phone: "phonenumber",
  Instagram: "instagram",
  Message: "comment",
};

function getInputAutoCompleteForField(label: string): string {
  const k = label.toLowerCase();
  if (k === "email") return "email";
  if (k === "name") return "name";
  if (k === "phone") return "tel";
  return "off";
}

function getInputTypeForField(label: string): "text" | "email" | "tel" {
  const k = label.toLowerCase();
  if (k === "email") return "email";
  if (k === "phone") return "tel";
  return "text";
}

const CONTACT_FORM_INTRO_DEFAULT =
  "For partnerships, press, editorial requests, and the occasional letter.";

export function ContactForm({ T, cfg, isActive = true }: SectionProps) {
  const CREATOR = useCreatorData();
  const { get, editable } = useEdit();
  const sectionPath = useTemplateSectionInstance();
  const contactClass = sectionPathClassParts(sectionPath);
  const contact = CREATOR.contact;
  const userId = CREATOR.bio?.userId || "";
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const narrow = narrowTemplateLayout(T.device);

  // Hooks must be called unconditionally (rules of hooks); the render is gated below.
  const templateAnalytics = useTemplateAnalyticsOptional();
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [status, setStatus] = React.useState<null | "success" | "error">(null);

  // Auto-clear success/error status after 6 s so the stale banner never persists
  // when the user navigates away and back (all pages stay pre-mounted).
  React.useEffect(() => {
    if (!status) return;
    const t = window.setTimeout(() => setStatus(null), 6000);
    return () => window.clearTimeout(t);
  }, [status]);

  // Pages stay pre-mounted (CSS-hidden). Clear draft fields when leaving Contact
  // so navigating away and back always shows an empty form.
  React.useEffect(() => {
    if (isActive) return;
    setValues({});
    setErrors({});
    setStatus(null);
    setSubmitting(false);
  }, [isActive]);

  // Show a shimmer skeleton while the contact_page row is still loading.
  // `contact === undefined` with a real userId means the fetch is in flight.
  // Without userId we're in mock/preview mode — fall through to the form.
  if (contact === undefined && userId) {
    return (
      <Shell T={T} cfg={cfg} independentShellClassName="mte-tpl-contact-shell">
        <ContactFormSkeleton
          narrow={narrow}
          showSideInfo={cfg?.showSideInfo !== false}
        />
      </Shell>
    );
  }

  if (!contact?.enabled) return null;

  const fields = Array.isArray(contact.fields) ? contact.fields : [];
  const buttonLabel = String(
    get(
      "contact.buttonText",
      (contact.buttonText && contact.buttonText.trim()) ||
        (cfg?.cta as string) ||
        "Send message",
    ) || "Send message",
  ).trim() || "Send message";
  const titleText = String(
    get(
      "contact.titleText",
      (contact.titleText && contact.titleText.trim()) || "",
    ) || "",
  ).trim();
  const imageUrl = contact.imageUrl && contact.imageUrl.trim();
  const liveIntro = String(
    get("contact.intro", CONTACT_FORM_INTRO_DEFAULT) ?? "",
  );
  const paintIntro = Boolean(liveIntro.trim());

  const setValue = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    for (const label of fields) {
      const key = SUBMIT_FIELD_FROM_LABEL[label] || label.toLowerCase();
      const v = (values[key] || "").trim();
      if (!v) {
        next[key] = `Please enter your ${label.toLowerCase()}`;
        continue;
      }
      if (label === "Email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        next[key] = "Please enter a valid email address";
      }
      if (label === "Phone" && !/^\d{7,15}$/.test(v.replace(/\D/g, ""))) {
        next[key] = "Please enter a valid phone number";
      }
    }
    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    if (!userId) {
      setStatus("error");
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      const submitted = JSON.stringify(values, null, 2);
      if (!contact.emailFallback) {
        await supabase
          .from("contact_page")
          .update({ submitted_data: submitted })
          .eq("user_id", userId);
      }

      const response = await fetch(
        "https://dev.thesearchbubble.com/send-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email: contact.creatorMail,
            creatorName: username,
            values,
          }),
        },
      );
      if (!response.ok) throw new Error("Email request failed");

      setStatus("success");
      setValues({});
      templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.CONTACT_SUBMIT, {
        sectionId: "contactForm",
        sectionType: "ContactForm",
        entityType: "form",
        details: { field_count: fields.length },
      });
    } catch (err) {
      console.error("[ContactForm] submit failed:", err);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  // Email/Name on the same row when both are present; longer labels span full width.
  const fullSpanLabels = new Set(["Message", "Subject", "Instagram"]);

  const showSideInfo = cfg?.showSideInfo !== false;

  const gridClass =
    narrow || !showSideInfo
      ? `mte-tpl-cf-grid mte-tpl-cf-grid--${contactClass.combo} mte-tpl-contact-section mte-tpl-contact-section--${contactClass.combo} mte-tpl-cf-grid--single`
      : `mte-tpl-cf-grid mte-tpl-cf-grid--${contactClass.combo} mte-tpl-contact-section mte-tpl-contact-section--${contactClass.combo}`;
  const formClass = narrow
    ? `mte-tpl-cf-form mte-tpl-cf-form--${contactClass.combo} mte-tpl-cf-form--single`
    : `mte-tpl-cf-form mte-tpl-cf-form--${contactClass.combo}`;

  return (
    <Shell T={T} cfg={cfg} independentShellClassName="mte-tpl-contact-shell">
      <div className={gridClass}>
        {showSideInfo && (
          <div className={`mte-tpl-cf-side mte-tpl-cf-side--${contactClass.combo}`}>
            {paintIntro ? (
              <p className={`mte-tpl-cf-intro mte-tpl-cf-intro--${contactClass.combo}`}>
                <E path="contact.intro" multiline>
                  {CONTACT_FORM_INTRO_DEFAULT}
                </E>
              </p>
            ) : null}
          </div>
        )}
        <div className={`mte-tpl-cf-main mte-tpl-cf-main--${contactClass.combo}`}>
          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className={`mte-tpl-cf-img mte-tpl-cf-img--${contactClass.combo}`}
            />
          )}
          {editable || titleText ? (
            <h3 className={`mte-tpl-cf-title mte-tpl-cf-title--${contactClass.combo}`}>
              <E path="contact.titleText" placeholder="Add title">
                {titleText}
              </E>
            </h3>
          ) : null}
          <form onSubmit={handleSubmit} noValidate className={formClass}>
            {fields.map((label) => {
              const key = SUBMIT_FIELD_FROM_LABEL[label] || label.toLowerCase();
              const span = fullSpanLabels.has(label);
              const isTextarea = label === "Message";
              const err = errors[key];
              return (
                <label
                  key={label}
                  className={
                    span
                      ? `mte-tpl-cf-field-label mte-tpl-cf-field-label--${contactClass.combo} mte-tpl-cf-field-label--full`
                      : `mte-tpl-cf-field-label mte-tpl-cf-field-label--${contactClass.combo}`
                  }
                >
                  <span className={`mte-tpl-cf-label mte-tpl-cf-label--${contactClass.combo}`}>{label}</span>
                  {isTextarea ? (
                    <textarea
                      rows={6}
                      value={values[key] || ""}
                      onChange={(e) => setValue(key, e.target.value)}
                      autoComplete="off"
                      className={`mte-tpl-cf-field mte-tpl-cf-field--${contactClass.combo} mte-tpl-cf-field--textarea${err ? " mte-tpl-cf-field--err" : ""}`}
                    />
                  ) : (
                    <input
                      type={getInputTypeForField(label)}
                      value={values[key] || ""}
                      onChange={(e) => setValue(key, e.target.value)}
                      autoComplete={getInputAutoCompleteForField(label)}
                      className={`mte-tpl-cf-field mte-tpl-cf-field--${contactClass.combo}${err ? " mte-tpl-cf-field--err" : ""}`}
                    />
                  )}
                  {err && <span className={`mte-tpl-cf-err mte-tpl-cf-err--${contactClass.combo}`}>{err}</span>}
                </label>
              );
            })}
            <div className={`mte-tpl-contact-actions mte-tpl-contact-actions--${contactClass.combo}`}>
              <button
                type="submit"
                disabled={submitting}
                className={`${btnClass("primary")} mte-tpl-btn--bare mte-tpl-contact-submit mte-tpl-contact-submit--${contactClass.combo}`}
              >
                {submitting ? "Sending…" : `${buttonLabel} →`}
              </button>
              {status === "success" && (
                <span className={`mte-tpl-contact-status mte-tpl-contact-status--${contactClass.combo}`}>Message sent</span>
              )}
              {status === "error" && (
                <span className={`mte-tpl-contact-status mte-tpl-contact-status--${contactClass.combo} mte-tpl-contact-status--err`}>
                  Something went wrong — please try again
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </Shell>
  );
}
ContactForm.options = {
  showSideInfo: { kind: "toggle", label: "Side info column", default: true },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

// Batch sizes for the RecentTimeline raw-items fallback (mirrors LinkInBioStackSection).
const RECENT_INITIAL_BATCH = 25;
const RECENT_CHUNK_SIZE = 25;

// ── Recent timeline ────────────────────────────────────────────
export function RecentTimeline({
  T,
  cfg,
  onNavigate,
  carouselDeepLink,
  onCarouselDeepLinkConsumed,
  isActive = true,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const style = (cfg?.style as string) || "timeline";
  const showFilters = cfg?.showFilters !== false;
  const items = CREATOR.recent;

  // ── Infinite-scroll for raw fallback items (mirrors LinkInBioStackSection) ─
  const [recentVisibleCount, setRecentVisibleCount] = React.useState(RECENT_INITIAL_BATCH);
  const [recentRevealingBatch, setRecentRevealingBatch] = React.useState(false);
  const recentSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const recentGridRef = React.useRef<HTMLDivElement | null>(null);
  const recentObserverRef = React.useRef<IntersectionObserver | null>(null);
  const recentLoadingRef = React.useRef(false);

  // Reset when items list length changes (e.g. live data arrives).
  React.useEffect(() => {
    setRecentVisibleCount(RECENT_INITIAL_BATCH);
    setRecentRevealingBatch(false);
    recentLoadingRef.current = false;
  }, [items.length]);

  const recentVisibleItems = React.useMemo(
    () => items.slice(0, recentVisibleCount),
    [items, recentVisibleCount],
  );

  React.useEffect(() => {
    if (recentObserverRef.current) recentObserverRef.current.disconnect();
    if (recentVisibleCount >= items.length) return;
    const sentinel = recentSentinelRef.current;
    if (!sentinel) return;

    const scrollRoot = document.getElementById("master-template-scroll-root");

    recentObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (recentLoadingRef.current) return;
        recentLoadingRef.current = true;
        setRecentRevealingBatch(true);
        const batchStart = recentVisibleCount;
        window.setTimeout(() => {
          setRecentVisibleCount((prev) =>
            Math.min(prev + RECENT_CHUNK_SIZE, items.length),
          );
          setRecentRevealingBatch(false);
          recentLoadingRef.current = false;
          scheduleScrollTemplateNewBatchIntoView(
            () => findTemplateBatchAnchor(recentGridRef.current, batchStart),
            { behavior: "auto" },
          );
        }, 400);
      },
      {
        root: scrollRoot ?? null,
        rootMargin: "50px 0px",
        threshold: 0,
      },
    );
    recentObserverRef.current.observe(sentinel);
    return () => recentObserverRef.current?.disconnect();
  }, [recentVisibleCount, items.length]);

  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);

  const allContentCarousels = CREATOR.carousels.filter(isCarouselLatestTab);
  const {
    visibleCarousels: contentCarousels,
    isResolvingTabs: contentTabsResolving,
  } = useTemplateFilteredCarousels(
    allContentCarousels,
    userId,
    username,
    hasSession,
  );
  const [activeTab, setActiveTab] = React.useState(0);
  const [pendingTabIndex, setPendingTabIndex] = React.useState<number | null>(
    null,
  );
  const paintedTabIndex =
    pendingTabIndex != null ? pendingTabIndex : activeTab;
  const recentTabBarRef = React.useRef<HTMLDivElement>(null);
  const [emptyContentTabSlugs, setEmptyContentTabSlugs] = React.useState<
    Record<string, true>
  >({});
  const [mountedRecentTabSlugs, setMountedRecentTabSlugs] = React.useState<
    Record<string, true>
  >({});

  // Mount active tab first; keep visited tabs for instant switch.
  React.useEffect(() => {
    if (isActive) return;
    setActiveTab(0);
    setPendingTabIndex(null);
    setEmptyContentTabSlugs({});
    setMountedRecentTabSlugs({});
    setRecentVisibleCount(RECENT_INITIAL_BATCH);
    setRecentRevealingBatch(false);
  }, [isActive]);

  const contentTabs = contentCarousels.filter(
    (c) => c.renderSlug && !emptyContentTabSlugs[String(c.renderSlug)],
  );

  const contentSlugKey = contentTabs.map((c) => c.renderSlug ?? c.id).join("|");
  React.useEffect(() => {
    setActiveTab((i) =>
      contentTabs.length === 0 ? 0 : Math.min(i, contentTabs.length - 1),
    );
  }, [contentSlugKey, contentTabs.length]);

  React.useEffect(() => {
    if (pendingTabIndex == null) return;
    if (pendingTabIndex === activeTab) setPendingTabIndex(null);
  }, [pendingTabIndex, activeTab]);

  React.useEffect(() => {
    const slug = String(
      contentTabs[Math.min(paintedTabIndex, Math.max(contentTabs.length - 1, 0))]
        ?.renderSlug || "",
    ).trim();
    if (!slug) return;
    setMountedRecentTabSlugs((prev) =>
      prev[slug] ? prev : { ...prev, [slug]: true },
    );
  }, [paintedTabIndex, contentSlugKey, contentTabs]);

  // After the active tab has had a beat to fetch, mount the rest idle.
  React.useEffect(() => {
    if (contentTabs.length <= 1) return;
    const t = window.setTimeout(() => {
      setMountedRecentTabSlugs((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const c of contentTabs) {
          const slug = String(c.renderSlug || "").trim();
          if (slug && !next[slug]) {
            next[slug] = true;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 750);
    return () => window.clearTimeout(t);
  }, [contentSlugKey, contentTabs]);

  React.useEffect(() => {
    if (
      !carouselDeepLink ||
      carouselDeepLink.target !== "content" ||
      !onCarouselDeepLinkConsumed
    ) {
      return;
    }
    if (contentTabsResolving) return;
    const slug = carouselDeepLink.slug?.trim() ?? "";
    if (!slug) {
      onCarouselDeepLinkConsumed();
      return;
    }
    const idx = contentTabs.findIndex((c) =>
      tabSlugMatchesCarouselSlug(slug, c),
    );
    if (idx >= 0) setActiveTab(idx);
    onCarouselDeepLinkConsumed();
  }, [
    carouselDeepLink,
    contentSlugKey,
    contentTabsResolving,
    onCarouselDeepLinkConsumed,
  ]);

  useTemplateTabScrollIntoView(recentTabBarRef, paintedTabIndex, contentSlugKey);

  const sectionHeading =
    cfg?.heading !== undefined && String(cfg.heading).trim() !== ""
      ? cfg.heading
      : undefined;

  const currentCarousel = contentTabs[paintedTabIndex] || contentTabs[0];

  const showStayUpToDateEmbed =
    hasSession &&
    contentTabs.length > 0 &&
    Boolean(currentCarousel?.renderSlug);

  if (allContentCarousels.length > 0 && hasSession && contentTabsResolving) {
    return (
      <Shell T={T} cfg={cfg} heading={sectionHeading}>
        <div className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content" aria-busy="true">
          <TemplateTabFeedPendingGrid
            pageVariant="content"
            device={T.device}
            T={T}
          />
        </div>
      </Shell>
    );
  }

  const tabBar = showFilters && contentTabs.length >= 1 && (
    <div className="mte-tpl-tabbar" ref={recentTabBarRef}>
      {contentTabs.map((c, i) => (
        <button
          key={c.renderSlug || c.id}
          type="button"
          onClick={() => {
            setPendingTabIndex(i);
            setActiveTab(i);
            templateAnalytics?.trackContentTab({
              slug: c.renderSlug || String(c.id),
              label: c.title || "",
              contentView: "latest",
              sourcePageKey: "content",
            });
            if (c.renderSlug) {
              onNavigate?.("content", { carouselSlug: c.renderSlug });
            }
          }}
          className={
            i === paintedTabIndex
              ? "mte-tpl-tab-pill mte-tpl-tab-pill--active"
              : "mte-tpl-tab-pill"
          }
        >
          {c.title}
        </button>
      ))}
    </div>
  );
  if (style === "feed")
    return (
      <Shell T={T} cfg={cfg} heading={sectionHeading}>
        {tabBar}
        {showStayUpToDateEmbed ? (
          <div className="mte-tpl-tab-panels-stack">
            {contentTabs.map((c, i) =>
              c.renderSlug && mountedRecentTabSlugs[c.renderSlug] ? (
                <div
                  key={c.renderSlug}
                  className={
                    i === paintedTabIndex
                      ? "mte-tpl-tab-panel mte-tpl-tab-panel--active"
                      : "mte-tpl-tab-panel"
                  }
                  role="tabpanel"
                  aria-hidden={i !== paintedTabIndex}
                >
                  <TemplateStayUpToDateTabPanel
                    userId={userId}
                    username={username}
                    renderSlug={c.renderSlug}
                    T={T}
                    pageVariant="content"
                    aspect="1/1"
                    showShopButton={false}
                    onTabFeedResolved={(slug, hasItems) => {
                      if (!hasItems && slug) {
                        const tab = contentCarousels.find(
                          (x) => String(x.renderSlug) === String(slug),
                        );
                        if (!shouldHideEmptyContentTab(tab)) return;
                        setEmptyContentTabSlugs((s) => ({ ...s, [slug]: true }));
                      }
                    }}
                  />
                </div>
              ) : null,
            )}
          </div>
        ) : allContentCarousels.length === 0 ? (
          <TemplatePlatformHighlightFeed
            T={T}
            userId={userId}
            username={username}
            highlights={CREATOR.highlights}
            contentShape="square"
            isActive={isActive}
          />
        ) : (
          <>
            <div ref={recentGridRef} className="mte-tpl-rc-feed-grid mte-tpl-grid-enter">
              {recentVisibleItems.map((r) => (
                <a key={r.id} href="#" className="mte-tpl-rc-feed-card">
                  <img
                    src={r.image}
                    alt=""
                    className="mte-tpl-img-filter mte-tpl-rc-feed-img"
                  />
                  <div className="mte-tpl-rc-feed-grad" />
                  <div className="mte-tpl-rc-feed-cap">
                    <div className="mte-tpl-rc-feed-meta">
                      {r.kind} · {r.date}
                    </div>
                    <div className="mte-tpl-rc-feed-title">{r.title}</div>
                  </div>
                </a>
              ))}
            </div>
            {recentVisibleCount < items.length && (
              <>
                {recentRevealingBatch && (
                  <div
                    className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content"
                    aria-busy="true"
                  >
                    <TemplateTabFeedPendingGrid
                      pageVariant="content"
                      device={T.device}
                      maxItems={Math.min(
                        RECENT_CHUNK_SIZE,
                        items.length - recentVisibleCount,
                      )}
                      T={T}
                    />
                  </div>
                )}
                <div ref={recentSentinelRef} aria-hidden style={{ height: 1, width: "100%" }} />
              </>
            )}
          </>
        )}
      </Shell>
    );
  return (
    <Shell T={T} cfg={cfg} heading={sectionHeading}>
      {tabBar}
      {showStayUpToDateEmbed ? (
        <div className="mte-tpl-tab-panels-stack">
          {contentTabs.map((c, i) =>
            c.renderSlug && mountedRecentTabSlugs[c.renderSlug] ? (
              <div
                key={c.renderSlug}
                className={
                  i === paintedTabIndex
                    ? "mte-tpl-tab-panel mte-tpl-tab-panel--active"
                    : "mte-tpl-tab-panel"
                }
                role="tabpanel"
                aria-hidden={i !== paintedTabIndex}
              >
                <TemplateStayUpToDateTabPanel
                  userId={userId}
                  username={username}
                  renderSlug={c.renderSlug}
                  T={T}
                  pageVariant="content"
                  aspect="4/5"
                  showShopButton={false}
                  onTabFeedResolved={(slug, hasItems) => {
                    if (!hasItems && slug) {
                      const tab = contentCarousels.find(
                        (x) => String(x.renderSlug) === String(slug),
                      );
                      if (!shouldHideEmptyContentTab(tab)) return;
                      setEmptyContentTabSlugs((s) => ({ ...s, [slug]: true }));
                    }
                  }}
                />
              </div>
            ) : null,
          )}
        </div>
      ) : (
        <>
          <div ref={recentGridRef} className="mte-tpl-rc-tl mte-tpl-grid-enter">
            {recentVisibleItems.map((r) => (
              <a key={r.id} href="#" className="mte-tpl-rc-tl-row">
                <div className="mte-tpl-rc-tl-date">{r.date}</div>
                {T.device !== "mobile" && (
                  <div className="mte-tpl-rc-tl-kind">
                    {r.kind} · {r.meta}
                  </div>
                )}
                <div className="mte-tpl-rc-tl-title">
                  {T.device === "mobile" ? `${r.title} · ${r.kind}` : r.title}
                </div>
                {T.device !== "mobile" && (
                  <img
                    src={r.image}
                    alt=""
                    className="mte-tpl-img-filter mte-tpl-rc-tl-thumb"
                  />
                )}
              </a>
            ))}
          </div>
          {recentVisibleCount < items.length && (
            <>
              {recentRevealingBatch && (
                <div
                  className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content"
                  aria-busy="true"
                >
                  <TemplateTabFeedPendingGrid
                    pageVariant="content"
                    device={T.device}
                    maxItems={Math.min(
                      RECENT_CHUNK_SIZE,
                      items.length - recentVisibleCount,
                    )}
                    T={T}
                  />
                </div>
              )}
              <div ref={recentSentinelRef} aria-hidden style={{ height: 1, width: "100%" }} />
            </>
          )}
        </>
      )}
    </Shell>
  );
}
RecentTimeline.options = {
  style: {
    kind: "radio",
    label: "Style",
    options: ["timeline", "feed"],
    default: "timeline",
  },
  showFilters: { kind: "toggle", label: "Show filters", default: true },
  heading: { kind: "text", label: "Heading", default: "" },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;

// ── Search header ──────────────────────────────────────────────
export function SearchHeader({ T, cfg }: SectionProps) {
  // Catalog-only fallback when the live search feed is not mounted.
  // Public `/website/:user/search` uses `TemplateSearchMockupChrome` + FTS.
  const CREATOR = useCreatorData();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const placeholder = (cfg?.placeholder as string) || "Search";
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);
  const isMobile = T.device === "mobile";
  const cols = (cfg?.columns as number) || 5;
  const aspect = (cfg?.aspect as string) || "4/5";
  const buttonLabel = (cfg?.buttonLabel as string) || "Shop";

  const [draft, setDraft] = React.useState("");
  const [query, setQuery] = React.useState("");
  const { data: searchBarTags } = useLoadSearchBarTags(username || undefined);

  const searching = query.trim().length > 0;
  const needle = query.trim();

  const {
    products: productHits,
    contentItems: contentHits,
    brandCounts,
    loading,
    contentLoading,
    fetchNextProductsPage,
    hasNextProductsPage,
    isFetchingNextProductsPage,
    fetchNextContentPage,
    hasNextContentPage,
    isFetchingNextContentPage,
    productsTotal,
    contentTotal,
  } = useShopCatalog({
    username,
    userId,
    enabled: hasSession && searching,
    shopView: "all",
    categorySlug: null,
    shopBrowse: {},
    surface: "search",
    searchQuery: needle,
  });

  const hitCount = searching ? productsTotal + contentTotal : 0;

  const commitSearch = React.useCallback(
    (term?: string) => {
      const next = (term ?? draft).trim();
      setDraft(next);
      setQuery(next);
      if (next) templateAnalytics?.trackSearch(next, "search");
    },
    [draft, templateAnalytics],
  );

  const clearSearch = React.useCallback(() => {
    setDraft("");
    setQuery("");
  }, []);

  const suggestTerms = React.useMemo(
    () => parseSearchSuggestionTags(searchBarTags),
    [searchBarTags],
  );

  return (
    <Shell T={T} cfg={cfg}>
      <div className="mte-tpl-shop-search-panel">
        <ShopSearchBar
          placeholder={placeholder}
          draft={draft}
          onDraft={setDraft}
          onSubmit={() => commitSearch()}
          onClear={clearSearch}
        />
        <ShopSearchSuggestions terms={suggestTerms} onSearch={(t) => commitSearch(t)} />
      </div>

      {!searching ? (
        <ShopCatalogEmpty message="Search products and content" />
      ) : (
        <>
          <div className="mte-tpl-shop-search-count">
            {hitCount} result{hitCount === 1 ? "" : "s"} for “{query.trim()}”
          </div>
          {productHits.length > 0 ? (
            <ShopProductCatalogPanel
              catalogKey={`search-p-${needle}`}
              T={T}
              products={productHits}
              loading={loading && productHits.length === 0}
              userId={userId}
              cols={isMobile ? 2 : cols}
              aspect={aspect}
              showButton
              showCategory={false}
              showPrice={false}
              buttonLabel={buttonLabel}
              shopView="all"
              brandCounts={brandCounts}
              hasMore={hasNextProductsPage}
              loadingMore={isFetchingNextProductsPage}
              onLoadMore={() => {
                void fetchNextProductsPage();
              }}
            />
          ) : null}
          {contentHits.length > 0 ? (
            <ShopContentCatalogPanel
              catalogKey={`search-c-${needle}`}
              T={T}
              items={contentHits}
              loading={contentLoading && contentHits.length === 0}
              userId={userId}
              username={username}
              cols={isMobile ? 2 : cols}
              aspect={aspect}
              buttonLabel={buttonLabel}
              hasMore={hasNextContentPage}
              loadingMore={isFetchingNextContentPage}
              onLoadMore={() => {
                void fetchNextContentPage();
              }}
            />
          ) : null}
          {hitCount === 0 && !loading && !contentLoading ? (
            <ShopCatalogEmpty message={`Nothing matching “${query.trim()}”`} />
          ) : null}
        </>
      )}
    </Shell>
  );
}
SearchHeader.options = {
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
  placeholder: {
    kind: "text",
    label: "Placeholder hint",
    default: "Search",
  },
} as SectionOptions;

// ── TextBlock ───────────────────────────────────────────────────
// A freeform text section with an optional heading and a body paragraph.
// Designed as an opt-in "extra text" block for About, Contact, and other pages.
export function TextBlock({ T, cfg }: SectionProps) {
  const { editable } = useEdit();
  const heading = (cfg?.heading as string | undefined) ?? "";
  const body    = (cfg?.body    as string | undefined) ?? "";
  const align   = (cfg?.align   as string | undefined) ?? "left";
  const isEmpty = !heading.trim() && !body.trim();

  if (isEmpty) {
    if (!editable) return null;
    return (
      <Shell T={T} cfg={cfg}>
        <p className="mte-tpl-muted-p mte-tpl-section-empty-hint" role="status">
          Open tweaks → Text Block to add a heading or body text.
        </p>
      </Shell>
    );
  }

  return (
    <Shell T={T} cfg={cfg} heading={heading || undefined}>
      {body.trim() ? (
        <div
          className="mte-tpl-textblock-body"
          style={{ textAlign: align === "center" ? "center" : undefined }}
        >
          {body.split("\n").filter((l) => l.trim()).map((para, i) => (
            <p key={i} className="mte-tpl-textblock-para">{para}</p>
          ))}
        </div>
      ) : null}
    </Shell>
  );
}
TextBlock.options = {
  heading: {
    kind: "text",
    label: "Heading",
    default: "",
  },
  body: {
    kind: "textarea",
    label: "Body text",
    default: "",
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
  align: {
    kind: "radio",
    label: "Alignment",
    options: ["left", "center"],
    default: "left",
  },
} as SectionOptions;
